import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import PlanningTab from "./pages/PlanningTab";
import DesignTab from "./pages/DesignTab";
import WhiteboardTab from "./pages/WhiteboardTab";
import SchoolPostsTab from "./components/StudentDashboard/SchoolPostsTab";
import ProfilePicture from "./components/ProfilePicture";
import WebSocketChat from "./WebSocketChat";
import ChannelChat from "./ChannelChat";
import ParentsEveningSystem from "./components/ParentsEveningSystem";
import MyClubs from "./components/MyClubs";
import EducationGames from "./components/EducationGames";
import TeacherNuggetAnalytics from "./components/TeacherNuggetAnalytics";
import HomeworksBank from "./components/HomeworksBank";
import LiveMonitorTab_dynamic from "./LiveMonitorTab_dynamic";
import SchoolManager from "./SchoolManager";  // ✅ Import SchoolManager
import FireMarshalDashboard from "./components/FireMarshalDashboard";  // ✅ Import Fire Marshal Dashboard
import Reports from "./components/Reports";  // ✅ Import Reports module
import { buildApiUrl } from "./config";  // ✅ Import config
import authService from "./services/authService";  // ✅ Import auth service for security groups
import { getYearLabel, getPhysicalYear } from './utils/yearUtils';
// Chat removed

const TeacherDashboard = () => {
  console.log("👨‍🏫 TeacherDashboard rendered");

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get current view from URL parameter, default to "home"
  const currentViewFromUrl = searchParams.get('module') || 'home';
  const [currentView, setCurrentView] = useState(currentViewFromUrl);
  
  // Chat removed
  const [teacherEmail, setTeacherEmail] = useState("");
  const [teacherName, setTeacherName] = useState("");
  const [authServiceReady, setAuthServiceReady] = useState(false);  // ✅ Track auth initialization
  const [assignments, setAssignments] = useState({});
  const [stats, setStats] = useState({
    due_summary: { overdue: 0, upcoming: 0 },
    subjects: {},
    forms: {},
  });
  const [homeworks, setHomeworks] = useState([]);
  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [submittedCount, setSubmittedCount] = useState({});
  
  // ✅ ADD: States for assignment edit/delete functionality
  const [showAssignmentOptions, setShowAssignmentOptions] = useState(null);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // ✅ ADD: State for submissions viewer
  const [viewingSubmissions, setViewingSubmissions] = useState(null);
  
  // ✅ ADD: State for saved assignments viewer
  const [showSavedAssignments, setShowSavedAssignments] = useState(false);
  const [savedAssignments, setSavedAssignments] = useState([]);
  
  // Courses Management State
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedNugget, setSelectedNugget] = useState(null);
  const [courseSubTab, setCourseSubTab] = useState('courses'); // 'courses' or 'analytics'
  const [yearFilter, setYearFilter] = useState('all'); // 'all' or specific year
  const [showAddCourseModal, setShowAddCourseModal] = useState(false);
  const [showEditCourseModal, setShowEditCourseModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [courseMenuOpenId, setCourseMenuOpenId] = useState(null);
  const [showAddTopicModal, setShowAddTopicModal] = useState(false);
  const [showEditTopicModal, setShowEditTopicModal] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [showAddNuggetModal, setShowAddNuggetModal] = useState(false);
  const [nuggetDashboardView, setNuggetDashboardView] = useState(false);
  const [showAddVideoModal, setShowAddVideoModal] = useState(false);
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [showAddSlideModal, setShowAddSlideModal] = useState(false);
  const [showAddImageModal, setShowAddImageModal] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [nuggetSlides, setNuggetSlides] = useState([]);
  
  // Debug: Log whenever nuggetSlides state changes
  useEffect(() => {
    console.log('🔄 [STATE] nuggetSlides updated:', {
      count: nuggetSlides.length,
      isEmpty: nuggetSlides.length === 0,
      slides: nuggetSlides.map((s, i) => ({
        index: i,
        id: s.id,
        slide_order: s.slide_order,
        elements_count: s.elements?.length || 0
      }))
    });
  }, [nuggetSlides]);
  const [draggedSlideIndex, setDraggedSlideIndex] = useState(null);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saving', 'saved', 'error'
  const [showAssignCourseModal, setShowAssignCourseModal] = useState(false);
  const [courseToAssign, setCourseToAssign] = useState(null);
  const [assignmentYears, setAssignmentYears] = useState([]);
  const [assignmentForms, setAssignmentForms] = useState([]);
  const [assignmentStudents, setAssignmentStudents] = useState([]);
  const [questionAnswers, setQuestionAnswers] = useState({}); // Track answers by slide and element index
  const [submittedAnswers, setSubmittedAnswers] = useState({}); // Track submitted answers
  
  // 📅 NEW: Teacher Timetable State
  const [teacherTimetable, setTeacherTimetable] = useState(null);
  const [loadingTimetable, setLoadingTimetable] = useState(false);
  const [timetableError, setTimetableError] = useState(null);
  // parentContacts state removed - parent contacts module no longer available
  
  // � NEW: Notification Badge States
  const [formUnreadCounts, setFormUnreadCounts] = useState({}); // Per-form unread counts
  const [unreadPostsCount, setUnreadPostsCount] = useState(0); // Track unread posts for teachers
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // �📷 Staff Photo State
  const [staffData, setStaffData] = useState(null);
  const [staffPhotoUrl, setStaffPhotoUrl] = useState(null);
  
  const server = buildApiUrl("");  // ✅ Use config instead of localhost

  const myHomeworks = homeworks.filter(hw => hw.createdBy === teacherEmail);

  // Load courses from backend
  const loadCourses = async () => {
    if (!teacherEmail) return;
    
    try {
      const response = await fetch(buildApiUrl(`/api/courses?teacher_email=${encodeURIComponent(teacherEmail)}`));
      if (response.ok) {
        const coursesData = await response.json();
        console.log('📚 [COURSES] Loaded courses:', coursesData.length);
        coursesData.forEach((course, idx) => {
          console.log(`📖 [COURSE ${idx}] "${course.name}":`, {
            id: course.id,
            topics_count: course.topics?.length || 0
          });
          course.topics?.forEach((topic, topicIdx) => {
            console.log(`  📂 [TOPIC ${topicIdx}] "${topic.name}":`, {
              id: topic.id,
              nuggets_count: topic.nuggets?.length || 0
            });
            topic.nuggets?.forEach((nugget, nuggetIdx) => {
              console.log(`    💎 [NUGGET ${nuggetIdx}] ID: ${nugget.id}, Name: "${nugget.name}"`);
            });
          });
        });
        setCourses(coursesData);
      } else {
        console.error('Failed to load courses');
      }
    } catch (error) {
      console.error('Error loading courses:', error);
    }
  };

  // Load slides for a nugget
  const loadNuggetSlides = async (nuggetId) => {
    console.log('🔍 [LOAD] Starting loadNuggetSlides for nuggetId:', nuggetId);
    try {
      const apiUrl = buildApiUrl(`/api/nuggets/${nuggetId}/slides`);
      console.log('🌐 [LOAD] Fetching from URL:', apiUrl);
      
      const response = await fetch(apiUrl);
      console.log('📡 [LOAD] Response status:', response.status, response.statusText);
      
      if (response.ok) {
        const slides = await response.json();
        console.log('📦 [LOAD] Raw response data:', JSON.stringify(slides, null, 2));
        console.log('📊 [LOAD] Slides count:', slides?.length || 0);
        console.log('📊 [LOAD] Slides type:', typeof slides, 'Is array:', Array.isArray(slides));
        
        // Log each slide's structure
        if (Array.isArray(slides)) {
          slides.forEach((slide, idx) => {
            console.log(`📄 [LOAD] Slide ${idx}:`, {
              id: slide.id,
              slide_order: slide.slide_order,
              elements_count: slide.elements?.length || 0,
              has_elements_property: 'elements' in slide,
              elements_is_array: Array.isArray(slide.elements)
            });
          });
        }
        
        // Ensure slides is always an array with elements property
        const validSlides = Array.isArray(slides) && slides.length > 0 
          ? slides.map(slide => ({
              ...slide,
              elements: Array.isArray(slide.elements) ? slide.elements : []
            }))
          : [{ elements: [] }];
        
        console.log('✅ [LOAD] ValidSlides prepared:', validSlides.length, 'slides');
        console.log('✅ [LOAD] ValidSlides structure:', JSON.stringify(validSlides.map(s => ({
          id: s.id,
          elements_count: s.elements.length
        })), null, 2));
        
        console.log('🔧 [LOAD] Calling setNuggetSlides...');
        setNuggetSlides(validSlides);
        console.log('🔧 [LOAD] setNuggetSlides completed');
        
        setCurrentSlideIndex(0);
        console.log('✅ [LOAD] Load complete - set to slide index 0');
      } else {
        console.error('Failed to load slides:', response.status);
        setNuggetSlides([{ elements: [] }]);
      }
    } catch (error) {
      console.error('Error loading slides:', error);
      setNuggetSlides([{ elements: [] }]);
    }
  };

  // Save slides for a nugget
  const saveNuggetSlides = async (nuggetId, slides) => {
    setSaveStatus('saving');
    try {
      // Add order field to each element for backend validation
      const slidesWithOrder = slides.map(slide => ({
        ...slide,
        elements: slide.elements.map((element, index) => ({
          ...element,
          order: index
        }))
      }));
      
      const response = await fetch(buildApiUrl(`/api/nuggets/${nuggetId}/slides`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slides: slidesWithOrder })
      });
      
      if (response.ok) {
        setSaveStatus('saved');
      } else {
        const errorText = await response.text();
        console.error('Failed to save slides:', response.status, errorText);
        setSaveStatus('error');
      }
    } catch (error) {
      console.error('Error saving slides:', error);
      setSaveStatus('error');
    }
  };

  // Handle slide drag start
  const handleDragStart = (e, index) => {
    setDraggedSlideIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle slide drop
  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedSlideIndex === null || draggedSlideIndex === targetIndex) {
      setDraggedSlideIndex(null);
      return;
    }

    const newSlides = [...nuggetSlides];
    const draggedSlide = newSlides[draggedSlideIndex];
    
    // Remove from old position
    newSlides.splice(draggedSlideIndex, 1);
    
    // Insert at new position
    if (draggedSlideIndex < targetIndex) {
      newSlides.splice(targetIndex - 1, 0, draggedSlide);
      setCurrentSlideIndex(targetIndex - 1);
    } else {
      newSlides.splice(targetIndex, 0, draggedSlide);
      setCurrentSlideIndex(targetIndex);
    }

    setNuggetSlides(newSlides);
    setDraggedSlideIndex(null);
  };

  // Dashboard modules configuration
  const dashboardModules = [
    {
      id: "overview",
      title: "Overview",
      description: "Dashboard summary and statistics",
      icon: "📊",
      gradient: "from-blue-500 to-blue-600",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=300&h=200&fit=crop&crop=center"
    },
    {
      id: "classes",
      title: "My Classes",
      description: "Manage your assigned classes",
      icon: "🏫",
      gradient: "from-green-500 to-green-600",
      image: "https://images.unsplash.com/photo-1523580846011-d3bb57ba03d1?w=300&h=200&fit=crop&crop=center"
    },
    {
      id: "monitor",
      title: "Live Monitor",
      description: "Monitor student screens in real-time",
      icon: "👁️",
      gradient: "from-slate-500 to-slate-600",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=300&h=200&fit=crop&crop=center"
    },
    {
      id: "assignments",
      title: "Learning",
      description: "Create and manage homework",
      icon: "📝",
      gradient: "from-purple-500 to-purple-600",
      image: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=300&h=200&fit=crop&crop=center"
    },
    {
      id: "analytics",
      title: "Analytics",
      description: "Performance insights and reports",
      icon: "📈",
      gradient: "from-orange-500 to-orange-600",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=300&h=200&fit=crop&crop=center"
    },
    
    {
      id: "planning",
      title: "Planning",
      description: "Lesson plans and curriculum",
      icon: "🗓️",
      gradient: "from-yellow-500 to-yellow-600",
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=300&h=200&fit=crop&crop=center"
    },
    {
      id: "design",
      title: "Design",
      description: "Create educational materials",
      icon: "🎨",
      gradient: "from-pink-500 to-pink-600",
      image: "https://images.unsplash.com/photo-1541963463532-d68292c34d19?w=300&h=200&fit=crop&crop=center"
    },
    {
      id: "whiteboard",
      title: "Whiteboard",
      description: "Interactive teaching tools",
      icon: "🧑‍🏫",
      gradient: "from-teal-500 to-teal-600",
      image: "https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=300&h=200&fit=crop&crop=center"
    },
    {
      id: "posts",
      title: "School Posts",
      description: "Manage school announcements",
      icon: "📢",
      gradient: "from-red-500 to-red-600",
      image: "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=300&h=200&fit=crop&crop=center"
    },
    {
      id: "chat",
      title: "Parent Chat",
      description: "Message with parents",
      icon: "💬",
      gradient: "from-cyan-500 to-cyan-600",
      image: "https://images.unsplash.com/photo-1577563908411-5077b6dc7624?w=300&h=200&fit=crop&crop=center"
    },
    {
      id: "attendance",
      title: "Attendance",
      description: "Take attendance for all classes",
      icon: "✅",
      gradient: "from-emerald-500 to-emerald-600",
      image: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=300&h=200&fit=crop&crop=center"
    },
    {
      id: "timetable",
      title: "My Timetable",
      description: "View and print your compact teaching schedule",
      icon: "📅",
      gradient: "from-indigo-500 to-indigo-600",
      image: "https://images.unsplash.com/photo-1506784193567-0cc9e2ad2e96?w=300&h=200&fit=crop&crop=center"
    },
    {
      id: "my-clubs",
      title: "My Clubs",
      description: "Manage your assigned after-school clubs",
      icon: "🏃‍♂️",
      gradient: "from-green-500 to-emerald-600",
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=300&h=200&fit=crop&crop=center"
    },
    // {
    //   id: "education-games",
    //   title: "Education Games",
    //   description: "Create interactive racing games for students",
    //   icon: "🏎️",
    //   gradient: "from-amber-500 to-yellow-600",
    //   image: "https://images.unsplash.com/photo-1511512578047-dfb367046420?w=300&h=200&fit=crop&crop=center"
    // },
    {
      id: "parents-evening",
      title: "Parents Evening",
      description: "Manage parent-teacher consultations",
      icon: "🎓",
      gradient: "from-purple-500 to-pink-500",
      image: "https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?w=300&h=200&fit=crop&crop=center"
    },
    {
      id: "fire-registers",
      title: "Fire Registers",
      description: "Emergency fire drill attendance",
      icon: "🚨",
      gradient: "from-red-600 to-orange-600",
      image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=300&h=200&fit=crop&crop=center"
    },
    {
      id: "fire-marshal",
      title: "Fire Marshal",
      description: "View all fire drill reports and missing students",
      icon: "🚒",
      gradient: "from-red-700 to-red-800",
      image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=300&h=200&fit=crop&crop=center"
    },
    {
      id: "school-manager",
      title: "School Manager",
      description: "Administrative tools and user management",
      icon: "🏢",
      gradient: "from-slate-600 to-slate-700",
      image: "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=300&h=200&fit=crop&crop=center"
    },
    {
      id: "user-management",
      title: "User Management",
      description: "Manage staff and security groups",
      icon: "👤",
      gradient: "from-gray-600 to-gray-700",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=200&fit=crop&crop=center"
    },
    {
      id: "system-settings",
      title: "System Settings", 
      description: "System configuration and administration",
      icon: "⚙️",
      gradient: "from-stone-600 to-stone-700",
      image: "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=300&h=200&fit=crop&crop=center"
    },
    {
      id: "reports",
      title: "Reports",
      description: "View school analytics and reports - Pupils Overview, Attendance, Applicants, Leavers, Capacity",
      icon: "📊",
      gradient: "from-green-600 to-emerald-600",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=300&h=200&fit=crop&crop=center"
    }
  ];

  // ✅ Filter modules based on user's security group
  const allowedModules = useMemo(() => {
    try {
      if (!teacherEmail) {
        // Return basic modules while waiting for email to load
        const basicModules = dashboardModules.filter(module => 
          ['overview', 'classes', 'assignments', 'attendance', 'timetable'].includes(module.id)
        );
        return basicModules;
      }
      
      // Get user's allowed modules from security group
      const userModules = authService.getUserModulesFromSecurityGroup(teacherEmail);
      
      if (!userModules || userModules.length === 0) {
        // If no specific modules assigned, show basic teacher modules
        const basicModules = dashboardModules.filter(module => 
          ['overview', 'classes', 'assignments', 'attendance', 'timetable'].includes(module.id)
        );
        return basicModules;
      }
      
      // Filter dashboard modules to only show allowed ones
      const filteredModules = dashboardModules.filter(module => userModules.includes(module.id));
      
      return filteredModules;
    } catch (error) {
      console.error('❌ Error filtering modules:', error);
      // Fallback to basic modules on error
      const basicModules = dashboardModules.filter(module => 
        ['overview', 'classes', 'assignments', 'attendance', 'timetable'].includes(module.id)
      );
      return basicModules;
    }
  }, [teacherEmail, dashboardModules, authServiceReady]);  // ✅ Added authServiceReady dependency

  // ✅ ADD THIS useEffect HERE - before the existing useEffects
  useEffect(() => {
    // Ensure auth service is initialized before calculating modules
    const initializeAuth = async () => {
      try {
        await authService.ensureInitialized();
        setAuthServiceReady(true);  // ✅ Trigger useMemo recalculation
      } catch (error) {
        console.error('❌ Auth service initialization error:', error);
        setAuthServiceReady(true); // Set to true anyway to prevent blocking UI
      }
    };
    
    initializeAuth();
  }, []);

  // ✅ ADD THIS useEffect HERE - before the existing useEffects
  useEffect(() => {
    // Get teacher info from localStorage
    const storedTeacherEmail = localStorage.getItem('teacherEmail');
    const storedTeacherName = localStorage.getItem('teacherName');
    
    if (storedTeacherEmail) {
      setTeacherEmail(storedTeacherEmail);
    } else {
      // For testing - set a default
      setTeacherEmail("teacher@school.com");
      localStorage.setItem('teacherEmail', "teacher@school.com");
    }
    
    if (storedTeacherName) {
      setTeacherName(storedTeacherName);
    } else {
      // For testing - set a default
      setTeacherName("Teacher");
      localStorage.setItem('teacherName', "Teacher");
    }
    
    // ✅ CRITICAL FIX: Ensure userRole is set to "teacher" for teacher dashboard
    localStorage.setItem('userRole', 'teacher');
    console.log("👨‍🏫 Teacher Dashboard: Set userRole to 'teacher'");
  }, []);

  useEffect(() => {
    // Fetch parents from backend API (school-aware)
    console.log("Loading parents data...");
    fetch(buildApiUrl('/data/parents.json'))
      .then(res => {
        console.log("Parents response status:", res.status);
        return res.json();
      })
      .then(data => {
        console.log("Parents data loaded:", data.length, "parents");
        setParents(data);
      })
      .catch(err => {
        console.error("Failed to load parents:", err);
        setParents([]);
      });
  }, []);

  useEffect(() => {
    console.log("🔄 TeacherDashboard: Loading homeworks from:", `${server}/debug-homeworks/`);
    fetch(`${server}/debug-homeworks/`)
      .then(res => {
        console.log("📡 Homeworks API response status:", res.status);
        return res.json();
      })
      .then(data => {
        console.log("📚 Homeworks loaded:", data.homeworks?.length || 0, "items");
        setHomeworks(data.homeworks || []);
      })
      .catch(err => {
        console.error("❌ Failed to fetch homeworks", err);
        console.error("🌐 Server URL was:", `${server}/debug-homeworks/`);
      });
  }, []);

  useEffect(() => {
    console.log("🔄 TeacherDashboard: Loading students from:", buildApiUrl('/synced-students'));
    fetch(buildApiUrl('/synced-students'))
      .then(res => {
        console.log("📡 Students API response status:", res.status);
        return res.json();
      })
      .then(data => {
        console.log("👥 Raw students data:", data.students?.length || 0, "items");
        const deduped = Array.from(
          new Map((data.students || []).map(s => [s.person_id, s])).values()
        );
        console.log("👥 Deduped students:", deduped.length, "items");
        setStudents(deduped);
      })
      .catch(err => {
        console.error("❌ Failed to fetch students", err);
        console.error("🌐 API URL was:", buildApiUrl('/synced-students'));
      });
  }, []);

  useEffect(() => {
    console.log("🔄 TeacherDashboard: Loading homework stats from:", `${server}/homework-stats`);
    fetch(`${server}/homework-stats`)
      .then(res => {
        console.log("📡 Stats API response status:", res.status);
        return res.json();
      })
      .then(data => {
        console.log("📊 Homework stats loaded:", data);
        setStats({
          ...data,
          due_summary: data.due_summary || { overdue: 0, upcoming: 0 },
        });
      })
      .catch(err => {
        console.error("❌ Failed to fetch stats", err);
        console.error("🌐 Server URL was:", `${server}/homework-stats`);
      });
  }, []);

  useEffect(() => {
    if (teacherEmail) {
      fetch(`${server}/staff`)
        .then(res => res.json())
        .then(data => {
          const teacher = data.find(t => t.email === teacherEmail);
          if (teacher) {
            setTeacherName(teacher.name);
            setStaffData(teacher);
            // Set photo URL if available
            if (teacher.photo_url) {
              setStaffPhotoUrl(`${server}${teacher.photo_url}`);
            }
          }
        })
        .catch(err => {
          console.error("Failed to fetch teacher name:", err);
        });
    }
  }, [teacherEmail, server]);

  useEffect(() => {
    console.log("🔄 TeacherDashboard: Loading teacher assignments from:", `${server}/assignments`);
    fetch(`${server}/assignments`)
      .then(res => {
        console.log("📡 Assignments API response status:", res.status);
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        return res.json();
      })
      .then(data => {
        console.log("👨‍🏫 Teacher assignments loaded:", data);
        console.log("📋 Number of assigned classes:", Object.keys(data || {}).length);
        setAssignments(data);
      })
      .catch(err => {
        console.error("❌ CRITICAL: Failed to fetch teacher assignments", err);
        console.error("🌐 Server URL was:", `${server}/assignments`);
        console.error("📱 This will prevent Forms tab from loading!");
        // Set empty assignments to prevent crashes
        setAssignments({});
      });
  }, []);

  const [submissions, setSubmissions] = useState({});

  useEffect(() => {
    fetch(`${server}/homework-result-map`)
      .then(res => res.json())
      .then(data => {
        setSubmissions(data);

        // Update submittedCount map for each homework
        const countMap = {};
        for (const studentId in data) {
          for (const hwId in data[studentId]) {
            if (data[studentId][hwId]?.submitted_at) {
              countMap[hwId] = (countMap[hwId] || 0) + 1;
            }
          }
        }
        setSubmittedCount(countMap);
      })
      .catch(err => {
        console.error("Error fetching homework submissions", err);
      });
  }, []);

  // Load courses when teacherEmail is available
  useEffect(() => {
    if (teacherEmail) {
      loadCourses();
    }
  }, [teacherEmail]);

  // Load slides when a nugget is selected in dashboard view
  useEffect(() => {
    if (nuggetDashboardView && selectedNugget && selectedNugget.id) {
      console.log('👆 [CLICK] Nugget selected, loading slides for:', selectedNugget.id);
      loadNuggetSlides(selectedNugget.id);
    }
  }, [nuggetDashboardView, selectedNugget]);

  // Auto-save slides whenever they change
  useEffect(() => {
    if (nuggetDashboardView && selectedNugget && selectedNugget.id && nuggetSlides.length > 0) {
      // Debounce the save to avoid too many requests
      const timer = setTimeout(() => {
        saveNuggetSlides(selectedNugget.id, nuggetSlides);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [nuggetSlides]);

  // ✅ ADD: Function to load saved homework drafts
  const loadSavedAssignments = async () => {
    try {
      const response = await fetch(`${server}/saved-homework-drafts`);
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched saved assignments data:', data);
        // Extract the drafts array from the response
        const drafts = data.drafts || [];
        console.log('Extracted drafts array:', drafts);
        setSavedAssignments(Array.isArray(drafts) ? drafts : []);
        setShowSavedAssignments(true);
      } else {
        console.error("Failed to fetch saved assignments");
        setSavedAssignments([]);
        setShowSavedAssignments(true);
      }
    } catch (error) {
      console.error("Error loading saved assignments:", error);
      setSavedAssignments([]);
      setShowSavedAssignments(true);
    }
  };

  // 📅 NEW: Teacher Timetable Loading Function
  const loadTeacherTimetable = async () => {
    if (!teacherEmail || teacherEmail === "teacher@school.com") {
      console.log("📅 No teacher email available for timetable loading");
      return;
    }

    setLoadingTimetable(true);
    setTimetableError(null);

    try {
      console.log(`📅 Loading timetable for teacher: ${teacherEmail}`);
      const response = await fetch(`${server}/teacher-timetable/${encodeURIComponent(teacherEmail)}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📅 Teacher timetable loaded:', data);
        
        if (data.success && data.timetable) {
          setTeacherTimetable(data.timetable);
        } else {
          setTimetableError("No timetable found for this teacher");
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setTimetableError(errorData.detail || "Failed to load teacher timetable");
      }
    } catch (error) {
      console.error("Error loading teacher timetable:", error);
      setTimetableError("Failed to connect to server");
    } finally {
      setLoadingTimetable(false);
    }
  };

  // 📅 Load teacher timetable when email is available
  useEffect(() => {
    if (teacherEmail && teacherEmail !== "teacher@school.com") {
      loadTeacherTimetable();
    }
  }, [teacherEmail]);

  const assignedForms = useMemo(() => {
    if (!assignments || typeof assignments !== 'object') return [];
    
    const ownedForms = [];
    const teachingForms = [];
    const teacherEmailLower = teacherEmail.toLowerCase();
    
    Object.entries(assignments).forEach(([form, classData]) => {
      if (!classData) return;
      
      // Handle new structure with owners and teachers
      if (typeof classData === 'object' && !Array.isArray(classData)) {
        const owners = Array.isArray(classData.owners) ? classData.owners : [];
        const teachers = Array.isArray(classData.teachers) ? classData.teachers : [];
        
        // Check if teacher is an owner (case-insensitive)
        if (owners.some(email => email.toLowerCase() === teacherEmailLower)) {
          ownedForms.push(form);
        }
        // Check if teacher is in the teachers list (but not already added as owner)
        else if (teachers.some(email => email.toLowerCase() === teacherEmailLower)) {
          teachingForms.push(form);
        }
      }
      // Handle legacy array structure
      else if (Array.isArray(classData)) {
        if (classData.some(email => email.toLowerCase() === teacherEmailLower)) {
          teachingForms.push(form);
        }
      }
      // Handle simple string structure
      else if (typeof classData === 'string') {
        if (classData.toLowerCase() === teacherEmailLower) {
          teachingForms.push(form);
        }
      }
    });
    
    // Return owned forms first, then teaching forms
    return [...ownedForms.sort(), ...teachingForms.sort()];
  }, [assignments, teacherEmail]);

  const formOwnership = useMemo(() => {
    if (!assignments || typeof assignments !== 'object') return {};
    
    const ownership = {};
    const teacherEmailLower = teacherEmail.toLowerCase();
    
    Object.entries(assignments).forEach(([form, classData]) => {
      if (!classData) return;
      
      // Handle new structure with owners and teachers (case-insensitive)
      if (typeof classData === 'object' && !Array.isArray(classData)) {
        const owners = Array.isArray(classData.owners) ? classData.owners : [];
        ownership[form] = owners.some(email => email.toLowerCase() === teacherEmailLower) ? 'owner' : 'teacher';
      } else {
        // Legacy format - assume teacher role
        ownership[form] = 'teacher';
      }
    });
    
    return ownership;
  }, [assignments, teacherEmail]);

  const getTotalStudents = (hw) => {
    if (!hw.assignedTo) return 0;
    if (!Array.isArray(hw.assignedTo)) return 0;
    return students.filter(s => hw.assignedTo.includes(s.form)).length;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // ✅ ADD: Assignment edit/delete handler functions
  const handleEditAssignment = (assignment) => {
    setEditingAssignment(assignment);
    setShowAssignmentOptions(null);
    // Navigate to edit page with assignment data
    navigate(`/homeworks?edit=${assignment.id}`, { state: { assignment } });
  };

  const handleDeleteAssignment = async (assignmentId) => {
    try {
      console.log(`🗑️ Attempting to delete assignment ${assignmentId}`);
      
      // ✅ Use the correct backend endpoint: /homework-by-id/{hw_id}
      const deleteUrl = `${server}/homework-by-id/${assignmentId}`;
      console.log(`Server URL: ${deleteUrl}`);
      
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          // Add any authentication headers if needed
          // 'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        }
      });

      console.log(`Delete response status: ${response.status}`);
      console.log(`Delete response ok: ${response.ok}`);

      if (response.ok) {
        // Remove from local state
        setHomeworks(homeworks.filter(hw => hw.id !== assignmentId));
        setShowDeleteConfirm(null);
        setShowAssignmentOptions(null);
        
        // Show success message
        alert('✅ Assignment deleted successfully!');
        console.log(`✅ Successfully deleted assignment ${assignmentId}`);
      } else {
        // Get more detailed error information
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        try {
          const errorData = await response.text();
          console.log('Server error response:', errorData);
          if (errorData) {
            errorMessage += ` - ${errorData}`;
          }
        } catch (parseError) {
          console.log('Could not parse error response');
        }

        // Handle specific status codes
        if (response.status === 404) {
          alert('❌ Assignment not found. It may have already been deleted.');
        } else if (response.status === 403) {
          alert('❌ You do not have permission to delete this assignment.');
        } else if (response.status === 500) {
          alert('❌ Server error occurred. Please try again later.');
        } else {
          alert(`❌ Failed to delete assignment: ${errorMessage}`);
        }
        
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Error deleting assignment:', error);
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        alert('❌ Network error: Could not connect to server. Please check your connection.');
      } else if (!error.message.includes('HTTP')) {
        alert('❌ Unexpected error occurred. Please try again.');
      }
      // If it's an HTTP error, the alert was already shown above
    }
  };

  const confirmDeleteAssignment = (assignment) => {
    setShowDeleteConfirm(assignment);
    setShowAssignmentOptions(null);
  };

  // ✅ ADD: Export submissions handler
  const handleExportSubmissions = async (assignment) => {
    try {
      console.log(`📤 Exporting submissions for assignment ${assignment.id}`);
      
      // Fetch submissions data
      const response = await fetch(`${server}/homework-result-map`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const submissionsData = await response.json();
      
      // Create CSV content
      let csvContent = "Student ID,Student Name,Submission Status,Submission Date,Score\n";
      
      // Get students assigned to this homework
      const assignedStudents = students.filter(s => 
        Array.isArray(assignment.assignedTo) && assignment.assignedTo.includes(s.form)
      );
      
      assignedStudents.forEach(student => {
        const submission = submissionsData[student.school_id]?.[assignment.id];
        const status = submission ? "Submitted" : "Not Submitted";
        const submissionDate = submission?.submitted_at ? new Date(submission.submitted_at).toLocaleDateString() : "N/A";
        const score = submission?.score || "N/A";
        
        csvContent += `"${student.school_id}","${student.full_name}","${status}","${submissionDate}","${score}"\n`;
      });
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${assignment.title}_submissions.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        alert('✅ Submissions exported successfully!');
      } else {
        alert('❌ Export not supported in this browser');
      }
    } catch (error) {
      console.error('❌ Error exporting submissions:', error);
      alert('❌ Failed to export submissions. Please try again.');
    }
  };

  // ✅ ADD: View submissions handler
  const handleViewSubmissions = (assignment) => {
    setViewingSubmissions(assignment);
    setShowAssignmentOptions(null);
  };

  // Chat removed: parentContacts

  // Export homework submissions to XLSX
  const exportHomeworkData = (hw) => {
    const assignedStudents = students.filter(s => 
      Array.isArray(hw.assignedTo) && hw.assignedTo.includes(s.form)
    );
    const rows = assignedStudents.map((student) => {
      const sid = student.school_id;
      const submission = submissions[sid]?.[hw.id];
      const result = submission?.result || {};
      const answers = result.answers || [];

      const row = {
        "Student Name": student.full_name,
        "Submitted": submission?.submitted_at ? "Yes" : "No",
        "Submitted At": submission?.submitted_at || "",
        "Score": result.score ?? "",
      };

      answers.forEach((a, idx) => {
        row[`Q${idx + 1}`] = a.selected || "";
      });

      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Homework");

    const blob = new Blob([XLSX.write(workbook, { bookType: "xlsx", type: "array" })]);
    saveAs(blob, `${hw.title.replace(/\s+/g, "_")}_submissions.xlsx`);
  };

  function countSubmissionsForHomework(submissions, homeworkId, assignedStudents) {
    let count = 0;
    for (const student of assignedStudents) {
      const sid = student.school_id;
      const submission = submissions[sid]?.[homeworkId];
      if (submission?.submitted_at) {
        count++;
      }
    }
    return count;
  }

  // Function to handle view changes
  const handleViewChange = (newView) => {
    // Scroll to top when switching views
    window.scrollTo(0, 0);
    
    setCurrentView(newView);
    // Update URL parameter
    if (newView === 'home') {
      // Remove module parameter for home view
      setSearchParams({});
    } else {
      // Set module parameter for other views
      setSearchParams({ module: newView });
    }
  };

  // Function to go back to home
  const goToHome = () => {
    // Scroll to top when going home
    window.scrollTo(0, 0);
    
    setCurrentView('home');
    setSearchParams({});
  };

  // Sync currentView with URL changes (browser back/forward)
  useEffect(() => {
    const moduleFromUrl = searchParams.get('module') || 'home';
    if (moduleFromUrl !== currentView) {
      setCurrentView(moduleFromUrl);
    }
  }, [searchParams]);

  // Component that displays module with notification badge for Teacher
  const ModuleWithBadge = ({ module, unreadCount }) => {
    return (
      <div
        onClick={() => handleViewChange(module.id)}
        className="group cursor-pointer"
      >
        <div className="relative bg-white/20 backdrop-blur-md border-2 border-gray-300/60 rounded-2xl p-4 sm:p-6 hover:bg-white/30 hover:border-gray-400/80 hover:shadow-xl hover:shadow-gray-200/50 hover:-translate-y-1 transition-all duration-500 ease-out h-full min-h-[160px] sm:min-h-[180px] flex flex-col">
          
          {/* Gradient Background Accent - removed for cleaner glass look */}
          
          {/* Icon Container */}
          <div className="relative mb-3 sm:mb-4">
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br ${module.gradient} opacity-80 flex items-center justify-center text-white text-lg sm:text-xl font-semibold shadow-lg group-hover:scale-110 group-hover:rotate-3 group-hover:opacity-100 transition-all duration-300`}>
              {module.icon}
            </div>
            {/* Notification Badge */}
            {unreadCount > 0 && (
              <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5 shadow-lg animate-pulse">
                {unreadCount > 99 ? '99+' : unreadCount}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col relative z-10">
            <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4 group-hover:text-gray-700 transition-colors">
              {module.title}
            </h3>
            
            {/* Action Button */}
            <div className="flex items-center text-gray-400 group-hover:text-blue-600 font-medium text-xs sm:text-sm transition-colors duration-300 mt-auto">
              <span className="hidden sm:inline">Launch module</span>
              <span className="sm:hidden">Launch</span>
              <svg className="w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </div>
          </div>
          
          {/* Subtle Border Glow */}
          <div className="absolute inset-0 rounded-2xl ring-1 ring-gray-200/50 group-hover:ring-gray-300/50 transition-all duration-300"></div>
        </div>
      </div>
    );
  };

  // Home Page Component with notification integration
  const HomePage = () => {
    const [totalUnreadMessages, setTotalUnreadMessages] = useState(0);
    const [parentUnreadCount, setParentUnreadCount] = useState(0);
    const [studentUnreadCount, setStudentUnreadCount] = useState(0);

    // Load unread message counts for parent chat AND student chat
    const loadUnreadCounts = useCallback(async () => {
      try {
        let parentUnreadTotal = 0;
        let studentUnreadTotal = 0;

        // Get unread messages from parents
        const parentResponse = await fetch(buildApiUrl(`/messages/unread-by-contact/${teacherEmail}`));
        const parentData = await parentResponse.json();
        if (parentData?.unread_by_contact) {
          parentUnreadTotal = Object.values(parentData.unread_by_contact).reduce((sum, count) => sum + count, 0);
        }

        // Get unread messages from students
        const studentResponse = await fetch(buildApiUrl(`/api/student-chat/teacher-unread/${teacherEmail}`));
        const studentData = await studentResponse.json();
        if (studentData?.unread_count) {
          studentUnreadTotal = studentData.unread_count;
        }
        
        // Set per-form unread counts only if changed (use parent component state)
        if (studentData?.unread_by_form) {
          setFormUnreadCounts(prev => {
            const newCounts = studentData.unread_by_form;
            // Only update if counts actually changed
            if (JSON.stringify(prev) !== JSON.stringify(newCounts)) {
              return newCounts;
            }
            return prev;
          });
        }

        // Set separate counts
        setParentUnreadCount(parentUnreadTotal);
        setStudentUnreadCount(studentUnreadTotal);
        // Set total unread from both parents and students
        setTotalUnreadMessages(parentUnreadTotal + studentUnreadTotal);
      } catch (error) {
        console.error("Failed to load unread counts:", error);
      }
    }, [teacherEmail]);

    // ✅ NEW: Load unread posts count for teachers
    const loadUnreadPostsCount = useCallback(async () => {
      try {
        const response = await fetch(
          buildApiUrl(`/api/posts/unread-count/${teacherEmail}?user_role=teacher`)
        );
        const data = await response.json();
        if (data?.success) {
          setUnreadPostsCount(data.unread_count);
        }
      } catch (error) {
        console.error("Failed to load unread posts count:", error);
      }
    }, [teacherEmail]);

    useEffect(() => {
      if (teacherEmail) {
        loadUnreadCounts();
        loadUnreadPostsCount(); // ✅ NEW: Load unread posts
        // Refresh every 5 seconds
        const interval = setInterval(() => {
          loadUnreadCounts();
          loadUnreadPostsCount(); // ✅ NEW: Poll for unread posts
        }, 5000);
        return () => clearInterval(interval);
      }
    }, [teacherEmail, loadUnreadCounts, loadUnreadPostsCount]); // ✅ Add loadUnreadPostsCount dependency

    return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-indigo-100 to-purple-100 relative overflow-hidden">
      {/* School-themed decorative elements */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-10 left-10 text-6xl">📚</div>
        <div className="absolute top-20 right-20 text-5xl">✏️</div>
        <div className="absolute bottom-20 left-20 text-7xl">🎓</div>
        <div className="absolute bottom-32 right-32 text-6xl">📖</div>
        <div className="absolute top-1/3 left-1/4 text-5xl">🎒</div>
        <div className="absolute top-2/3 right-1/4 text-6xl">🖊️</div>
        <div className="absolute top-1/2 right-10 text-5xl">📝</div>
        <div className="absolute bottom-10 left-1/3 text-5xl">🏫</div>
      </div>
      
      {/* Header - Thin & Modern */}
      <div className="bg-white shadow-sm border-b relative z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center p-1 shadow-sm">
                <img 
                  src="https://ai-school-backend-production.up.railway.app/volume-file/LogoGreen.png" 
                  alt="School Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Teacher Dashboard</h1>
                <p className="text-xs text-gray-500">Welcome back, {teacherName}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <ProfilePicture 
                userName={teacherName} 
                userEmail={teacherEmail} 
                userRole="teacher" 
                staffId={staffData?.staff_id}
                size={24}
              />
              <button
                onClick={() => goToHome()}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100/70 rounded-lg transition-all duration-200"
                title="Home"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Today's Schedule */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-2xl p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">📅</span>
            <h3 className="text-sm font-semibold text-gray-900">Today's Schedule</h3>
            <span className="text-xs text-gray-500">
              {['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][new Date().getDay()]}
            </span>
          </div>
          {(() => {
            const todayName = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][new Date().getDay()];
            const todaySchedule = teacherTimetable?.days?.[todayName] || [];
            const fmtTime = (t) => {
              if (!t) return '';
              const [h, m] = t.split(':');
              const hr = parseInt(h);
              const ampm = hr >= 12 ? 'PM' : 'AM';
              const display = hr > 12 ? hr - 12 : hr === 0 ? 12 : hr;
              return `${display}:${m} ${ampm}`;
            };

            if (loadingTimetable) {
              return (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span className="ml-2 text-sm text-gray-500">Loading schedule...</span>
                </div>
              );
            }

            if (!todaySchedule.length) {
              return (
                <div className="text-center py-4">
                  <span className="text-2xl">🎉</span>
                  <p className="text-sm text-gray-500 mt-1">No classes scheduled today</p>
                </div>
              );
            }

            return (
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
                {todaySchedule.map((slot, idx) => {
                  const colors = [
                    'border-blue-500',
                    'border-emerald-500',
                    'border-purple-500',
                    'border-orange-500',
                    'border-pink-500',
                    'border-cyan-500',
                    'border-amber-500',
                    'border-rose-500',
                  ];
                  const dividerColors = [
                    'bg-blue-500',
                    'bg-emerald-500',
                    'bg-purple-500',
                    'bg-orange-500',
                    'bg-pink-500',
                    'bg-cyan-500',
                    'bg-amber-500',
                    'bg-rose-500',
                  ];
                  const borderColor = colors[idx % colors.length];
                  const dividerColor = dividerColors[idx % dividerColors.length];
                  return (
                  <div key={idx} className={`flex-shrink-0 w-36 p-3 bg-white/60 rounded-xl border-2 ${borderColor} hover:bg-white/80 transition-colors text-center`}>
                    <div className="text-xs text-gray-500 mb-1">
                      <span className="font-medium">{fmtTime(slot.period?.startTime)}</span>
                      <span className="mx-0.5">-</span>
                      <span>{fmtTime(slot.period?.endTime)}</span>
                    </div>
                    <div className={`w-8 h-0.5 ${dividerColor} rounded-full mx-auto mb-1.5`}></div>
                    <p className="text-sm font-medium text-gray-900 truncate">{slot.form || slot.class || 'No Form'}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {slot.subject || ''}{slot.room ? ` • ${slot.room}` : ''}
                    </p>
                  </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Dashboard Modules - Modern Grid Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 relative z-10">
        <div className="bg-white/30 backdrop-blur-md rounded-2xl shadow-lg border border-white/40 p-6 sm:p-8">
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {allowedModules.map((module) => {
              // Determine unread count based on module type
              let unreadCount = 0;
              if (module.id === 'chat') {
                // Parent chat gets parent unread count
                unreadCount = parentUnreadCount;
              } else if (module.id === 'classes') {
                // Classes/student chat gets student unread count
                unreadCount = studentUnreadCount;
              }
              
              return (
                <ModuleWithBadge 
                  key={module.id} 
                  module={module} 
                  unreadCount={unreadCount}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
    );
  };

  // Individual Module Components
  const OverviewView = () => (
    <div className="h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm border-b flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => goToHome()}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Back to Home"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">📊 Overview</h1>
                <p className="text-gray-600">Dashboard summary and analytics</p>
              </div>
            </div>
            <button
              onClick={() => goToHome()}
              className="p-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Home"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-4 md:space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Assignments</h3>
            <div className="space-y-3 md:space-y-4">
              {myHomeworks.slice(0, 3).map((hw) => (
                <div key={hw.id} className="flex flex-col md:flex-row md:items-center justify-between p-3 md:p-4 bg-gray-50 rounded-lg space-y-2 md:space-y-0">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 text-sm md:text-base">{hw.title}</h4>
                    <p className="text-xs md:text-sm text-gray-500">{hw.subject} • Due {formatDate(hw.dueDate)}</p>
                  </div>
                  <div className="flex items-center justify-between md:justify-end gap-3 md:gap-4">
                    <span className={`px-2 md:px-3 py-1 rounded-full text-xs font-medium border`}>
                      {hw.status}
                    </span>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {(() => {
                          const count = submittedCount[hw.id] || 0;
                          const total = getTotalStudents(hw);
                          console.log(`📊 Homework "${hw.title}" (ID: ${hw.id}): ${count}/${total} submitted`);
                          return `${count}/${total}`;
                        })()}
                      </p>
                      <p className="text-xs text-gray-500">submitted</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );

  const ClassesView = () => (
    <div className="h-screen bg-gradient-to-br from-yellow-50 via-blue-50 to-red-50 flex flex-col relative overflow-hidden">
      {/* Cork Board Pattern Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-5">
        <div className="absolute top-10 left-10 w-2 h-2 bg-red-400 rounded-full"></div>
        <div className="absolute top-20 right-40 w-2 h-2 bg-blue-400 rounded-full"></div>
        <div className="absolute bottom-40 left-60 w-2 h-2 bg-yellow-400 rounded-full"></div>
        <div className="absolute top-1/2 right-20 w-2 h-2 bg-green-400 rounded-full"></div>
        <div className="absolute bottom-20 right-1/3 w-2 h-2 bg-orange-400 rounded-full"></div>
      </div>
      
      {/* Header - Whiteboard Style */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 shadow-lg border-b-4 border-blue-900 flex-shrink-0 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 sm:py-6">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={() => goToHome()}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                  📋 My Classes
                </h1>
                <p className="text-blue-100 text-xs sm:text-sm hidden sm:block">Manage your assigned classes and students</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
          {assignedForms.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {assignedForms.map((form) => {
              const isOwner = formOwnership[form] === 'owner';
              const formUnreadCount = formUnreadCounts[form] || 0;
              return (
              <div
                key={form}
                className="relative group cursor-pointer"
                onClick={() => navigate(`/form/${encodeURIComponent(form)}`)}
              >
                {/* Pushpin */}
                <div className={`absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full z-20 shadow-md ${
                  isOwner ? 'bg-red-600' : 'bg-blue-600'
                }`}>
                  <div className="absolute inset-0 rounded-full animate-ping opacity-75"></div>
                </div>
                
                {/* Notice Card - Looks like pinned paper */}
                <div className={`bg-gradient-to-br shadow-xl border-2 transition-all duration-200 group-hover:shadow-2xl active:scale-95 relative overflow-hidden rounded-lg ${
                  isOwner 
                    ? 'from-amber-100 to-amber-200 border-amber-300 hover:border-amber-400 group-hover:rotate-1' 
                    : 'from-slate-100 to-slate-200 border-slate-300 hover:border-slate-400 group-hover:-rotate-1'
                }`}
                style={{ boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
              >
                {/* Tape effect at top */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-16 h-6 bg-white/40 -mt-1"></div>
                
                <div className="p-4 md:p-6 pt-6">
                  {/* Unread badge */}
                  {formUnreadCount > 0 && (
                    <div className="absolute top-3 right-3 bg-red-500 rounded-full w-6 h-6 flex items-center justify-center shadow-lg border-2 border-white z-10">
                      <span className="text-white text-xs font-bold">{formUnreadCount > 9 ? '9+' : formUnreadCount}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                    <div className={`w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center transition-all shadow-md border-2 ${
                      isOwner
                        ? 'bg-gradient-to-br from-red-500 to-red-700 border-red-800 group-hover:from-red-600 group-hover:to-red-800'
                        : 'bg-gradient-to-br from-blue-500 to-blue-700 border-blue-800 group-hover:from-blue-600 group-hover:to-blue-800'
                    }`}>
                      <span className="font-bold text-lg md:text-xl text-white drop-shadow-md">
                        {form}
                      </span>
                    </div>
                    <div className="flex-grow">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-bold transition-colors text-sm md:text-base ${
                          isOwner 
                            ? 'text-red-900 group-hover:text-red-950'
                            : 'text-blue-900 group-hover:text-blue-950'
                        }`}>
                          Form {form}
                        </h3>
                        {isOwner && (
                          <span className="bg-gradient-to-r from-amber-500 to-amber-600 text-amber-900 text-xs font-bold px-2 py-1 rounded-full shadow-sm border border-amber-700">
                            👑
                          </span>
                        )}
                      </div>
                      <p className="text-xs md:text-sm font-medium ${
                        isOwner ? 'text-red-800' : 'text-blue-800'
                      }">
                        {isOwner ? 'Class Owner' : 'Class Teacher'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Bottom section with handwritten style */}
                  <div className="flex items-center justify-between pt-3 border-t-2 border-dashed ${
                    isOwner ? 'border-amber-400' : 'border-slate-400'
                  }">
                    <span className="text-xs md:text-sm font-semibold ${
                      isOwner ? 'text-amber-900' : 'text-slate-900'
                    }">
                      📚 {stats?.forms?.[form] || 0} assignments
                    </span>
                    <span className={`text-lg group-hover:translate-x-1 transition-transform font-bold ${
                      isOwner ? 'text-red-700' : 'text-blue-700'
                    }`}>→</span>
                  </div>
                </div>
              </div>
              </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-gradient-to-br from-amber-200 to-orange-200 rounded-2xl shadow-2xl p-12 text-center border-4 border-amber-400 relative">
            {/* Pushpin on empty state */}
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-5 h-5 bg-red-600 rounded-full shadow-lg"></div>
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg border-2 border-blue-800">
              <span className="text-4xl">🏫</span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No Classes Assigned</h3>
            <p className="text-gray-700 font-medium">No assigned classes found for your account</p>
          </div>
        )}
        </div>
      </div>
    </div>
  );

  const MonitorView = () => {
    const [connectedStudents, setConnectedStudents] = useState([]);

    useEffect(() => {
      const fetchActiveStudents = async () => {
        try {
          const response = await fetch('http://localhost:8000/active-students');
          const data = await response.json();
          setConnectedStudents(data);
        } catch (error) {
          console.error('Error fetching active students:', error);
        }
      };

      fetchActiveStudents();
      const interval = setInterval(fetchActiveStudents, 5000); // Refresh every 5 seconds

      return () => clearInterval(interval);
    }, []);

    return (
      <div className="h-screen bg-gray-50 flex flex-col">
        {/* Header */}
        <div className="bg-white shadow-sm border-b flex-shrink-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => goToHome()}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">👁️ Live Monitor</h1>
                  <p className="text-gray-600">Monitor student screens in real-time</p>
                </div>
              </div>
              <button
                onClick={() => goToHome()}
                className="p-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-6 bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Connected Students</h3>
                  <p className="text-sm text-gray-600">
                    {connectedStudents.length} student{connectedStudents.length !== 1 ? 's' : ''} currently online
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-green-600 font-medium">Live</span>
                </div>
              </div>
            </div>
            
            <LiveMonitorTab_dynamic 
              connectedStudents={connectedStudents} 
              students={students} 
            />
          </div>
        </div>
      </div>
    );
  };

  const AssignmentsView = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get('tab') || 'my-assignments';
    const mainTabFromUrl = searchParams.get('mainTab') || 'assignments';
    const [activeTab, setActiveTab] = useState(tabFromUrl);
    const [mainTab, setMainTab] = useState(mainTabFromUrl); // Main tabs: assignments, courses, tests
    const [showNewMenu, setShowNewMenu] = useState(false); // For plus button dropdown
    
    // Listening assignment states
    const [listeningAssignments, setListeningAssignments] = useState([]);
    const [showListeningModal, setShowListeningModal] = useState(false);
    const [currentListeningAssignment, setCurrentListeningAssignment] = useState(null);
    const [listeningMediaType, setListeningMediaType] = useState('audio'); // audio, video, record
    const [listeningMediaFile, setListeningMediaFile] = useState(null);
    const [listeningVideoLink, setListeningVideoLink] = useState('');
    const [listeningTitle, setListeningTitle] = useState('');
    const [listeningDescription, setListeningDescription] = useState('');
    const [listeningQuestions, setListeningQuestions] = useState([]);
    const [isRecording, setIsRecording] = useState(false);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [recordedAudio, setRecordedAudio] = useState(null);
    const [selectedYears, setSelectedYears] = useState([]);
    const [openOptionsMenu, setOpenOptionsMenu] = useState(null); // Track which assignment's menu is open

    // Reading assignment states
    const [readingAssignments, setReadingAssignments] = useState([]);
    const [showReadingModal, setShowReadingModal] = useState(false);
    const [currentReadingAssignment, setCurrentReadingAssignment] = useState(null);
    const [readingTitle, setReadingTitle] = useState('');
    const [readingPassage, setReadingPassage] = useState('');
    const [readingSelectedYears, setReadingSelectedYears] = useState([]);
    const [readingOptionsMenu, setReadingOptionsMenu] = useState(null);

    // Reading recordings review states
    const [showRecordingsView, setShowRecordingsView] = useState(false);
    const [selectedRecordingAssignment, setSelectedRecordingAssignment] = useState(null);
    const [assignmentRecordings, setAssignmentRecordings] = useState([]);
    const [assignmentAccuracy, setAssignmentAccuracy] = useState([]);
    const [assignmentStudentsTree, setAssignmentStudentsTree] = useState({});
    const [recordingsLoading, setRecordingsLoading] = useState(false);
    const [playingAudioId, setPlayingAudioId] = useState(null);
    const [expandedYears, setExpandedYears] = useState({});
    const [expandedForms, setExpandedForms] = useState({});
    const audioRefs = React.useRef({});

    // Sync mainTab and activeTab with URL params (only when in assignments view)
    useEffect(() => {
      const currentModule = searchParams.get('module');
      const currentMainTab = searchParams.get('mainTab');
      const currentTab = searchParams.get('tab');
      
      // Only update if we're in assignments module and values are different
      if ((currentModule === 'assignments' || !currentModule) && 
          (currentMainTab !== mainTab || currentTab !== activeTab)) {
        const params = new URLSearchParams(searchParams);
        params.set('mainTab', mainTab);
        params.set('tab', activeTab);
        params.set('module', 'assignments');
        setSearchParams(params, { replace: true });
      }
    }, [mainTab, activeTab]);

    // Load listening assignments on mount
    useEffect(() => {
      if (mainTab === 'assignments' && activeTab === 'listening' && teacherEmail) {
        loadListeningAssignments();
      }
      if (mainTab === 'assignments' && activeTab === 'reading' && teacherEmail) {
        loadReadingAssignments();
      }
    }, [mainTab, activeTab, teacherEmail]);

    // Load listening assignments from backend
    const loadListeningAssignments = async () => {
      try {
        const response = await fetch(buildApiUrl(`/api/listening/assignments/${teacherEmail}`));
        const data = await response.json();
        if (data.success) {
          setListeningAssignments(data.assignments);
        }
      } catch (error) {
        console.error('Error loading listening assignments:', error);
      }
    };

    // Delete listening assignment
    const handleDeleteListeningAssignment = async (assignmentId) => {
      if (!window.confirm('Are you sure you want to delete this assignment? This action cannot be undone.')) {
        return;
      }

      try {
        const response = await fetch(buildApiUrl(`/api/listening/assignments/${assignmentId}`), {
          method: 'DELETE'
        });
        const data = await response.json();
        
        if (data.success) {
          // Remove from local state
          setListeningAssignments(listeningAssignments.filter(a => a.id !== assignmentId));
          setOpenOptionsMenu(null);
          alert('Assignment deleted successfully!');
        } else {
          alert('Failed to delete assignment: ' + data.error);
        }
      } catch (error) {
        console.error('Error deleting assignment:', error);
        alert('Error deleting assignment. Please try again.');
      }
    };

    // Edit listening assignment - populate form with existing data
    const handleEditListeningAssignment = (assignment) => {
      setCurrentListeningAssignment(assignment);
      setListeningTitle(assignment.title);
      setListeningDescription(assignment.description);
      setListeningMediaType(assignment.mediaType);
      setListeningVideoLink(assignment.videoLink || '');
      setListeningQuestions(assignment.questions || []);
      setSelectedYears(assignment.assignedTo || []);
      setShowListeningModal(true);
      setOpenOptionsMenu(null);
    };

    // Save listening assignment to backend (create or update)
    const saveListeningAssignment = async () => {
      try {
        const isEditing = currentListeningAssignment !== null;
        console.log(`🎧 Starting to ${isEditing ? 'update' : 'create'} listening assignment...`);
        console.log('Title:', listeningTitle);
        console.log('Teacher Email:', teacherEmail);
        console.log('Media Type:', listeningMediaType);
        console.log('Questions:', listeningQuestions);
        
        let mediaUrl = null;
        
        // Upload media file if exists
        if (listeningMediaFile || recordedAudio) {
          console.log('📤 Uploading media file...');
          const formData = new FormData();
          if (listeningMediaFile) {
            formData.append('file', listeningMediaFile);
            formData.append('media_type', listeningMediaType === 'upload-video' ? 'video' : 'audio');
          } else if (recordedAudio) {
            formData.append('file', recordedAudio, 'recording.webm');
            formData.append('media_type', 'recorded');
          }
          
          const uploadUrl = buildApiUrl('/api/listening/upload-media');
          console.log('Upload URL:', uploadUrl);
          
          const uploadResponse = await fetch(uploadUrl, {
            method: 'POST',
            body: formData
          });
          
          console.log('Upload response status:', uploadResponse.status);
          const uploadData = await uploadResponse.json();
          console.log('Upload response:', uploadData);
          
          if (uploadData.success) {
            mediaUrl = uploadData.url;
            console.log('✅ Media uploaded:', mediaUrl);
          }
        }
        
        // Prepare assignment data
        const assignmentData = {
          title: listeningTitle,
          description: listeningDescription,
          teacherEmail: teacherEmail,
          teacherId: teacherEmail,
          mediaType: listeningMediaType,
          videoLink: listeningVideoLink,
          questions: listeningQuestions,
          assignedTo: selectedYears,
          schoolId: 'default'
        };
        
        console.log('Assignment data:', assignmentData);
        
        // Create or update assignment
        let apiUrl, method;
        if (isEditing) {
          apiUrl = buildApiUrl(`/api/listening/assignments/${currentListeningAssignment.id}`);
          method = 'PUT';
          console.log('📝 Updating assignment...');
        } else {
          apiUrl = buildApiUrl('/api/listening/assignments');
          method = 'POST';
          console.log('📝 Creating assignment...');
        }
        
        console.log('API URL:', apiUrl);
        
        const response = await fetch(apiUrl, {
          method: method,
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(assignmentData)
        });
        
        console.log('Response status:', response.status);
        const data = await response.json();
        console.log('Response:', data);
        
        const assignmentId = isEditing ? currentListeningAssignment.id : data.assignmentId;
        
        if (data.success && mediaUrl) {
          // Update media URL
          console.log('📎 Updating media URL...');
          const urlFormData = new FormData();
          urlFormData.append('media_url', mediaUrl);
          
          const updateUrl = buildApiUrl(`/api/listening/assignments/${assignmentId}/media`);
          console.log('Update URL:', updateUrl);
          
          await fetch(updateUrl, {
            method: 'PUT',
            body: urlFormData
          });
        }
        
        // Reload assignments
        console.log('🔄 Reloading assignments...');
        await loadListeningAssignments();
        setShowListeningModal(false);
        
        // Reset form
        setListeningTitle('');
        setListeningDescription('');
        setListeningMediaType('audio');
        setListeningMediaFile(null);
        setListeningVideoLink('');
        setListeningQuestions([]);
        setRecordedAudio(null);
        setSelectedYears([]);
        setCurrentListeningAssignment(null);
        
        console.log(`✅ Assignment ${isEditing ? 'updated' : 'created'} successfully!`);
        alert(`Listening assignment ${isEditing ? 'updated' : 'created'} successfully!`);
      } catch (error) {
        console.error(`❌ Error ${currentListeningAssignment ? 'updating' : 'creating'} listening assignment:`, error);
        alert(`Error ${currentListeningAssignment ? 'updating' : 'creating'} assignment. Please try again.`);
      }
    };

    // ========== READING ASSIGNMENT FUNCTIONS ==========
    const loadReadingAssignments = async () => {
      try {
        const response = await fetch(buildApiUrl(`/api/reading/assignments/${teacherEmail}`));
        const data = await response.json();
        if (data.success) {
          setReadingAssignments(data.assignments);
        }
      } catch (error) {
        console.error('Error loading reading assignments:', error);
      }
    };

    const handleDeleteReadingAssignment = async (assignmentId) => {
      if (!window.confirm('Are you sure you want to delete this reading assignment?')) return;
      try {
        const response = await fetch(buildApiUrl(`/api/reading/assignments/${assignmentId}`), { method: 'DELETE' });
        const data = await response.json();
        if (data.success) {
          setReadingAssignments(readingAssignments.filter(a => a.id !== assignmentId));
          setReadingOptionsMenu(null);
          alert('Reading assignment deleted successfully!');
        }
      } catch (error) {
        console.error('Error deleting reading assignment:', error);
        alert('Error deleting assignment.');
      }
    };

    const handleEditReadingAssignment = (assignment) => {
      setCurrentReadingAssignment(assignment);
      setReadingTitle(assignment.title);
      setReadingPassage(assignment.passage);
      setReadingSelectedYears(assignment.assignedTo || []);
      setShowReadingModal(true);
      setReadingOptionsMenu(null);
    };

    const saveReadingAssignment = async () => {
      try {
        const isEditing = currentReadingAssignment !== null;
        const assignmentData = {
          title: readingTitle,
          passage: readingPassage,
          teacherEmail: teacherEmail,
          teacherId: teacherEmail,
          assignedTo: readingSelectedYears,
          schoolId: 'default'
        };

        let apiUrl, method;
        if (isEditing) {
          apiUrl = buildApiUrl(`/api/reading/assignments/${currentReadingAssignment.id}`);
          method = 'PUT';
        } else {
          apiUrl = buildApiUrl('/api/reading/assignments');
          method = 'POST';
        }

        const response = await fetch(apiUrl, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(assignmentData)
        });
        const data = await response.json();

        if (data.success) {
          await loadReadingAssignments();
          setShowReadingModal(false);
          setReadingTitle('');
          setReadingPassage('');
          setReadingSelectedYears([]);
          setCurrentReadingAssignment(null);
          alert(`Reading assignment ${isEditing ? 'updated' : 'created'} successfully!`);
        } else {
          alert('Failed to save reading assignment.');
        }
      } catch (error) {
        console.error('Error saving reading assignment:', error);
        alert('Error saving assignment. Please try again.');
      }
    };

    // Load recordings, accuracy AND all students for a specific assignment (teacher review)
    const loadAssignmentRecordings = async (assignment) => {
      setSelectedRecordingAssignment(assignment);
      setShowRecordingsView(true);
      setRecordingsLoading(true);
      setPlayingAudioId(null);
      setExpandedYears({});
      setExpandedForms({});
      try {
        const yearsParam = (assignment.assignedTo || []).join(',');
        const [recRes, accRes, studentsRes] = await Promise.all([
          fetch(buildApiUrl(`/api/reading/recordings/${assignment.id}/all`)),
          fetch(buildApiUrl(`/api/reading/accuracy/${assignment.id}/all`)),
          yearsParam ? fetch(buildApiUrl(`/api/reading/students-by-years?years=${encodeURIComponent(yearsParam)}`)) : Promise.resolve(null)
        ]);
        const recData = await recRes.json();
        const accData = await accRes.json();
        if (recData.success) setAssignmentRecordings(recData.recordings || []);
        if (accData.success) setAssignmentAccuracy(accData.results || []);
        if (studentsRes) {
          const studentsData = await studentsRes.json();
          if (studentsData.success) setAssignmentStudentsTree(studentsData.years || {});
        } else {
          setAssignmentStudentsTree({});
        }
      } catch (error) {
        console.error('Error loading recordings:', error);
      } finally {
        setRecordingsLoading(false);
      }
    };

    const closeRecordingsView = () => {
      // Stop any playing audio
      Object.values(audioRefs.current).forEach(audio => {
        if (audio) { audio.pause(); audio.currentTime = 0; }
      });
      setShowRecordingsView(false);
      setSelectedRecordingAssignment(null);
      setAssignmentRecordings([]);
      setAssignmentAccuracy([]);
      setAssignmentStudentsTree({});
      setPlayingAudioId(null);
      setExpandedYears({});
      setExpandedForms({});
    };

    const toggleYear = (year) => {
      setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }));
    };

    const toggleForm = (formKey) => {
      setExpandedForms(prev => ({ ...prev, [formKey]: !prev[formKey] }));
    };

    const toggleAudioPlay = (recordingId, assignmentId, studentId) => {
      const audioKey = recordingId;
      if (playingAudioId === audioKey) {
        // Pause
        if (audioRefs.current[audioKey]) {
          audioRefs.current[audioKey].pause();
        }
        setPlayingAudioId(null);
      } else {
        // Stop previous
        if (playingAudioId && audioRefs.current[playingAudioId]) {
          audioRefs.current[playingAudioId].pause();
          audioRefs.current[playingAudioId].currentTime = 0;
        }
        // Play new
        const audioUrl = buildApiUrl(`/api/reading/recordings/${assignmentId}/${studentId}/audio`);
        if (!audioRefs.current[audioKey]) {
          audioRefs.current[audioKey] = new Audio(audioUrl);
          audioRefs.current[audioKey].onended = () => setPlayingAudioId(null);
        }
        audioRefs.current[audioKey].play();
        setPlayingAudioId(audioKey);
      }
    };

    const formatDuration = (seconds) => {
      if (!seconds) return '0:00';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Subject icon and color mapping for course cards
    const getSubjectStyle = (subject) => {
      const styles = {
        'English': { icon: '📝', gradient: 'from-blue-500 to-cyan-500', shadow: 'shadow-blue-300/50' },
        'Mathematics': { icon: '🔢', gradient: 'from-green-500 to-emerald-500', shadow: 'shadow-green-300/50' },
        'Arabic': { icon: '🕌', gradient: 'from-amber-500 to-orange-500', shadow: 'shadow-amber-300/50' },
        'Islamic': { icon: '☪️', gradient: 'from-teal-500 to-green-600', shadow: 'shadow-teal-300/50' },
        'Qatar History': { icon: '🏛️', gradient: 'from-rose-500 to-pink-600', shadow: 'shadow-rose-300/50' },
        'Science': { icon: '🔬', gradient: 'from-violet-500 to-purple-600', shadow: 'shadow-violet-300/50' },
        'History': { icon: '📜', gradient: 'from-yellow-500 to-amber-600', shadow: 'shadow-yellow-300/50' },
      };
      return styles[subject] || { icon: '📖', gradient: 'from-purple-500 to-indigo-600', shadow: 'shadow-purple-300/50' };
    };

    // Update URL when tab changes
    const handleTabChange = (newTab) => {
      setActiveTab(newTab);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', newTab);
      setSearchParams(newSearchParams, { replace: true });
    };

    return (
      <div className="h-screen bg-gradient-to-br from-yellow-50 via-blue-50 to-red-50 flex overflow-hidden">
        {/* Left Sidebar - Thin Navigation */}
        <div className="w-20 bg-gradient-to-b from-purple-600 via-red-500 to-orange-500 flex flex-col items-center space-y-4 shadow-2xl relative overflow-hidden py-4">
          {/* School stationary pattern background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
            <div className="absolute w-8 h-1 bg-white rounded top-8 left-2 rotate-45"></div>
            <div className="absolute w-6 h-1 bg-white rounded top-20 right-3 -rotate-12"></div>
            <div className="absolute w-10 h-1 bg-white rounded top-40 left-1 rotate-12"></div>
            <div className="absolute w-7 h-1 bg-white rounded top-56 right-2 -rotate-45"></div>
            <div className="absolute w-9 h-1 bg-white rounded top-72 left-3 rotate-6"></div>
          </div>

          {/* Back Button */}
          <button
            onClick={() => {
              setCurrentView('home');
              setSearchParams({});
            }}
            className="w-14 h-14 rounded-xl transition-all duration-300 flex flex-col items-center justify-center group relative z-10 bg-white/10 hover:bg-white/20"
            title="Back to Home"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-white text-[10px] mt-1 font-medium">Back</span>
          </button>

          {/* Divider */}
          <div className="w-12 h-px bg-white/30 z-10"></div>

          {/* Assignments Button */}
          <button
            onClick={() => {
              setMainTab('assignments');
              setActiveTab('my-assignments');
            }}
            className={`w-14 h-14 rounded-xl transition-all duration-300 flex flex-col items-center justify-center group relative z-10 ${
              mainTab === 'assignments' ? 'bg-gradient-to-br from-purple-500 to-indigo-600 shadow-lg shadow-purple-500/50' : 'bg-white/10 hover:bg-white/20'
            }`}
            title="Assignments"
          >
            <span className={`text-2xl ${mainTab === 'assignments' ? 'scale-110' : ''} transition-transform`}>📝</span>
            <span className="text-white text-[10px] mt-1 font-medium">Assignment</span>
          </button>

          {/* Courses Button */}
          <button
            onClick={() => {
              setActiveTab('courses');
              setMainTab('courses');
              setSelectedCourse(null);
              setSelectedTopic(null);
              setSelectedNugget(null);
              setNuggetDashboardView(false);
            }}
            className={`w-14 h-14 rounded-xl transition-all duration-300 flex flex-col items-center justify-center group relative z-10 ${
              mainTab === 'courses' ? 'bg-gradient-to-br from-red-500 to-pink-600 shadow-lg shadow-red-500/50' : 'bg-white/10 hover:bg-white/20'
            }`}
            title="Courses"
          >
            <span className={`text-2xl ${mainTab === 'courses' ? 'scale-110' : ''} transition-transform`}>📚</span>
            <span className="text-white text-[10px] mt-1 font-medium">Courses</span>
          </button>

          {/* Tests Button */}
          <button
            onClick={() => {
              setActiveTab('tests');
              setMainTab('tests');
              setSelectedCourse(null);
              setSelectedTopic(null);
              setSelectedNugget(null);
              setNuggetDashboardView(false);
            }}
            className={`w-14 h-14 rounded-xl transition-all duration-300 flex flex-col items-center justify-center group relative z-10 ${
              mainTab === 'tests' ? 'bg-gradient-to-br from-orange-500 to-red-600 shadow-lg shadow-orange-500/50' : 'bg-white/10 hover:bg-white/20'
            }`}
            title="Tests"
          >
            <span className={`text-2xl ${mainTab === 'tests' ? 'scale-110' : ''} transition-transform`}>📝</span>
            <span className="text-white text-[10px] mt-1 font-medium">Tests</span>
          </button>

          {/* Sub-items for Assignments */}
          {mainTab === 'assignments' && (
            <>
              <div className="w-12 h-px bg-white/30"></div>

              {/* Drafts */}
              <button
                onClick={() => {
                  setActiveTab('saved');
                  loadSavedAssignments();
                }}
                className={`w-14 h-14 rounded-xl transition-all duration-300 flex flex-col items-center justify-center group relative z-10 ${
                  activeTab === 'saved' ? 'bg-gradient-to-br from-teal-400 to-cyan-500 shadow-lg shadow-teal-400/50' : 'bg-white/10 hover:bg-white/20'
                }`}
                title="Drafts"
              >
                <span className={`text-2xl ${activeTab === 'saved' ? 'scale-110' : ''} transition-transform`}>📄</span>
                <span className="text-white text-[10px] mt-1 font-medium">Drafts</span>
              </button>

              {/* Bank */}
              <button
                onClick={() => {
                  handleTabChange('homework-bank');
                  handleViewChange("homework-bank");
                }}
                className={`w-14 h-14 rounded-xl transition-all duration-300 flex flex-col items-center justify-center group relative z-10 ${
                  activeTab === 'homework-bank' ? 'bg-gradient-to-br from-purple-400 to-indigo-500 shadow-lg shadow-purple-400/50' : 'bg-white/10 hover:bg-white/20'
                }`}
                title="Bank"
              >
                <span className={`text-2xl ${activeTab === 'homework-bank' ? 'scale-110' : ''} transition-transform`}>🏦</span>
                <span className="text-white text-[10px] mt-1 font-medium">Bank</span>
              </button>

              {/* Writing */}
              <button
                onClick={() => setActiveTab('writing')}
                className={`w-14 h-14 rounded-xl transition-all duration-300 flex flex-col items-center justify-center group relative z-10 ${
                  activeTab === 'writing' ? 'bg-gradient-to-br from-blue-400 to-indigo-500 shadow-lg shadow-blue-400/50' : 'bg-white/10 hover:bg-white/20'
                }`}
                title="Writing"
              >
                <span className={`text-2xl ${activeTab === 'writing' ? 'scale-110' : ''} transition-transform`}>✍️</span>
                <span className="text-white text-[10px] mt-1 font-medium">Writing</span>
              </button>

              {/* Listening */}
              <button
                onClick={() => setActiveTab('listening')}
                className={`w-14 h-14 rounded-xl transition-all duration-300 flex flex-col items-center justify-center group relative z-10 ${
                  activeTab === 'listening' ? 'bg-gradient-to-br from-pink-400 to-rose-500 shadow-lg shadow-pink-400/50' : 'bg-white/10 hover:bg-white/20'
                }`}
                title="Listening"
              >
                <span className={`text-2xl ${activeTab === 'listening' ? 'scale-110' : ''} transition-transform`}>🎧</span>
                <span className="text-white text-[10px] mt-1 font-medium">Listening</span>
              </button>

              {/* Reading */}
              <button
                onClick={() => setActiveTab('reading')}
                className={`w-14 h-14 rounded-xl transition-all duration-300 flex flex-col items-center justify-center group relative z-10 ${
                  activeTab === 'reading' ? 'bg-gradient-to-br from-yellow-400 to-orange-500 shadow-lg shadow-yellow-400/50' : 'bg-white/10 hover:bg-white/20'
                }`}
                title="Reading"
              >
                <span className={`text-2xl ${activeTab === 'reading' ? 'scale-110' : ''} transition-transform`}>📖</span>
                <span className="text-white text-[10px] mt-1 font-medium">Reading</span>
              </button>
            </>
          )}
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header with Space Theme */}
          <div className="bg-white/80 backdrop-blur-md shadow-lg border-b border-purple-200/50 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
                  <span className="text-3xl">🚀</span>
                </div>
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                    Learning Hub
                  </h1>
                  <p className="text-gray-600 text-sm mt-1">Explore the universe of learning</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="px-4 py-2 bg-purple-100 rounded-lg">
                  <span className="text-purple-700 font-semibold text-sm">{myHomeworks.length} Active Missions</span>
                </div>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-8">
            {/* Debug: Log current state */}
            {(() => {
              console.log('📊 Current render state - mainTab:', mainTab, 'activeTab:', activeTab, 'selectedCourse:', selectedCourse?.name || 'none');
              return null;
            })()}
            {/* Assignments Tab */}
            {mainTab === 'assignments' && activeTab === 'my-assignments' && (
              <div className="space-y-6">
                {/* Header with Create Button */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-800">My Assignments</h2>
                    <p className="text-gray-600 mt-1">Manage and track your assignments</p>
                  </div>
                  <button
                    onClick={() => navigate("/homeworks?tab=generate")}
                    className="px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl hover:from-purple-600 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center space-x-2 font-semibold"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span>Create Assignment</span>
                  </button>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-4 gap-6 mb-8">
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-blue-200/50 hover:shadow-xl transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-4xl">🎯</span>
                      <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-600 font-bold">{myHomeworks.length}</span>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm font-medium">Total Assignments</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-green-200/50 hover:shadow-xl transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-4xl">✅</span>
                      <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                        <span className="text-green-600 font-bold">{myHomeworks.filter(hw => hw.status === 'active').length}</span>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm font-medium">Active</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-orange-200/50 hover:shadow-xl transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-4xl">⏰</span>
                      <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                        <span className="text-orange-600 font-bold">{stats?.due_summary?.overdue || 0}</span>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm font-medium">Overdue</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-purple-200/50 hover:shadow-xl transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-4xl">📊</span>
                      <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                        <span className="text-purple-600 font-bold">{Object.keys(submittedCount).length}</span>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm font-medium">Submissions</p>
                  </div>
                </div>

                {/* Assignments Table */}
                {myHomeworks.length === 0 ? (
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-16 text-center border border-purple-200/50">
                    <div className="w-32 h-32 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <span className="text-6xl">🌌</span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">No Missions Yet</h3>
                    <p className="text-gray-600 mb-8 text-lg">Start your teaching journey by creating your first assignment</p>
                    <button
                      onClick={() => navigate("/homeworks?tab=generate")}
                      className="px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl font-semibold text-lg"
                    >
                      🚀 Launch New Assignment
                    </button>
                  </div>
                ) : (
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden border border-purple-200/50">
                    {/* Table Header */}
                    <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
                      <h2 className="text-white font-bold text-xl flex items-center">
                        <span className="mr-3">🎓</span>
                        My Assignments
                      </h2>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gradient-to-r from-purple-50 to-indigo-50 border-b-2 border-purple-200">
                          <tr>
                            <th className="px-6 py-4 text-left text-sm font-bold text-purple-900">Title & Subject</th>
                            <th className="px-6 py-4 text-left text-sm font-bold text-purple-900">Assigned To</th>
                            <th className="px-6 py-4 text-left text-sm font-bold text-purple-900">Due Date</th>
                            <th className="px-6 py-4 text-center text-sm font-bold text-purple-900">Submissions</th>
                            <th className="px-6 py-4 text-center text-sm font-bold text-purple-900">Status</th>
                            <th className="px-6 py-4 text-center text-sm font-bold text-purple-900">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-purple-100">
                          {myHomeworks.map((hw, index) => (
                            <tr 
                              key={hw.id} 
                              className="hover:bg-purple-50/50 transition-colors group"
                              style={{ animationDelay: `${index * 50}ms` }}
                            >
                              <td className="px-6 py-5">
                                <div className="flex items-center space-x-3">
                                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center flex-shrink-0 shadow-md">
                                    <span className="text-white text-lg">📝</span>
                                  </div>
                                  <div>
                                    <button
                                      onClick={() => navigate(`/view-homework/${hw.id}`)}
                                      className="font-semibold text-purple-700 hover:text-purple-900 text-left transition-colors"
                                    >
                                      {hw.title}
                                    </button>
                                    <p className="text-sm text-gray-600">{hw.subject}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-5">
                                <div className="flex flex-wrap gap-2">
                                  {hw.assignedTo && hw.assignedTo.slice(0, 3).map((form) => (
                                    <span key={form} className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
                                      {form}
                                    </span>
                                  ))}
                                  {hw.assignedTo && hw.assignedTo.length > 3 && (
                                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                                      +{hw.assignedTo.length - 3}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-5">
                                <div className="flex items-center space-x-2">
                                  <span className="text-2xl">📅</span>
                                  <span className="text-sm font-medium text-gray-700">{formatDate(hw.dueDate)}</span>
                                </div>
                              </td>
                              <td className="px-6 py-5 text-center">
                                <div className="inline-flex items-center space-x-2 px-4 py-2 bg-green-50 rounded-lg border border-green-200">
                                  <span className="text-lg">📊</span>
                                  <span className="font-bold text-green-700">
                                    {submittedCount[hw.id] || 0}/{getTotalStudents(hw)}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-5 text-center">
                                <span className={`px-4 py-2 rounded-full text-xs font-bold inline-block ${
                                  hw.status === 'active' 
                                    ? 'bg-gradient-to-r from-green-400 to-emerald-500 text-white shadow-md' 
                                    : 'bg-gray-200 text-gray-700'
                                }`}>
                                  {hw.status === 'active' ? '🟢 Active' : '⚫ Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-5">
                                <div className="flex items-center justify-center space-x-2">
                                  <button
                                    onClick={() => {
                                      const hasAttachments = Array.isArray(hw.attachments) && hw.attachments.length > 0;
                                      const hasQuestions = Array.isArray(hw.questions) && hw.questions.length > 0;
                                      if (hasAttachments) {
                                        navigate(`/interactive-homework/${hw.id}`);
                                      } else if (hasQuestions) {
                                        navigate(`/view-homework/${hw.id}`);
                                      } else {
                                        alert('⚠️ This assignment has no content to view.');
                                      }
                                    }}
                                    className="p-2 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors group-hover:scale-110 transform duration-200"
                                    title="View"
                                  >
                                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => handleEditAssignment(hw)}
                                    className="p-2 bg-green-100 hover:bg-green-200 rounded-lg transition-colors group-hover:scale-110 transform duration-200"
                                    title="Edit"
                                  >
                                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => handleViewSubmissions(hw)}
                                    className="p-2 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors group-hover:scale-110 transform duration-200"
                                    title="Submissions"
                                  >
                                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => confirmDeleteAssignment(hw)}
                                    className="p-2 bg-red-100 hover:bg-red-200 rounded-lg transition-colors group-hover:scale-110 transform duration-200"
                                    title="Delete"
                                  >
                                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Courses Tab Content */}
            {/* Courses Tab */}
            {mainTab === 'courses' && activeTab === 'courses' && (
              <div className="space-y-6">
                {/* Sub-tabs for Courses Section */}
                {!selectedCourse && (
                  <div className="flex gap-2 mb-6">
                    <button
                      onClick={() => setCourseSubTab('courses')}
                      className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                        courseSubTab === 'courses'
                          ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg'
                          : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      📚 Courses
                    </button>
                    <button
                      onClick={() => setCourseSubTab('analytics')}
                      className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                        courseSubTab === 'analytics'
                          ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-lg'
                          : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      📊 Course Analytics
                    </button>
                  </div>
                )}

                {/* Courses List Tab */}
                {courseSubTab === 'courses' && !selectedCourse && (
                  /* Courses List View */
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-purple-200/50">
                    <div className="flex justify-end items-center mb-6">
                      <div className="flex items-center gap-3">
                        <select
                          value={yearFilter}
                          onChange={(e) => setYearFilter(e.target.value)}
                          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                        >
                          <option value="all">All Years</option>
                          {['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13'].map(year => (
                            <option key={year} value={year}>Year {year}</option>
                          ))}
                        </select>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('Add Course button clicked, activeTab:', activeTab);
                            setShowAddCourseModal(true);
                          }}
                          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl font-semibold flex items-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add Course
                        </button>
                      </div>
                    </div>

                    {courses.length === 0 ? (
                      <div className="text-center py-16">
                        <div className="w-24 h-24 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <span className="text-5xl">📚</span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">No Courses Yet</h3>
                        <p className="text-gray-600">Start building your curriculum by creating your first course</p>
                      </div>
                    ) : (
                      <div className="space-y-8">
                        {/* Group courses by year */}
                        {(() => {
                          // Group courses by assigned year
                          const coursesByYear = {};
                          const unassignedCourses = [];
                          
                          courses.forEach(course => {
                            if (course.assigned_years && course.assigned_years.length > 0) {
                              course.assigned_years.forEach(year => {
                                if (!coursesByYear[year]) {
                                  coursesByYear[year] = [];
                                }
                                coursesByYear[year].push(course);
                              });
                            } else {
                              unassignedCourses.push(course);
                            }
                          });
                          
                          // Sort years numerically
                          const sortedYears = Object.keys(coursesByYear)
                            .filter(year => yearFilter === 'all' || year === yearFilter)
                            .sort((a, b) => parseInt(a) - parseInt(b));
                          
                          const filteredUnassignedCourses = yearFilter === 'all' ? unassignedCourses : [];
                          
                          return (
                            <>
                              {sortedYears.map(year => (
                                <div key={year} className="space-y-4">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-md">
                                      <span className="text-white font-bold text-lg">{year}</span>
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900">Year {year} Courses</h3>
                                    <span className="text-sm text-gray-500">({coursesByYear[year].length})</span>
                                  </div>
                                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                                    {coursesByYear[year].map((course, index) => (
                          <div
                            key={index}
                            className="bg-white rounded-2xl p-5 border border-purple-100 hover:border-purple-300 hover:shadow-xl transition-all duration-300 cursor-pointer group relative"
                          >
                            {/* Three-dot menu */}
                            <div className="absolute top-3 right-3 z-10">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCourseMenuOpenId(courseMenuOpenId === `assigned-${course.id}` ? null : `assigned-${course.id}`);
                                }}
                                className="p-1 rounded-full hover:bg-gray-100 transition-colors opacity-0 group-hover:opacity-100"
                                title="More options"
                              >
                                <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                                  <circle cx="12" cy="5" r="1.5" />
                                  <circle cx="12" cy="12" r="1.5" />
                                  <circle cx="12" cy="19" r="1.5" />
                                </svg>
                              </button>
                              {courseMenuOpenId === `assigned-${course.id}` && (
                                <>
                                  <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setCourseMenuOpenId(null); }} />
                                  <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-20">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCourseMenuOpenId(null);
                                        setEditingCourse(course);
                                        setShowEditCourseModal(true);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-2 transition-colors"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                      Edit
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCourseMenuOpenId(null);
                                        setCourseToAssign(course);
                                        setShowAssignCourseModal(true);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 flex items-center gap-2 transition-colors"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                      </svg>
                                      Assign to Students
                                    </button>
                                    <div className="border-t border-gray-100 my-1" />
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCourseMenuOpenId(null);
                                        if (window.confirm(`Delete course "${course.name}"? This will also delete all topics and nuggets.`)) {
                                          handleDeleteCourse(course.id);
                                        }
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                      Delete
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                            <div onClick={() => {
                              console.log('🔍 Clicking course card (assigned):', course.name);
                              // Batch all state updates together
                              setMainTab(() => {
                                console.log('🔍 Setting mainTab to: courses');
                                return 'courses';
                              });
                              setActiveTab(() => {
                                console.log('🔍 Setting activeTab to: courses');
                                return 'courses';
                              });
                              setSelectedCourse(() => course);
                            }}>
                            <div className="flex flex-col items-center text-center">
                              {(() => { const s = getSubjectStyle(course.subject); return (
                              <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${s.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:${s.shadow} transition-all duration-300 mb-3 ring-4 ring-white`}>
                                <span className="text-3xl">{s.icon}</span>
                              </div>
                              ); })()}
                              <h3 className="text-sm font-bold text-gray-900 mb-1 group-hover:text-purple-600 transition-colors line-clamp-2">
                                {course.name}
                              </h3>
                              {course.subject && (
                                <span className="text-[10px] text-gray-500 font-medium mb-1">
                                  {course.subject}
                                </span>
                              )}
                              <p className="text-[11px] text-gray-500 line-clamp-2 mb-2">
                                {course.description || 'No description provided'}
                              </p>
                              <span className="text-[10px] text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full font-medium">
                                {course.topics?.length || 0} topics
                              </span>
                            </div>
                            </div>
                          </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                              
                              {/* Unassigned courses */}
                              {filteredUnassignedCourses.length > 0 && (
                                <div className="space-y-4">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center shadow-md">
                                      <span className="text-white font-bold text-lg">?</span>
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900">Unassigned Courses</h3>
                                    <span className="text-sm text-gray-500">({filteredUnassignedCourses.length})</span>
                                  </div>
                                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                                    {filteredUnassignedCourses.map((course, index) => (
                                      <div
                                        key={index}
                                        className="bg-white rounded-2xl p-5 border border-purple-100 hover:border-purple-300 hover:shadow-xl transition-all duration-300 cursor-pointer group relative"
                                      >
                                        {/* Three-dot menu */}
                                        <div className="absolute top-3 right-3 z-10">
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setCourseMenuOpenId(courseMenuOpenId === `unassigned-${course.id}` ? null : `unassigned-${course.id}`);
                                            }}
                                            className="p-1 rounded-full hover:bg-gray-100 transition-colors opacity-0 group-hover:opacity-100"
                                            title="More options"
                                          >
                                            <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                                              <circle cx="12" cy="5" r="1.5" />
                                              <circle cx="12" cy="12" r="1.5" />
                                              <circle cx="12" cy="19" r="1.5" />
                                            </svg>
                                          </button>
                                          {courseMenuOpenId === `unassigned-${course.id}` && (
                                            <>
                                              <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setCourseMenuOpenId(null); }} />
                                              <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-20">
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCourseMenuOpenId(null);
                                                    setEditingCourse(course);
                                                    setShowEditCourseModal(true);
                                                  }}
                                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-2 transition-colors"
                                                >
                                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                  </svg>
                                                  Edit
                                                </button>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCourseMenuOpenId(null);
                                                    setCourseToAssign(course);
                                                    setShowAssignCourseModal(true);
                                                  }}
                                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 flex items-center gap-2 transition-colors"
                                                >
                                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                                  </svg>
                                                  Assign to Students
                                                </button>
                                                <div className="border-t border-gray-100 my-1" />
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCourseMenuOpenId(null);
                                                    if (window.confirm(`Delete course "${course.name}"? This will also delete all topics and nuggets.`)) {
                                                      handleDeleteCourse(course.id);
                                                    }
                                                  }}
                                                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                                                >
                                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                  </svg>
                                                  Delete
                                                </button>
                                              </div>
                                            </>
                                          )}
                                        </div>
                                        <div onClick={() => {
                                          console.log('🔍 Clicking course card (unassigned):', course.name);
                                          // Batch all state updates together
                                          setMainTab(() => {
                                            console.log('🔍 Setting mainTab to: courses');
                                            return 'courses';
                                          });
                                          setActiveTab(() => {
                                            console.log('🔍 Setting activeTab to: courses');
                                            return 'courses';
                                          });
                                          setSelectedCourse(() => course);
                                        }}>
                                        <div className="flex flex-col items-center text-center">
                                          {(() => { const s = getSubjectStyle(course.subject); return (
                                          <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${s.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:${s.shadow} transition-all duration-300 mb-3 ring-4 ring-white`}>
                                            <span className="text-3xl">{s.icon}</span>
                                          </div>
                                          ); })()}
                                          <h3 className="text-sm font-bold text-gray-900 mb-1 group-hover:text-purple-600 transition-colors line-clamp-2">
                                            {course.name}
                                          </h3>
                                          {course.subject && (
                                            <span className="text-[10px] text-gray-500 font-medium mb-1">
                                              {course.subject}
                                            </span>
                                          )}
                                          <p className="text-[11px] text-gray-500 line-clamp-2 mb-2">
                                            {course.description || 'No description provided'}
                                          </p>
                                          <span className="text-[10px] text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full font-medium">
                                            {course.topics?.length || 0} topics
                                          </span>
                                        </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}

                {/* Course Analytics Tab */}
                {courseSubTab === 'analytics' && !selectedCourse && (
                  <TeacherNuggetAnalytics
                    courseId={null}
                    courseName="All Courses"
                    teacherEmail={teacherEmail}
                    onBack={() => {}}
                    isEmbedded={true}
                    courses={courses}
                  />
                )}

                {/* Tests Tab */}
                {courseSubTab === 'tests' && !selectedCourse && (
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-orange-200/50">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                          <span>📝</span> Course Tests
                        </h2>
                        <p className="text-gray-600 text-sm mt-1">Create and manage tests for your courses</p>
                      </div>
                      <button
                        className="px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-xl hover:from-orange-700 hover:to-red-700 transition-all duration-300 shadow-lg font-semibold flex items-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Create Test
                      </button>
                    </div>
                    <div className="text-center py-16">
                      <div className="w-32 h-32 rounded-full bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center mx-auto mb-6">
                        <span className="text-6xl">📝</span>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-800 mb-3">No Tests Yet</h3>
                      <p className="text-gray-600 mb-6 max-w-md mx-auto">
                        Start creating tests to assess your students' understanding of course material.
                      </p>
                    </div>
                  </div>
                )}

                {selectedCourse && !selectedTopic && (
                  /* Topics List View */
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-purple-200/50">
                    <div className="flex items-center gap-4 mb-6">
                      <button
                        onClick={() => setSelectedCourse(null)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <div className="flex-1">
                        <h2 className="text-2xl font-bold text-gray-900">{selectedCourse.name}</h2>
                        <p className="text-gray-600 text-sm mt-1">{selectedCourse.description}</p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAddTopicModal(true);
                        }}
                        className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 shadow-lg font-semibold flex items-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Topic
                      </button>
                    </div>

                    {(!selectedCourse.topics || selectedCourse.topics.length === 0) ? (
                      <div className="text-center py-16">
                        <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <span className="text-4xl">📝</span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">No Topics Yet</h3>
                        <p className="text-gray-600">Add topics to organize your course content</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {selectedCourse.topics.map((topic, index) => (
                          <div
                            key={index}
                            className="bg-gradient-to-br from-white to-blue-50 rounded-lg p-4 border-2 border-blue-200 hover:border-blue-400 hover:shadow-lg transition-all duration-300 cursor-pointer group relative"
                          >
                            <div className="absolute top-2 right-2 flex gap-1 z-10">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingTopic(topic);
                                  setShowEditTopicModal(true);
                                }}
                                className="p-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                                title="Edit"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Delete topic "${topic.name}"? This will also delete all nuggets.`)) {
                                    handleDeleteTopic(topic.id);
                                  }
                                }}
                                className="p-1.5 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                                title="Delete"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                            <div onClick={() => setSelectedTopic(topic)}>
                            <div className="flex items-start justify-between mb-3">
                              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-md">
                                <span className="text-xl">📑</span>
                              </div>
                              <span className="text-[10px] text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-full">
                                {topic.nuggets?.length || 0}
                              </span>
                            </div>
                            <h3 className="text-sm font-bold text-gray-900 mb-1.5 group-hover:text-blue-600 transition-colors line-clamp-1">
                              {topic.name}
                            </h3>
                            <p className="text-xs text-gray-600 line-clamp-2">
                              {topic.description || 'No description provided'}
                            </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {selectedCourse && selectedTopic && !selectedNugget && !nuggetDashboardView && (
                  /* Nuggets List View */
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-purple-200/50">
                    <div className="flex items-center gap-4 mb-6">
                      <button
                        onClick={() => setSelectedTopic(null)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <div className="flex-1">
                        <h2 className="text-2xl font-bold text-gray-900">{selectedTopic.name}</h2>
                        <p className="text-gray-600 text-sm mt-1">{selectedTopic.description}</p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAddNuggetModal(true);
                        }}
                        className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg font-semibold flex items-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Nugget
                      </button>
                    </div>

                    {(!selectedTopic.nuggets || selectedTopic.nuggets.length === 0) ? (
                      <div className="text-center py-16">
                        <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <span className="text-4xl">💎</span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">No Nuggets Yet</h3>
                        <p className="text-gray-600">Add nuggets to break down your topic into bite-sized content</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {selectedTopic.nuggets.map((nugget, index) => (
                          <div
                            key={index}
                            className="bg-gradient-to-br from-white to-green-50 rounded-xl p-5 border-2 border-green-200 hover:border-green-400 hover:shadow-lg transition-all duration-300 group relative"
                          >
                            {/* Delete button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm(`Are you sure you want to delete "${nugget.name}"?`)) {
                                  handleDeleteNugget(nugget.id);
                                }
                              }}
                              className="absolute top-2 right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-10"
                              title="Delete nugget"
                            >
                              ×
                            </button>
                            
                            <div
                              onClick={() => {
                                setSelectedNugget(nugget);
                                setNuggetDashboardView(true);
                              }}
                              className="cursor-pointer"
                            >
                              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-md mb-3">
                                <span className="text-xl">💎</span>
                              </div>
                              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                                {nugget.name}
                              </h3>
                              <p className="text-sm text-gray-600 line-clamp-2">
                                {nugget.description || 'Click to manage content'}
                              </p>
                              <div className="mt-3 flex gap-2 text-xs">
                                <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full">{nugget.videos?.length || 0} Videos</span>
                                <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full">{nugget.questions?.length || 0} Questions</span>
                                <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full">{nugget.slides?.length || 0} Slides</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {nuggetDashboardView && selectedNugget && (
                  /* Nugget Dashboard - PowerPoint Style */
                  <div className="h-full flex flex-col bg-gradient-to-br from-slate-50 to-slate-100">
                    {/* Top Toolbar */}
                    <div className="bg-white border-b border-gray-200 shadow-sm">
                      <div className="px-6 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <button
                            onClick={() => {
                              setNuggetDashboardView(false);
                              setSelectedNugget(null);
                              setNuggetSlides([]);
                              setCurrentSlideIndex(0);
                            }}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                          <div>
                            <h2 className="text-lg font-bold text-gray-900">{selectedNugget.name}</h2>
                            <p className="text-xs text-gray-500">PowerPoint-style presentation builder</p>
                          </div>
                        </div>
                        
                        {/* Add Element Buttons */}
                        <div className="flex items-center gap-2">
                          {/* Save Status Indicator */}
                          <div className="flex items-center gap-2 px-3 py-1 rounded-lg text-sm">
                            {saveStatus === 'saving' && (
                              <>
                                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                                <span className="text-blue-600 font-medium">Saving...</span>
                              </>
                            )}
                            {saveStatus === 'saved' && (
                              <>
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <span className="text-green-600 font-medium">✓ Saved</span>
                              </>
                            )}
                            {saveStatus === 'error' && (
                              <>
                                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                <span className="text-red-600 font-medium">⚠ Error saving</span>
                              </>
                            )}
                          </div>
                          
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newSlides = [...nuggetSlides, { id: Date.now(), elements: [] }];
                              setNuggetSlides(newSlides);
                              setCurrentSlideIndex(newSlides.length - 1); // Navigate to the new slide
                            }}
                            className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all font-medium shadow-sm flex items-center gap-2"
                          >
                            <span>➕</span> New Slide
                          </button>
                          {nuggetSlides.length > 0 && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setShowAddVideoModal(true);
                                }}
                                className="px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg hover:from-red-600 hover:to-orange-600 transition-all font-medium shadow-sm flex items-center gap-2"
                              >
                                <span>🎥</span> Video
                              </button>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setShowAddImageModal(true);
                                }}
                                className="px-4 py-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg hover:from-pink-600 hover:to-rose-600 transition-all font-medium shadow-sm flex items-center gap-2"
                              >
                                <span>🖼️</span> Image
                              </button>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setShowAddQuestionModal(true);
                                }}
                                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all font-medium shadow-sm flex items-center gap-2"
                              >
                                <span>❓</span> Question
                              </button>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setShowAddSlideModal(true);
                                }}
                                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg hover:from-purple-600 hover:to-indigo-600 transition-all font-medium shadow-sm flex items-center gap-2"
                              >
                                <span>📝</span> Text
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Main Content Area - PowerPoint Layout */}
                    <div className="flex-1 flex overflow-hidden">
                      {/* Left Pane - Slide Thumbnails */}
                      <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
                        <div className="p-4 border-b border-gray-200">
                          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                            <span>📑</span> Slides ({nuggetSlides.length})
                          </h3>
                        </div>
                        <div className="p-3 space-y-3">
                          {(() => {
                            console.log('🎨 [RENDER] Rendering slides section:', {
                              nuggetSlides_length: nuggetSlides.length,
                              nuggetSlides_is_array: Array.isArray(nuggetSlides),
                              will_show_empty: nuggetSlides.length === 0
                            });
                            return null;
                          })()}
                          {nuggetSlides.length === 0 ? (
                            <div className="text-center py-8">
                              <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                                <span className="text-3xl">📄</span>
                              </div>
                              <p className="text-sm text-gray-500 mb-3">No slides yet</p>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setNuggetSlides([{ id: Date.now(), elements: [] }]);
                                  setCurrentSlideIndex(0);
                                }}
                                className="text-xs px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all"
                              >
                                Create First Slide
                              </button>
                            </div>
                          ) : (
                            nuggetSlides.map((slide, index) => (
                              <div
                                key={slide.id}
                                draggable
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDrop(e, index)}
                                onClick={() => setCurrentSlideIndex(index)}
                                className={`cursor-move rounded-lg border-2 transition-all ${
                                  draggedSlideIndex === index
                                    ? 'opacity-50 border-purple-500 bg-purple-50'
                                    : currentSlideIndex === index
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300 bg-white'
                                }`}
                              >
                                <div className="p-2">
                                  <div className="aspect-video bg-white rounded border border-gray-200 mb-2 overflow-hidden relative">
                                    {slide.elements.length === 0 ? (
                                      <div className="h-full flex items-center justify-center text-gray-400">
                                        <span className="text-2xl">📄</span>
                                      </div>
                                    ) : (
                                      <div className="h-full bg-white p-0.5 overflow-hidden relative" style={{ transform: 'scale(0.25)', transformOrigin: 'top left', width: '400%', height: '400%' }}>
                                        <div className="space-y-4 h-full overflow-hidden">
                                          {slide.elements.map((element, i) => (
                                            <div key={i}>
                                              {element.type === 'video' && (
                                                <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg p-6 border-2 border-red-200">
                                                  <div className="flex items-center gap-3 mb-3">
                                                    <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
                                                      <span className="text-xl">🎥</span>
                                                    </div>
                                                    <h4 className="font-bold text-gray-900 text-lg">{element.title}</h4>
                                                  </div>
                                                  <div className="relative rounded-lg overflow-hidden bg-black" style={{ paddingBottom: '56.25%' }}>
                                                    <div className="absolute inset-0 flex items-center justify-center bg-black">
                                                      <div className="text-white text-6xl">▶️</div>
                                                    </div>
                                                  </div>
                                                </div>
                                              )}
                                              
                                              {element.type === 'image' && (
                                                <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-lg p-4 border-2 border-pink-200">
                                                  <div className="flex items-start gap-3">
                                                    <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-500 rounded-lg flex items-center justify-center flex-shrink-0">
                                                      <span className="text-xl">🖼️</span>
                                                    </div>
                                                    <div className="flex-1">
                                                      {element.url ? (
                                                        <img src={element.url} alt={element.title || 'Image'} className="w-full rounded-lg" />
                                                      ) : (
                                                        <div className="text-gray-600">{element.title}</div>
                                                      )}
                                                    </div>
                                                  </div>
                                                </div>
                                              )}
                                              
                                              {element.type === 'question' && (
                                                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-4 border-2 border-blue-200">
                                                  <div className="flex items-start gap-3">
                                                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
                                                      <span className="text-lg font-bold text-white">Q</span>
                                                    </div>
                                                    <div className="flex-1">
                                                      <h4 className="font-bold text-gray-900 mb-3">{element.text}</h4>
                                                      {element.options && element.options.length > 0 && (
                                                        <div className="space-y-2">
                                                          {element.options.map((option, optIdx) => (
                                                            <div key={optIdx} className="flex items-center gap-2 p-2 bg-white rounded border border-blue-200">
                                                              <div className="w-4 h-4 rounded-full border-2 border-blue-400 flex-shrink-0" />
                                                              <span className="text-gray-700">{option.text || option}</span>
                                                            </div>
                                                          ))}
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>
                                                </div>
                                              )}
                                              
                                              {element.type === 'text' && (
                                                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 border-2 border-purple-200">
                                                  <div className="flex items-start gap-3">
                                                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center flex-shrink-0">
                                                      <span className="text-xl">📝</span>
                                                    </div>
                                                    <div 
                                                      className="flex-1 text-gray-800"
                                                      style={{
                                                        fontWeight: element.bold ? 'bold' : 'normal',
                                                        fontStyle: element.italic ? 'italic' : 'normal',
                                                        textDecoration: element.underline ? 'underline' : 'none',
                                                        textAlign: element.align || 'left',
                                                        color: element.color || '#000000',
                                                        fontSize: element.fontSize ? `${element.fontSize}px` : '16px'
                                                      }}
                                                      dangerouslySetInnerHTML={{ __html: element.text || 'Text' }}
                                                    />
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium text-gray-600">Slide {index + 1}</span>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const newSlides = nuggetSlides.filter((_, i) => i !== index);
                                        setNuggetSlides(newSlides);
                                        if (currentSlideIndex >= newSlides.length) {
                                          setCurrentSlideIndex(Math.max(0, newSlides.length - 1));
                                        }
                                      }}
                                      className="text-xs text-red-500 hover:text-red-700 p-1"
                                    >
                                      🗑️
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      {/* Main Canvas Area */}
                      <div className="flex-1 flex items-center justify-center p-8">
                        <div className="w-full max-w-5xl">
                          {nuggetSlides.length === 0 ? (
                            /* Empty State */
                            <div className="bg-white rounded-xl shadow-2xl aspect-video p-12 relative border border-gray-200">
                              <div className="h-full flex flex-col items-center justify-center text-center">
                                <div className="w-24 h-24 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mb-6">
                                  <span className="text-5xl">✨</span>
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-3">Start Your Presentation</h3>
                                <p className="text-gray-600 max-w-md mb-6">
                                  Create slides and add videos, images, questions, or text to build your learning content
                                </p>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setNuggetSlides([{ id: Date.now(), elements: [] }]);
                                    setCurrentSlideIndex(0);
                                  }}
                                  className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all font-semibold shadow-lg text-lg"
                                >
                                  ➕ Create First Slide
                                </button>
                              </div>
                            </div>
                          ) : (
                            /* Current Slide Display with Navigation */
                            <div className="relative w-full">
                              {/* Navigation Arrows */}
                              <div className="flex items-center gap-4">
                                <button
                                  onClick={() => setCurrentSlideIndex(Math.max(0, currentSlideIndex - 1))}
                                  disabled={currentSlideIndex === 0}
                                  className="p-3 bg-white rounded-full shadow-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                >
                                  <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                  </svg>
                                </button>
                                
                                <div className="flex-1">
                                  <div className="bg-white rounded-xl shadow-2xl p-8 relative border border-gray-200 max-h-[600px] overflow-y-auto">
                                    <div className="absolute top-4 right-4 text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full z-10">
                                      {currentSlideIndex + 1} / {nuggetSlides.length}
                                    </div>
                              
                              {nuggetSlides[currentSlideIndex]?.elements.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-center">
                                  <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-full flex items-center justify-center mb-4">
                                    <span className="text-4xl">📄</span>
                                  </div>
                                  <h3 className="text-xl font-bold text-gray-900 mb-2">Blank Slide</h3>
                                  <p className="text-gray-600 text-sm max-w-md mb-4">
                                    Add content using the buttons above
                                  </p>
                                  <div className="flex gap-2 flex-wrap justify-center">
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowAddVideoModal(true);
                                      }}
                                      className="px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg hover:from-red-600 hover:to-orange-600 transition-all text-sm"
                                    >
                                      🎥 Video
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowAddImageModal(true);
                                      }}
                                      className="px-4 py-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg hover:from-pink-600 hover:to-rose-600 transition-all text-sm"
                                    >
                                      🖼️ Image
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowAddQuestionModal(true);
                                      }}
                                      className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all text-sm"
                                    >
                                      ❓ Question
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowAddSlideModal(true);
                                      }}
                                      className="px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg hover:from-purple-600 hover:to-indigo-600 transition-all text-sm"
                                    >
                                      📝 Text
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                /* Display Slide Elements */
                                <div className="space-y-4">
                                  {nuggetSlides[currentSlideIndex]?.elements.map((element, i) => (
                                    <div key={i} className="relative group">
                                      {element.type === 'video' && (
                                        <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg p-6 border-2 border-red-200">
                                          <div className="space-y-4">
                                            <div className="flex items-center gap-3">
                                              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <span className="text-xl">🎥</span>
                                              </div>
                                              <h4 className="font-bold text-gray-900 text-lg">{element.title}</h4>
                                            </div>
                                            <div className="relative rounded-lg overflow-hidden shadow-lg" style={{ paddingBottom: '56.25%' }}>
                                              {(() => {
                                                const url = element.url;
                                                
                                                // YouTube video
                                                if (url.includes('youtube.com') || url.includes('youtu.be')) {
                                                  let videoId = '';
                                                  if (url.includes('youtube.com/watch?v=')) {
                                                    videoId = url.split('v=')[1]?.split('&')[0];
                                                  } else if (url.includes('youtu.be/')) {
                                                    videoId = url.split('youtu.be/')[1]?.split('?')[0];
                                                  } else if (url.includes('youtube.com/embed/')) {
                                                    videoId = url.split('embed/')[1]?.split('?')[0];
                                                  }
                                                  
                                                  return (
                                                    <iframe
                                                      className="absolute top-0 left-0 w-full h-full"
                                                      src={`https://www.youtube.com/embed/${videoId}`}
                                                      title={element.title}
                                                      frameBorder="0"
                                                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                      allowFullScreen
                                                    />
                                                  );
                                                }
                                                
                                                // Vimeo video
                                                if (url.includes('vimeo.com')) {
                                                  const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
                                                  return (
                                                    <iframe
                                                      className="absolute top-0 left-0 w-full h-full"
                                                      src={`https://player.vimeo.com/video/${videoId}`}
                                                      title={element.title}
                                                      frameBorder="0"
                                                      allow="autoplay; fullscreen; picture-in-picture"
                                                      allowFullScreen
                                                    />
                                                  );
                                                }
                                                
                                                // Direct video URL (mp4, webm, etc.)
                                                if (url.match(/\.(mp4|webm|ogg)$/i)) {
                                                  return (
                                                    <video
                                                      className="absolute top-0 left-0 w-full h-full"
                                                      controls
                                                      src={url}
                                                    >
                                                      Your browser does not support the video tag.
                                                    </video>
                                                  );
                                                }
                                                
                                                // Fallback: show link if URL format not recognized
                                                return (
                                                  <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-gray-100">
                                                    <div className="text-center p-4">
                                                      <p className="text-gray-600 mb-2">Video format not supported for embed</p>
                                                      <a 
                                                        href={url} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer" 
                                                        className="text-blue-600 hover:underline break-all text-sm"
                                                      >
                                                        {url}
                                                      </a>
                                                    </div>
                                                  </div>
                                                );
                                              })()}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      
                                      {element.type === 'image' && (
                                        <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-lg p-4 border-2 border-pink-200">
                                          <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-500 rounded-lg flex items-center justify-center flex-shrink-0">
                                              <span className="text-xl">🖼️</span>
                                            </div>
                                            <div className="flex-1">
                                              {element.url ? (
                                                <img src={element.url} alt={element.title || 'Image'} className="w-full rounded-lg" />
                                              ) : (
                                                <p className="text-sm text-gray-600">{element.title}</p>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      
                                      {element.type === 'question' && (
                                        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-4 border-2 border-blue-200">
                                          <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
                                              <span className="text-lg font-bold text-white">Q</span>
                                            </div>
                                            <div className="flex-1">
                                              <div className="flex items-center gap-2 mb-3">
                                                <h4 className="font-bold text-gray-900">{element.text}</h4>
                                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">{element.questionType}</span>
                                              </div>
                                              
                                              {/* Question Image */}
                                              {element.questionImage && element.questionImage.trim() !== '' && (
                                                <div className="mb-3">
                                                  <img 
                                                    src={buildApiUrl(element.questionImage)} 
                                                    alt="Question" 
                                                    className="max-h-64 rounded-lg shadow-md"
                                                  />
                                                </div>
                                              )}
                                              
                                              {/* Answer Options */}
                                              {element.options && element.options.length > 0 && (
                                                <div className="mt-3 space-y-2">
                                                  {element.options.map((option, idx) => {
                                                    const questionKey = `${currentSlideIndex}-${i}`;
                                                    const isSelected = questionAnswers[questionKey] === option;
                                                    const isSubmitted = submittedAnswers[questionKey];
                                                    const isCorrect = option === element.correctAnswer;
                                                    
                                                    return (
                                                      <button
                                                        key={idx}
                                                        onClick={() => {
                                                          if (!isSubmitted) {
                                                            setQuestionAnswers({
                                                              ...questionAnswers,
                                                              [questionKey]: option
                                                            });
                                                          }
                                                        }}
                                                        disabled={isSubmitted}
                                                        className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                                                          isSubmitted
                                                            ? isCorrect
                                                              ? 'border-green-400 bg-green-50'
                                                              : isSelected
                                                              ? 'border-red-400 bg-red-50'
                                                              : 'border-gray-200 bg-gray-50'
                                                            : isSelected
                                                            ? 'border-blue-400 bg-blue-100'
                                                            : 'border-gray-300 hover:border-blue-300 hover:bg-blue-50'
                                                        } ${isSubmitted ? 'cursor-default' : 'cursor-pointer'}`}
                                                      >
                                                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                                                          isSubmitted
                                                            ? isCorrect
                                                              ? 'border-green-500 bg-green-500 text-white'
                                                              : isSelected
                                                              ? 'border-red-500 bg-red-500 text-white'
                                                              : 'border-gray-400 text-gray-600'
                                                            : isSelected
                                                            ? 'border-blue-500 bg-blue-500 text-white'
                                                            : 'border-blue-300 text-blue-600'
                                                        }`}>
                                                          {String.fromCharCode(65 + idx)}
                                                        </div>
                                                        <div className="flex-1">
                                                          {element.optionImages && Array.isArray(element.optionImages) && element.optionImages[idx] && element.optionImages[idx].trim() !== '' && (
                                                            <img 
                                                              src={buildApiUrl(element.optionImages[idx])} 
                                                              alt={`Option ${idx + 1}`} 
                                                              className="max-h-20 rounded-lg shadow-sm mb-2"
                                                            />
                                                          )}
                                                          <span className="text-gray-700">{option}</span>
                                                        </div>
                                                        {isSubmitted && isCorrect && (
                                                          <span className="text-green-600 font-bold">✓</span>
                                                        )}
                                                        {isSubmitted && !isCorrect && isSelected && (
                                                          <span className="text-red-600 font-bold">✗</span>
                                                        )}
                                                      </button>
                                                    );
                                                  })}
                                                </div>
                                              )}
                                              
                                              {/* Submit Answer Button and Result */}
                                              {(() => {
                                                const questionKey = `${currentSlideIndex}-${i}`;
                                                const userAnswer = questionAnswers[questionKey];
                                                const isSubmitted = submittedAnswers[questionKey];
                                                const isCorrect = userAnswer === element.correctAnswer;
                                                
                                                return (
                                                  <div className="mt-4">
                                                    {!isSubmitted ? (
                                                      <button
                                                        onClick={() => {
                                                          if (userAnswer) {
                                                            setSubmittedAnswers({
                                                              ...submittedAnswers,
                                                              [questionKey]: true
                                                            });
                                                          }
                                                        }}
                                                        disabled={!userAnswer}
                                                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                                                      >
                                                        Submit Answer
                                                      </button>
                                                    ) : (
                                                      <div className={`p-3 rounded-lg border-2 ${
                                                        isCorrect
                                                          ? 'bg-green-50 border-green-400'
                                                          : 'bg-red-50 border-red-400'
                                                      }`}>
                                                        <div className="flex items-center gap-2">
                                                          <span className="text-2xl">{isCorrect ? '✅' : '❌'}</span>
                                                          <div className="flex-1">
                                                            <div className={`font-bold ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                                                              {isCorrect ? 'Correct!' : 'Incorrect'}
                                                            </div>
                                                            {!isCorrect && (
                                                              <div className="text-sm text-gray-600 mt-1">
                                                                Correct answer: <span className="font-semibold text-green-700">{element.correctAnswer}</span>
                                                              </div>
                                                            )}
                                                          </div>
                                                        </div>
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              })()}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      
                                      {element.type === 'text' && (
                                        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 border-2 border-purple-200">
                                          <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center flex-shrink-0">
                                              <span className="text-xl">📝</span>
                                            </div>
                                            <div className="flex-1">
                                              <h4 className="font-bold text-gray-900 mb-2">{element.title}</h4>
                                              <div className="text-gray-700 whitespace-pre-wrap text-sm">
                                                {element.content}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Control Buttons */}
                                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                        {/* Move Up */}
                                        {i > 0 && (
                                          <button
                                            onClick={() => {
                                              const newSlides = [...nuggetSlides];
                                              const elements = newSlides[currentSlideIndex].elements;
                                              [elements[i], elements[i - 1]] = [elements[i - 1], elements[i]];
                                              setNuggetSlides(newSlides);
                                            }}
                                            className="bg-blue-500 text-white p-1.5 rounded-lg hover:bg-blue-600 text-xs"
                                            title="Move Up"
                                          >
                                            ⬆️
                                          </button>
                                        )}
                                        
                                        {/* Move Down */}
                                        {i < nuggetSlides[currentSlideIndex].elements.length - 1 && (
                                          <button
                                            onClick={() => {
                                              const newSlides = [...nuggetSlides];
                                              const elements = newSlides[currentSlideIndex].elements;
                                              [elements[i], elements[i + 1]] = [elements[i + 1], elements[i]];
                                              setNuggetSlides(newSlides);
                                            }}
                                            className="bg-blue-500 text-white p-1.5 rounded-lg hover:bg-blue-600 text-xs"
                                            title="Move Down"
                                          >
                                            ⬇️
                                          </button>
                                        )}
                                        
                                        {/* Delete */}
                                        <button
                                          onClick={() => {
                                            const newSlides = [...nuggetSlides];
                                            newSlides[currentSlideIndex].elements = newSlides[currentSlideIndex].elements.filter((_, idx) => idx !== i);
                                            setNuggetSlides(newSlides);
                                          }}
                                          className="bg-red-500 text-white p-1.5 rounded-lg hover:bg-red-600 text-xs"
                                          title="Delete"
                                        >
                                          🗑️
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                                  </div>
                                </div>
                                
                                <button
                                  onClick={() => setCurrentSlideIndex(Math.min(nuggetSlides.length - 1, currentSlideIndex + 1))}
                                  disabled={currentSlideIndex === nuggetSlides.length - 1}
                                  className="p-3 bg-white rounded-full shadow-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                >
                                  <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tests Main Tab */}
            {mainTab === 'tests' && activeTab === 'tests' && (
              <div className="space-y-6">
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-8 border border-orange-200/50">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                        <span className="text-5xl">📝</span> Tests
                      </h2>
                      <p className="text-gray-600 text-lg mt-2">Create and manage tests to assess student understanding</p>
                    </div>
                    <button
                      className="px-8 py-4 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-xl hover:from-orange-700 hover:to-red-700 transition-all duration-300 shadow-lg hover:shadow-xl font-semibold flex items-center gap-2 text-lg"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Create Test
                    </button>
                  </div>

                  {/* Empty State */}
                  <div className="text-center py-16">
                    <div className="w-40 h-40 rounded-full bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center mx-auto mb-8">
                      <span className="text-7xl">📝</span>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">No Tests Yet</h3>
                    <p className="text-gray-600 text-lg mb-8 max-w-md mx-auto">
                      Start creating tests to evaluate student knowledge and track their progress
                    </p>
                    <button
                      className="px-8 py-4 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl hover:from-orange-600 hover:to-red-700 transition-all duration-300 shadow-lg hover:shadow-xl font-semibold flex items-center gap-2 mx-auto text-lg"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Create Your First Test
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Writing, Listening, Reading under Assignments */}
            {mainTab === 'assignments' && (activeTab === 'writing' || activeTab === 'listening' || activeTab === 'reading') && (
              <div className="space-y-6">
                {/* Writing Tab */}
                {activeTab === 'writing' && (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center p-8">
                      <div className="text-8xl mb-6">✍️</div>
                      <h2 className="text-3xl font-bold text-gray-800 mb-3">Writing Tests</h2>
                      <p className="text-lg text-gray-600">Coming soon...</p>
                      <p className="text-sm text-gray-500 mt-2">Create and manage writing assessment tests</p>
                    </div>
                  </div>
                )}

                {/* Listening Tab */}
                {activeTab === 'listening' && (
                  <div className="space-y-6">
                    {/* Header with Add Button */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-3xl font-bold text-gray-800 flex items-center">
                          <span className="text-4xl mr-3">🎧</span>
                          Listening Assignments
                        </h2>
                        <p className="text-gray-600 mt-1">Create and manage listening comprehension tests</p>
                      </div>
                      <button
                        onClick={() => {
                          setShowListeningModal(true);
                          setCurrentListeningAssignment(null);
                          setListeningTitle('');
                          setListeningDescription('');
                          setListeningMediaType('audio');
                          setListeningMediaFile(null);
                          setListeningVideoLink('');
                          setListeningQuestions([]);
                          setRecordedAudio(null);
                          setSelectedYears([]);
                        }}
                        className="px-6 py-3 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-xl hover:from-pink-600 hover:to-rose-700 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center space-x-2 font-semibold"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        <span>New Listening Assignment</span>
                      </button>
                    </div>

                    {/* Assignments List */}
                    {listeningAssignments.length === 0 ? (
                      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-16 text-center border border-pink-200/50">
                        <div className="w-32 h-32 bg-gradient-to-br from-pink-100 to-rose-100 rounded-full flex items-center justify-center mx-auto mb-6">
                          <span className="text-6xl">🎧</span>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">No Listening Assignments Yet</h3>
                        <p className="text-gray-600 mb-8 text-lg">Create your first listening comprehension assignment</p>
                        <button
                          onClick={() => setShowListeningModal(true)}
                          className="px-8 py-4 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-xl hover:from-pink-600 hover:to-rose-700 transition-all duration-300 shadow-lg hover:shadow-xl font-semibold text-lg"
                        >
                          🎤 Create Assignment
                        </button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {listeningAssignments.map((assignment, index) => (
                          <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow border border-pink-100">
                            <div className="flex items-start justify-between mb-4">
                              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center">
                                <span className="text-2xl">🎧</span>
                              </div>
                              <div className="relative">
                                <button 
                                  onClick={() => setOpenOptionsMenu(openOptionsMenu === assignment.id ? null : assignment.id)}
                                  className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                                  </svg>
                                </button>
                                {openOptionsMenu === assignment.id && (
                                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-10">
                                    <button
                                      onClick={() => handleEditListeningAssignment(assignment)}
                                      className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 text-gray-700 transition-colors first:rounded-t-lg"
                                    >
                                      <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                      <span className="font-medium">Edit</span>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteListeningAssignment(assignment.id)}
                                      className="w-full text-left px-4 py-3 hover:bg-red-50 flex items-center gap-3 text-red-600 transition-colors last:rounded-b-lg border-t border-gray-100"
                                    >
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                      <span className="font-medium">Delete</span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                            <h3 className="text-lg font-bold text-gray-800 mb-2">{assignment.title}</h3>
                            <p className="text-sm text-gray-600 mb-4">{assignment.description}</p>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-pink-600 font-medium">{assignment.questions?.length || 0} Questions</span>
                                <span className="text-gray-500">{assignment.mediaType}</span>
                              </div>
                              {assignment.assignedTo && assignment.assignedTo.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {assignment.assignedTo.map(year => (
                                    <span key={year} className="px-2 py-1 bg-pink-100 text-pink-700 rounded text-xs font-medium">
                                      {year}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Create/Edit Modal */}
                    {showListeningModal && (
                      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                          {/* Modal Header */}
                          <div className="sticky top-0 bg-gradient-to-r from-pink-500 to-rose-600 text-white p-6 rounded-t-2xl">
                            <div className="flex items-center justify-between">
                              <h2 className="text-2xl font-bold flex items-center">
                                <span className="text-3xl mr-3">🎧</span>
                                {currentListeningAssignment ? 'Edit' : 'Create'} Listening Assignment
                              </h2>
                              <button
                                onClick={() => {
                                  setShowListeningModal(false);
                                  setCurrentListeningAssignment(null);
                                  setListeningTitle('');
                                  setListeningDescription('');
                                  setListeningMediaType('audio');
                                  setListeningVideoLink('');
                                  setListeningQuestions([]);
                                  setSelectedYears([]);
                                  setListeningMediaFile(null);
                                  setRecordedAudio(null);
                                }}
                                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </div>

                          {/* Modal Content */}
                          <div className="p-6 space-y-6">
                            {/* Title */}
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">Assignment Title</label>
                              <input
                                type="text"
                                value={listeningTitle}
                                onChange={(e) => setListeningTitle(e.target.value)}
                                placeholder="e.g., English Conversation - Unit 5"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                              />
                            </div>

                            {/* Description */}
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                              <textarea
                                value={listeningDescription}
                                onChange={(e) => setListeningDescription(e.target.value)}
                                placeholder="Brief description of the listening assignment"
                                rows="3"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                              />
                            </div>

                            {/* Assign to Years */}
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-3">Assign to Years</label>
                              <div className="grid grid-cols-4 gap-2">
                                {['Pre-School', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6'].map(year => (
                                  <button
                                    key={year}
                                    type="button"
                                    onClick={() => {
                                      if (selectedYears.includes(year)) {
                                        setSelectedYears(selectedYears.filter(y => y !== year));
                                      } else {
                                        setSelectedYears([...selectedYears, year]);
                                      }
                                    }}
                                    className={`py-2 px-4 rounded-lg font-medium transition-all ${
                                      selectedYears.includes(year)
                                        ? 'bg-pink-500 text-white shadow-lg'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                  >
                                    {year}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Media Type Selection */}
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-3">Media Type</label>
                              <div className="grid grid-cols-4 gap-3">
                                <button
                                  onClick={() => setListeningMediaType('audio')}
                                  className={`p-4 rounded-xl border-2 transition-all ${
                                    listeningMediaType === 'audio'
                                      ? 'border-pink-500 bg-pink-50'
                                      : 'border-gray-200 hover:border-pink-300'
                                  }`}
                                >
                                  <div className="text-3xl mb-2">🎵</div>
                                  <div className="text-sm font-medium">Audio File</div>
                                </button>
                                <button
                                  onClick={() => setListeningMediaType('record')}
                                  className={`p-4 rounded-xl border-2 transition-all ${
                                    listeningMediaType === 'record'
                                      ? 'border-pink-500 bg-pink-50'
                                      : 'border-gray-200 hover:border-pink-300'
                                  }`}
                                >
                                  <div className="text-3xl mb-2">🎤</div>
                                  <div className="text-sm font-medium">Record</div>
                                </button>
                                <button
                                  onClick={() => setListeningMediaType('video')}
                                  className={`p-4 rounded-xl border-2 transition-all ${
                                    listeningMediaType === 'video'
                                      ? 'border-pink-500 bg-pink-50'
                                      : 'border-gray-200 hover:border-pink-300'
                                  }`}
                                >
                                  <div className="text-3xl mb-2">📹</div>
                                  <div className="text-sm font-medium">Video Link</div>
                                </button>
                                <button
                                  onClick={() => setListeningMediaType('upload-video')}
                                  className={`p-4 rounded-xl border-2 transition-all ${
                                    listeningMediaType === 'upload-video'
                                      ? 'border-pink-500 bg-pink-50'
                                      : 'border-gray-200 hover:border-pink-300'
                                  }`}
                                >
                                  <div className="text-3xl mb-2">📼</div>
                                  <div className="text-sm font-medium">Upload Video</div>
                                </button>
                              </div>
                            </div>

                            {/* Media Upload/Input */}
                            <div className="bg-gray-50 rounded-xl p-6">
                              {listeningMediaType === 'audio' && (
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-3">Upload Audio File</label>
                                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-pink-400 transition-colors">
                                    <input
                                      type="file"
                                      accept="audio/*"
                                      onChange={(e) => setListeningMediaFile(e.target.files[0])}
                                      className="hidden"
                                      id="audio-upload"
                                    />
                                    <label htmlFor="audio-upload" className="cursor-pointer">
                                      <div className="text-5xl mb-3">🎵</div>
                                      <p className="text-gray-600 font-medium">Click to upload audio file</p>
                                      <p className="text-sm text-gray-500 mt-1">MP3, WAV, or other audio formats</p>
                                      {listeningMediaFile && (
                                        <p className="text-pink-600 font-medium mt-3">✓ {listeningMediaFile.name}</p>
                                      )}
                                    </label>
                                  </div>
                                </div>
                              )}

                              {listeningMediaType === 'record' && (
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-3">Record Audio</label>
                                  <div className="text-center">
                                    <div className="text-6xl mb-4">🎤</div>
                                    {!isRecording && !recordedAudio && (
                                      <button
                                        onClick={async () => {
                                          try {
                                            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                                            const recorder = new MediaRecorder(stream);
                                            const chunks = [];
                                            
                                            recorder.ondataavailable = (e) => chunks.push(e.data);
                                            recorder.onstop = () => {
                                              const blob = new Blob(chunks, { type: 'audio/webm' });
                                              setRecordedAudio(blob);
                                              stream.getTracks().forEach(track => track.stop());
                                            };
                                            
                                            recorder.start();
                                            setMediaRecorder(recorder);
                                            setIsRecording(true);
                                          } catch (err) {
                                            alert('Error accessing microphone: ' + err.message);
                                          }
                                        }}
                                        className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
                                      >
                                        🔴 Start Recording
                                      </button>
                                    )}
                                    {isRecording && (
                                      <button
                                        onClick={() => {
                                          if (mediaRecorder) {
                                            mediaRecorder.stop();
                                            setIsRecording(false);
                                          }
                                        }}
                                        className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium animate-pulse"
                                      >
                                        ⏹️ Stop Recording
                                      </button>
                                    )}
                                    {recordedAudio && (
                                      <div className="mt-4">
                                        <p className="text-green-600 font-medium mb-3">✓ Recording completed</p>
                                        <audio controls src={URL.createObjectURL(recordedAudio)} className="mx-auto" />
                                        <button
                                          onClick={() => setRecordedAudio(null)}
                                          className="mt-3 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                                        >
                                          Record Again
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {listeningMediaType === 'video' && (
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-3">Video Link (YouTube, Vimeo, etc.)</label>
                                  <input
                                    type="url"
                                    value={listeningVideoLink}
                                    onChange={(e) => setListeningVideoLink(e.target.value)}
                                    placeholder="https://www.youtube.com/watch?v=..."
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                  />
                                </div>
                              )}

                              {listeningMediaType === 'upload-video' && (
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-3">Upload Video File</label>
                                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-pink-400 transition-colors">
                                    <input
                                      type="file"
                                      accept="video/*"
                                      onChange={(e) => setListeningMediaFile(e.target.files[0])}
                                      className="hidden"
                                      id="video-upload"
                                    />
                                    <label htmlFor="video-upload" className="cursor-pointer">
                                      <div className="text-5xl mb-3">📹</div>
                                      <p className="text-gray-600 font-medium">Click to upload video file</p>
                                      <p className="text-sm text-gray-500 mt-1">MP4, MOV, or other video formats</p>
                                      {listeningMediaFile && (
                                        <p className="text-pink-600 font-medium mt-3">✓ {listeningMediaFile.name}</p>
                                      )}
                                    </label>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Questions Section */}
                            <div>
                              <div className="flex items-center justify-between mb-4">
                                <label className="text-sm font-semibold text-gray-700">Questions</label>
                                <button
                                  onClick={() => {
                                    setListeningQuestions([...listeningQuestions, {
                                      question: '',
                                      type: 'multiple-choice',
                                      options: ['', '', '', ''],
                                      correctAnswer: 0
                                    }]);
                                  }}
                                  className="px-4 py-2 bg-pink-100 text-pink-700 rounded-lg hover:bg-pink-200 transition-colors text-sm font-medium"
                                >
                                  + Add Question
                                </button>
                              </div>

                              {listeningQuestions.length === 0 ? (
                                <div className="bg-gray-100 rounded-lg p-8 text-center">
                                  <p className="text-gray-600">No questions added yet. Click "Add Question" to create comprehension questions.</p>
                                </div>
                              ) : (
                                <div className="space-y-4">
                                  {listeningQuestions.map((q, qIndex) => (
                                    <div key={qIndex} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                      <div className="flex items-start justify-between mb-3">
                                        <span className="text-sm font-bold text-gray-700">Question {qIndex + 1}</span>
                                        <button
                                          onClick={() => {
                                            setListeningQuestions(listeningQuestions.filter((_, i) => i !== qIndex));
                                          }}
                                          className="text-red-500 hover:text-red-700"
                                        >
                                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                          </svg>
                                        </button>
                                      </div>
                                      <input
                                        type="text"
                                        value={q.question}
                                        onChange={(e) => {
                                          const updated = [...listeningQuestions];
                                          updated[qIndex].question = e.target.value;
                                          setListeningQuestions(updated);
                                        }}
                                        placeholder="Enter your question"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-3 text-sm"
                                      />
                                      <div className="space-y-2">
                                        {q.options.map((opt, optIndex) => (
                                          <div key={optIndex} className="flex items-center space-x-2">
                                            <input
                                              type="radio"
                                              name={`correct-${qIndex}`}
                                              checked={q.correctAnswer === optIndex}
                                              onChange={() => {
                                                const updated = [...listeningQuestions];
                                                updated[qIndex].correctAnswer = optIndex;
                                                setListeningQuestions(updated);
                                              }}
                                              className="text-pink-500"
                                            />
                                            <input
                                              type="text"
                                              value={opt}
                                              onChange={(e) => {
                                                const updated = [...listeningQuestions];
                                                updated[qIndex].options[optIndex] = e.target.value;
                                                setListeningQuestions(updated);
                                              }}
                                              placeholder={`Option ${optIndex + 1}`}
                                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                            />
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-end space-x-3 pt-4 border-t">
                              <button
                                onClick={() => setShowListeningModal(false)}
                                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={saveListeningAssignment}
                                disabled={!listeningTitle || (!listeningMediaFile && !listeningVideoLink && !recordedAudio)}
                                className="px-6 py-3 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-lg hover:from-pink-600 hover:to-rose-700 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                              >
                                Create Assignment
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Reading Tab */}
                {activeTab === 'reading' && (
                  <div className="space-y-6">
                    {/* Header with Add Button */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-3xl font-bold text-gray-800 flex items-center">
                          <span className="text-4xl mr-3">📖</span>
                          My Reading Assignments
                        </h2>
                        <p className="text-gray-600 mt-1">Manage your reading assignments and review student recordings</p>
                      </div>
                      <button
                        onClick={() => {
                          setShowReadingModal(true);
                          setCurrentReadingAssignment(null);
                          setReadingTitle('');
                          setReadingPassage('');
                          setReadingSelectedYears([]);
                        }}
                        className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-600 text-white rounded-xl hover:from-yellow-600 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center space-x-2 font-semibold"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        <span>New Reading Assignment</span>
                      </button>
                    </div>

                    {/* Assignments Table */}
                    {readingAssignments.length === 0 ? (
                      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-16 text-center border border-yellow-200/50">
                        <div className="w-32 h-32 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                          <span className="text-6xl">📖</span>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">No Reading Assignments Yet</h3>
                        <p className="text-gray-600 mb-8 text-lg">Create your first reading passage for students</p>
                        <button
                          onClick={() => setShowReadingModal(true)}
                          className="px-8 py-4 bg-gradient-to-r from-yellow-500 to-orange-600 text-white rounded-xl hover:from-yellow-600 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl font-semibold text-lg"
                        >
                          📝 Create Reading Assignment
                        </button>
                      </div>
                    ) : (
                      <div className="bg-white rounded-2xl shadow-lg border border-yellow-100 overflow-hidden">
                        {/* Table Header */}
                        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 px-6 py-4 border-b border-yellow-100">
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold text-gray-800">📋 All Assignments ({readingAssignments.length})</h3>
                          </div>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">#</th>
                                <th className="text-left px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Title</th>
                                <th className="text-left px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Assigned Classes</th>
                                <th className="text-left px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Words</th>
                                <th className="text-left px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Created</th>
                                <th className="text-center px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {readingAssignments.map((assignment, index) => (
                                <tr key={assignment.id || index} className="hover:bg-yellow-50/50 transition-colors">
                                  <td className="px-6 py-4">
                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-white text-sm font-bold">
                                      {index + 1}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div>
                                      <p className="font-semibold text-gray-800">{assignment.title}</p>
                                      <p className="text-xs text-gray-500 mt-1 line-clamp-1 max-w-xs">{assignment.passage}</p>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    {assignment.assignedTo && assignment.assignedTo.length > 0 ? (
                                      <div className="flex flex-wrap gap-1 max-w-xs">
                                        {assignment.assignedTo.map(year => (
                                          <span key={year} className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                                            {year}
                                          </span>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-gray-400 text-sm italic">Not assigned</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="text-sm text-gray-600 font-medium">
                                      {assignment.passage ? assignment.passage.split(/\s+/).filter(w => w).length : 0}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="text-sm text-gray-500">
                                      {assignment.createdAt ? new Date(assignment.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center justify-center space-x-2">
                                      {/* View Recordings Button */}
                                      <button
                                        onClick={() => loadAssignmentRecordings(assignment)}
                                        className="px-3 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all text-xs font-medium flex items-center gap-1.5 shadow-sm"
                                        title="View student recordings"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                        </svg>
                                        Recordings
                                      </button>
                                      {/* Edit Button */}
                                      <button
                                        onClick={() => handleEditReadingAssignment(assignment)}
                                        className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all text-xs font-medium flex items-center gap-1.5"
                                        title="Edit assignment"
                                      >
                                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        Edit
                                      </button>
                                      {/* Delete Button */}
                                      <button
                                        onClick={() => handleDeleteReadingAssignment(assignment.id)}
                                        className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all text-xs font-medium flex items-center gap-1.5"
                                        title="Delete assignment"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        Delete
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Student Recordings Review Modal */}
                    {showRecordingsView && selectedRecordingAssignment && (
                      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                          {/* Modal Header */}
                          <div className="bg-gradient-to-r from-blue-500 to-blue-700 text-white p-6 rounded-t-2xl flex-shrink-0">
                            <div className="flex items-center justify-between">
                              <div>
                                <h2 className="text-2xl font-bold flex items-center">
                                  <span className="text-3xl mr-3">🎧</span>
                                  Student Recordings
                                </h2>
                                <p className="text-blue-100 mt-1 text-sm">
                                  {selectedRecordingAssignment.title}
                                </p>
                              </div>
                              <button
                                onClick={closeRecordingsView}
                                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                            {/* Summary Stats */}
                            {!recordingsLoading && (
                              <div className="flex items-center gap-4 mt-4 flex-wrap">
                                <div className="bg-white/20 rounded-lg px-4 py-2">
                                  <span className="text-xs text-blue-100">Recordings</span>
                                  <p className="text-xl font-bold">{assignmentRecordings.length}</p>
                                </div>
                                <div className="bg-white/20 rounded-lg px-4 py-2">
                                  <span className="text-xs text-blue-100">Total Students</span>
                                  <p className="text-xl font-bold">
                                    {Object.values(assignmentStudentsTree).reduce((sum, forms) => sum + Object.values(forms).reduce((s, students) => s + students.length, 0), 0)}
                                  </p>
                                </div>
                                <div className="bg-white/20 rounded-lg px-4 py-2">
                                  <span className="text-xs text-blue-100">Submitted</span>
                                  <p className="text-xl font-bold text-green-200">{assignmentRecordings.length}</p>
                                </div>
                                <div className="bg-white/20 rounded-lg px-4 py-2">
                                  <span className="text-xs text-blue-100">Avg Accuracy</span>
                                  <p className="text-xl font-bold">
                                    {assignmentAccuracy.length > 0
                                      ? Math.round(assignmentAccuracy.reduce((sum, a) => sum + (a.accuracyPercentage || 0), 0) / assignmentAccuracy.length) + '%'
                                      : '—'}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Modal Content - Expandable Tree */}
                          <div className="overflow-y-auto flex-1 p-6">
                            {recordingsLoading ? (
                              <div className="flex items-center justify-center py-16">
                                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                                <span className="ml-4 text-gray-600 text-lg">Loading...</span>
                              </div>
                            ) : Object.keys(assignmentStudentsTree).length === 0 ? (
                              <div className="text-center py-16">
                                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                  <span className="text-4xl">📭</span>
                                </div>
                                <h3 className="text-xl font-bold text-gray-700 mb-2">No Students Found</h3>
                                <p className="text-gray-500">No students found for the assigned year groups.</p>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {/* Build lookup maps for recordings and accuracy by student ID */}
                                {(() => {
                                  const recMap = {};
                                  assignmentRecordings.forEach(rec => {
                                    const key = rec.studentId || rec.studentEmail;
                                    if (!recMap[key]) recMap[key] = [];
                                    recMap[key].push(rec);
                                  });
                                  const accMap = {};
                                  assignmentAccuracy.forEach(acc => {
                                    accMap[acc.studentId] = acc;
                                  });

                                  const yearOrder = ['Pre-School', 'Reception', ...Array.from({length: 13}, (_, i) => `Year ${i+1}`)];
                                  const sortedYears = Object.keys(assignmentStudentsTree).sort((a, b) => yearOrder.indexOf(a) - yearOrder.indexOf(b));

                                  return sortedYears.map(year => {
                                    const forms = assignmentStudentsTree[year];
                                    const sortedForms = Object.keys(forms).sort();
                                    const yearStudentCount = sortedForms.reduce((sum, f) => sum + forms[f].length, 0);
                                    const yearSubmittedCount = sortedForms.reduce((sum, f) => sum + forms[f].filter(s => recMap[s.schoolId] || recMap[s.email]).length, 0);

                                    return (
                                      <div key={year} className="border border-gray-200 rounded-xl overflow-hidden">
                                        {/* Year Header - Expandable */}
                                        <button
                                          onClick={() => toggleYear(year)}
                                          className="w-full flex items-center justify-between px-5 py-4 bg-gradient-to-r from-purple-50 to-blue-50 hover:from-purple-100 hover:to-blue-100 transition-colors"
                                        >
                                          <div className="flex items-center gap-3">
                                            <svg className={`w-5 h-5 text-gray-500 transition-transform ${expandedYears[year] ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                            <span className="text-lg font-bold text-gray-800">📚 {year}</span>
                                            <span className="px-2.5 py-0.5 bg-purple-200 text-purple-800 rounded-full text-xs font-bold">{yearStudentCount} students</span>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <span className="px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">✅ {yearSubmittedCount} submitted</span>
                                            <span className="px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold">❌ {yearStudentCount - yearSubmittedCount} pending</span>
                                          </div>
                                        </button>

                                        {/* Expanded Year Content - Forms */}
                                        {expandedYears[year] && (
                                          <div className="bg-white">
                                            {sortedForms.map(formName => {
                                              const students = forms[formName];
                                              const formKey = `${year}|${formName}`;
                                              const formSubmittedCount = students.filter(s => recMap[s.schoolId] || recMap[s.email]).length;

                                              return (
                                                <div key={formKey} className="border-t border-gray-100">
                                                  {/* Form Header - Expandable */}
                                                  <button
                                                    onClick={() => toggleForm(formKey)}
                                                    className="w-full flex items-center justify-between px-8 py-3 hover:bg-gray-50 transition-colors"
                                                  >
                                                    <div className="flex items-center gap-3">
                                                      <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedForms[formKey] ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                      </svg>
                                                      <span className="font-semibold text-gray-700">🏫 {formName}</span>
                                                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">{students.length} students</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-xs">
                                                      <span className="text-green-600 font-medium">{formSubmittedCount} done</span>
                                                      <span className="text-gray-300">|</span>
                                                      <span className="text-red-500 font-medium">{students.length - formSubmittedCount} pending</span>
                                                    </div>
                                                  </button>

                                                  {/* Expanded Form Content - Students */}
                                                  {expandedForms[formKey] && (
                                                    <div className="bg-gray-50/50">
                                                      {students.map((student, sIdx) => {
                                                        const studentRecs = recMap[student.schoolId] || recMap[student.email] || [];
                                                        const studentAcc = accMap[student.schoolId] || accMap[student.email] || null;
                                                        const latestRec = studentRecs.length > 0 ? studentRecs[0] : null;
                                                        const hasSubmitted = latestRec !== null;

                                                        return (
                                                          <div
                                                            key={student.schoolId || sIdx}
                                                            className={`flex items-center justify-between px-10 py-3 border-t border-gray-100 ${hasSubmitted ? 'bg-green-50/30' : 'bg-white'} hover:bg-blue-50/30 transition-colors`}
                                                          >
                                                            {/* Student Name + Status */}
                                                            <div className="flex items-center gap-3 min-w-[200px]">
                                                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${hasSubmitted ? 'bg-gradient-to-br from-green-400 to-green-600' : 'bg-gradient-to-br from-gray-300 to-gray-400'}`}>
                                                                {(student.fullName || '?').charAt(0).toUpperCase()}
                                                              </div>
                                                              <span className="font-medium text-gray-800 text-sm">{student.fullName}</span>
                                                            </div>

                                                            {/* Status / Stats */}
                                                            {hasSubmitted ? (
                                                              <div className="flex items-center gap-4 flex-1 justify-end">
                                                                {/* Accuracy */}
                                                                {studentAcc ? (
                                                                  <>
                                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                                                                      studentAcc.accuracyPercentage >= 90 ? 'bg-green-100 text-green-700'
                                                                      : studentAcc.accuracyPercentage >= 70 ? 'bg-yellow-100 text-yellow-700'
                                                                      : 'bg-red-100 text-red-700'
                                                                    }`}>
                                                                      {Math.round(studentAcc.accuracyPercentage)}%
                                                                    </span>
                                                                    <span className="text-xs text-gray-500">{Math.round(studentAcc.wordsPerMinute)} WPM</span>
                                                                    <span className="text-xs text-gray-500">{studentAcc.wordsRead}/{studentAcc.totalWords} words</span>
                                                                    {studentAcc.mistakeCount > 0 && (
                                                                      <span className="px-1.5 py-0.5 bg-red-50 text-red-600 rounded text-xs font-medium">{studentAcc.mistakeCount} mistakes</span>
                                                                    )}
                                                                  </>
                                                                ) : (
                                                                  <span className="text-xs text-gray-400">No accuracy data</span>
                                                                )}
                                                                {/* Duration */}
                                                                <span className="text-xs text-gray-500">
                                                                  {formatDuration(latestRec.durationSeconds)}
                                                                </span>
                                                                {/* Play Button */}
                                                                <button
                                                                  onClick={() => toggleAudioPlay(latestRec.id, selectedRecordingAssignment.id, student.schoolId || student.email)}
                                                                  className={`p-2 rounded-full transition-all shadow-sm ${
                                                                    playingAudioId === latestRec.id
                                                                      ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse'
                                                                      : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700'
                                                                  }`}
                                                                  title={playingAudioId === latestRec.id ? 'Stop' : 'Play recording'}
                                                                >
                                                                  {playingAudioId === latestRec.id ? (
                                                                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                                                      <rect x="6" y="4" width="4" height="16" rx="1" />
                                                                      <rect x="14" y="4" width="4" height="16" rx="1" />
                                                                    </svg>
                                                                  ) : (
                                                                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                                                      <path d="M8 5v14l11-7z" />
                                                                    </svg>
                                                                  )}
                                                                </button>
                                                              </div>
                                                            ) : (
                                                              <div className="flex items-center gap-2">
                                                                <span className="px-3 py-1 bg-red-100 text-red-600 rounded-full text-xs font-bold flex items-center gap-1">
                                                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                  </svg>
                                                                  Not Submitted
                                                                </span>
                                                              </div>
                                                            )}
                                                          </div>
                                                        );
                                                      })}
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Create/Edit Modal */}
                    {showReadingModal && (
                      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                          {/* Modal Header */}
                          <div className="sticky top-0 bg-gradient-to-r from-yellow-500 to-orange-600 text-white p-6 rounded-t-2xl">
                            <div className="flex items-center justify-between">
                              <h2 className="text-2xl font-bold flex items-center">
                                <span className="text-3xl mr-3">📖</span>
                                {currentReadingAssignment ? 'Edit' : 'Create'} Reading Assignment
                              </h2>
                              <button
                                onClick={() => {
                                  setShowReadingModal(false);
                                  setCurrentReadingAssignment(null);
                                  setReadingTitle('');
                                  setReadingPassage('');
                                  setReadingSelectedYears([]);
                                }}
                                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </div>

                          {/* Modal Content */}
                          <div className="p-6 space-y-6">
                            {/* Title */}
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">Assignment Title</label>
                              <input
                                type="text"
                                value={readingTitle}
                                onChange={(e) => setReadingTitle(e.target.value)}
                                placeholder="e.g., The Solar System - Chapter 3"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                              />
                            </div>

                            {/* Reading Passage */}
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">Reading Passage</label>
                              <textarea
                                value={readingPassage}
                                onChange={(e) => setReadingPassage(e.target.value)}
                                placeholder="Type or paste the reading passage here..."
                                rows="12"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-y leading-relaxed"
                              />
                              <p className="text-xs text-gray-500 mt-1">{readingPassage.length} characters</p>
                            </div>

                            {/* Assign to Years */}
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-3">Assign to Years</label>
                              <div className="grid grid-cols-4 gap-2">
                                {['Pre-School', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6', 'Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11', 'Year 12', 'Year 13'].map(year => (
                                  <button
                                    key={year}
                                    type="button"
                                    onClick={() => {
                                      if (readingSelectedYears.includes(year)) {
                                        setReadingSelectedYears(readingSelectedYears.filter(y => y !== year));
                                      } else {
                                        setReadingSelectedYears([...readingSelectedYears, year]);
                                      }
                                    }}
                                    className={`py-2 px-4 rounded-lg font-medium transition-all ${
                                      readingSelectedYears.includes(year)
                                        ? 'bg-orange-500 text-white shadow-lg'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                  >
                                    {year}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-end space-x-3 pt-4 border-t">
                              <button
                                onClick={() => setShowReadingModal(false)}
                                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={saveReadingAssignment}
                                disabled={!readingTitle || !readingPassage}
                                className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-600 text-white rounded-lg hover:from-yellow-600 hover:to-orange-700 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                              >
                                {currentReadingAssignment ? 'Update' : 'Create'} Assignment
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const AnalyticsView = () => (
    <div className="h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm border-b flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => goToHome()}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">📈 Analytics</h1>
                <p className="text-gray-600">Performance insights and reports</p>
              </div>
            </div>
            <button
              onClick={() => goToHome()}
              className="p-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📈</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Analytics Coming Soon</h3>
            <p className="text-gray-600">Performance insights and detailed reports will be available here</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Chat view removed

  const PlanningView = () => (
    <div className="h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm border-b flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => goToHome()}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">🗓️ Planning</h1>
                <p className="text-gray-600">Lesson plans and curriculum</p>
              </div>
            </div>
            <button
              onClick={() => goToHome()}
              className="p-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <PlanningTab />
          </div>
        </div>
      </div>
    </div>
  );

  const DesignView = () => (
    <div className="h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm border-b flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => goToHome()}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">🎨 Design</h1>
                <p className="text-gray-600">Create educational materials</p>
              </div>
            </div>
            <button
              onClick={() => goToHome()}
              className="p-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <DesignTab />
          </div>
        </div>
      </div>
    </div>
  );

  const WhiteboardView = () => (
    <div className="h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm border-b flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => goToHome()}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">🧑‍🏫 Whiteboard</h1>
                <p className="text-gray-600">Interactive teaching tools</p>
              </div>
            </div>
            <button
              onClick={() => goToHome()}
              className="p-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <WhiteboardTab />
          </div>
        </div>
      </div>
    </div>
  );

  // PostsView inlined into renderCurrentView to prevent SchoolPostsTab from remounting on parent re-renders

  // ✅ Comprehensive Attendance View Component
  const AttendanceView = () => {
    const [allStudents, setAllStudents] = useState([]);
    const [attendanceRecords, setAttendanceRecords] = useState({});
    const [presentCodes, setPresentCodes] = useState([]);
    const [absenceCodes, setAbsenceCodes] = useState([]);
    const [currentPeriod, setCurrentPeriod] = useState(null);
    const [expandedYears, setExpandedYears] = useState({});
    const [expandedForms, setExpandedForms] = useState({});
    const [submittingAttendance, setSubmittingAttendance] = useState({});
    const [loading, setLoading] = useState(true);
    const [pendingChanges, setPendingChanges] = useState({});
    const [submittingAll, setSubmittingAll] = useState(false);
    const [lateMinutes, setLateMinutes] = useState({}); // Track late minutes input for each student
    const [loadingForms, setLoadingForms] = useState({}); // Track which forms are loading iSAMS data
    const [activeTab, setActiveTab] = useState('attendance'); // 'attendance' or 'out-of-school'
    const [outOfSchoolRecords, setOutOfSchoolRecords] = useState({}); // Track reason/notes for each student
    const [outOfSchoolModal, setOutOfSchoolModal] = useState({ isOpen: false, student: null, reason: '' });
    const [oosStudentsToday, setOosStudentsToday] = useState([]); // Students currently out of school from iSAMS
    const [loadingOOS, setLoadingOOS] = useState(false);

    // Year group configuration - dynamically built from actual student forms
    const yearGroups = useMemo(() => {
      if (!allStudents || allStudents.length === 0) {
        return [];
      }
      // Build year groups from actual student data
      const yearFormMap = {};
      allStudents.forEach(s => {
        const label = getYearLabel(s.nc_year || s.ncYear);
        const form = s.form;
        if (label && form) {
          if (!yearFormMap[label]) yearFormMap[label] = new Set();
          yearFormMap[label].add(form);
        }
      });
      const order = ['Pre-School', 'Preschool', 'Reception', ...Array.from({length: 13}, (_, i) => `Year ${i+1}`)];
      return order
        .filter(name => yearFormMap[name])
        .map(name => ({
          key: name === 'Pre-School' || name === 'Preschool' ? 'PS' : name === 'Reception' ? 'R' : name.replace('Year ', ''),
          name: name,
          forms: [...yearFormMap[name]].sort()
        }));
    }, [allStudents]);

    // Load data on component mount
    useEffect(() => {
      const loadAttendanceData = async () => {
        try {
          setLoading(true);

          // Load all students
          const studentsRes = await fetch(`${server}/synced-students`);
          const studentsData = await studentsRes.json();
          setAllStudents(studentsData.students || []);

          // Load present codes
          const presentRes = await fetch(`${server}/attendance/present-codes`);
          const presentData = await presentRes.json();
          setPresentCodes(presentData.codes || []);

          // Load absence codes
          const absenceRes = await fetch(`${server}/attendance/absence-codes`);
          const absenceData = await absenceRes.json();
          setAbsenceCodes(absenceData.codes || []);

          // Load current period
          const periodRes = await fetch(`${server}/attendance/current-period`);
          const periodData = await periodRes.json();
          setCurrentPeriod(periodData.period || null);

          // Load existing attendance records for today
          const today = new Date().toISOString().split('T')[0];
          const recordsRes = await fetch(`${server}/attendance/records-all`);
          const recordsData = await recordsRes.json();
          const todayRecords = recordsData.records?.[today] || {};
          setAttendanceRecords(todayRecords);

          // Load Out of School records for today
          await loadOOSRecords();

        } catch (error) {
          console.error('Error loading attendance data:', error);
        } finally {
          setLoading(false);
        }
      };

      loadAttendanceData();
    }, []);

    // Load Out of School records from iSAMS
    const loadOOSRecords = async () => {
      try {
        setLoadingOOS(true);
        const response = await fetch(`${server}/attendance/out-of-school-today`);
        const data = await response.json();
        
        if (data.success) {
          setOosStudentsToday(data.records || []);
          console.log(`Loaded ${data.records.length} OOS students for today`);
        } else {
          console.error('Failed to load OOS records:', data.error);
        }
      } catch (error) {
        console.error('Error loading OOS records:', error);
      } finally {
        setLoadingOOS(false);
      }
    };

    // Toggle year group expansion
    const toggleYear = (yearKey) => {
      setExpandedYears(prev => ({
        ...prev,
        [yearKey]: !prev[yearKey]
      }));
    };

    // Toggle form expansion and fetch iSAMS attendance when opening
    const toggleForm = async (formName) => {
      const isCurrentlyExpanded = expandedForms[formName];
      
      // Toggle the expansion state
      setExpandedForms(prev => ({
        ...prev,
        [formName]: !prev[formName]
      }));
      
      // If expanding (not collapsing), fetch iSAMS attendance data
      if (!isCurrentlyExpanded) {
        setLoadingForms(prev => ({ ...prev, [formName]: true }));
        
        try {
          console.log(`📡 Fetching iSAMS attendance for form ${formName}...`);
          const response = await fetch(`${server}/attendance/isams-form/${formName}`);
          const data = await response.json();
          
          console.log(`📊 iSAMS API Response for ${formName}:`, data);
          
          if (data.success && data.students) {
            console.log(`✅ Loaded ${data.students.length} students from iSAMS for ${formName}`);
            console.log(`📋 Sample student data:`, data.students[0]);
            
            // Show available forms if no students found
            if (data.students.length === 0 && data.available_forms) {
              console.log(`📝 Available forms in iSAMS:`, data.available_forms.slice(0, 30));
              console.log(`🔍 Looking for: "${formName}"`);
              
              // Show helpful message
              if (data.available_forms.length === 0) {
                console.log(`⚠️ No attendance data in iSAMS yet for ${data.date}. Teachers may not have marked attendance.`);
              } else {
                console.log(`⚠️ Form "${formName}" not found in iSAMS data. Available forms:`, data.available_forms.slice(0, 20));
              }
            }
            
            // Update attendance records with iSAMS data
            const updatedRecords = { ...attendanceRecords };
            
            // Initialize form record if it doesn't exist
            if (!updatedRecords[formName]) {
              updatedRecords[formName] = {};
            }
            
            // Update each student's attendance from iSAMS
            data.students.forEach(student => {
              const pupilId = student.pupilId || student.studentId;
              console.log(`  🔄 Updating student ${student.fullName} (${pupilId}): ${student.status}`);
              updatedRecords[formName][pupilId] = {
                status: student.status, // 'present', 'late', 'absent'
                absence_reason: student.absenceCodeId || '',
                minutes_late: student.isLate ? '' : undefined,
                source: 'isams' // Mark as loaded from iSAMS
              };
            });
            
            setAttendanceRecords(updatedRecords);
            console.log(`✅ Updated attendance records for ${formName}`, updatedRecords);
          } else {
            console.log(`ℹ️ No iSAMS data found for ${formName}:`, data);
          }
        } catch (error) {
          console.error(`❌ Error fetching iSAMS attendance for ${formName}:`, error);
        } finally {
          setLoadingForms(prev => ({ ...prev, [formName]: false }));
        }
      }
    };

    // Get students for a specific form
    const getStudentsForForm = (formName) => {
      return allStudents.filter(student => student.form === formName);
    };

    // Check if student has attendance taken
    const hasAttendanceTaken = (formName, studentId) => {
      const formRecords = attendanceRecords[formName];
      if (!formRecords || !formRecords.records) return false;
      return formRecords.records.some(record => record.studentId === studentId);
    };

    // Get student's attendance status
    const getStudentAttendanceStatus = (formName, studentId) => {
      // Check pending changes first
      const pendingKey = `${formName}-${studentId}`;
      if (pendingChanges[pendingKey]) {
        return pendingChanges[pendingKey].status;
      }
      
      // Check iSAMS loaded data (direct pupilId lookup)
      const formRecords = attendanceRecords[formName];
      if (formRecords && formRecords[studentId]) {
        return formRecords[studentId].status;
      }
      
      // Then check saved records (old structure with records array)
      if (formRecords && formRecords.records) {
        const record = formRecords.records.find(record => record.studentId === studentId);
        return record ? record.status : null;
      }
      
      return null;
    };

    // Get student's absence reason
    const getStudentAbsenceReason = (formName, studentId) => {
      // Check pending changes first
      const pendingKey = `${formName}-${studentId}`;
      if (pendingChanges[pendingKey]) {
        return pendingChanges[pendingKey].absence_reason;
      }
      
      // Check iSAMS loaded data (direct pupilId lookup)
      const formRecords = attendanceRecords[formName];
      if (formRecords && formRecords[studentId]) {
        return formRecords[studentId].absence_reason || '';
      }
      
      // Then check saved records (old structure with records array)
      if (formRecords && formRecords.records) {
        const record = formRecords.records.find(record => record.studentId === studentId);
        return record ? record.absence_reason : null;
      }
      
      return null;
    };

    // Mark student attendance locally (pending changes)
    const markStudentAttendance = (formName, student, status, absenceReason = null, minutesLate = null) => {
      const pendingKey = `${formName}-${student.pupil_id}`;
      
      setPendingChanges(prev => ({
        ...prev,
        [pendingKey]: {
          formName,
          student,
          status,
          absence_reason: absenceReason,
          minutes_late: minutesLate,
          timestamp: new Date().toISOString()
        }
      }));
    };

    // Submit all pending changes
    const submitAllAttendance = async () => {
      if (Object.keys(pendingChanges).length === 0) {
        alert('No attendance changes to submit');
        return;
      }

      setSubmittingAll(true);

      try {
        // Group changes by form
        const changesByForm = {};
        Object.values(pendingChanges).forEach(change => {
          if (!changesByForm[change.formName]) {
            changesByForm[change.formName] = [];
          }
          changesByForm[change.formName].push({
            studentId: change.student.pupil_id,
            status: change.status,
            absence_reason: change.absence_reason,
            minutes_late: change.minutes_late
          });
        });

        let totalSuccessful = 0;
        let totalFailed = 0;
        const results = [];

        // Submit each form's attendance
        for (const [formName, records] of Object.entries(changesByForm)) {
          try {
            const response = await fetch(`${server}/attendance/submit/${formName}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                records,
                teacherEmail: teacherEmail,
                formId: formName
              })
            });

            const result = await response.json();
            
            if (result.success) {
              totalSuccessful += result.successful || 0;
              totalFailed += result.failed || 0;
              results.push({
                formName,
                success: true,
                successful: result.successful,
                failed: result.failed
              });

              // Update local attendance records for this form
              setAttendanceRecords(prev => {
                const newRecords = { ...prev };
                if (!newRecords[formName]) {
                  newRecords[formName] = {
                    teacher_email: teacherEmail,
                    registration_period_id: currentPeriod?.id,
                    submitted_at: new Date().toISOString(),
                    records: []
                  };
                }

                // Add new records
                records.forEach(record => {
                  // Remove existing record for this student
                  newRecords[formName].records = newRecords[formName].records.filter(
                    existing => existing.studentId !== record.studentId
                  );
                  // Add new record
                  newRecords[formName].records.push({
                    ...record,
                    submitted_at: new Date().toISOString()
                  });
                });

                return newRecords;
              });
            } else {
              results.push({
                formName,
                success: false,
                error: result.error
              });
            }
          } catch (error) {
            results.push({
              formName,
              success: false,
              error: error.message
            });
          }
        }

        // Clear pending changes on successful submission
        setPendingChanges({});

        // Show results
        if (totalSuccessful > 0) {
          alert(`✅ Successfully submitted attendance for ${totalSuccessful} students${totalFailed > 0 ? `. ${totalFailed} failed.` : '.'}`);
        } else {
          alert(`❌ Failed to submit attendance. Please try again.`);
        }

        console.log('Attendance submission results:', results);

      } catch (error) {
        console.error('Error submitting attendance:', error);
        alert('Error submitting attendance. Please try again.');
      } finally {
        setSubmittingAll(false);
      }
    };

    // Handle out of school input change
    const handleOutOfSchoolInput = (studentId, field, value) => {
      setOutOfSchoolRecords(prev => ({
        ...prev,
        [studentId]: {
          ...prev[studentId],
          [field]: value
        }
      }));
    };

    // Submit individual out of school record
    const submitOutOfSchoolRecord = async (student) => {
      const record = outOfSchoolRecords[student.pupil_id];
      if (!record || (!record.reason && !record.notes)) {
        alert('Please enter at least a reason or notes');
        return;
      }

      setSubmittingAttendance(prev => ({ ...prev, [student.pupil_id]: true }));

      try {
        const today = new Date().toISOString().split('T')[0];
        const response = await fetch(`${server}/attendance/register-early-out`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            student_id: student.school_id,
            date: today,
            attendance_type: 'out_of_school',
            reason: record.reason || '',
            notes: record.notes || ''
          })
        });

        const result = await response.json();

        if (result.success) {
          alert(`✅ Successfully registered out of school for ${student.full_name}`);
          // Clear the form for this student
          setOutOfSchoolRecords(prev => {
            const newRecords = { ...prev };
            delete newRecords[student.pupil_id];
            return newRecords;
          });
        } else {
          alert(`❌ Error: ${result.error}`);
        }
      } catch (error) {
        console.error('Error submitting out of school record:', error);
        alert(`❌ Failed: ${error.message}`);
      } finally {
        setSubmittingAttendance(prev => ({ ...prev, [student.pupil_id]: false }));
      }
    };

    // Bulk submit all out of school records
    const submitAllOutOfSchool = async () => {
      const recordsToSubmit = Object.entries(outOfSchoolRecords).filter(
        ([studentId, record]) => record.reason || record.notes
      );

      if (recordsToSubmit.length === 0) {
        alert('No records to submit. Please enter reason or notes for at least one student.');
        return;
      }

      if (!window.confirm(`Submit ${recordsToSubmit.length} out of school records to iSAMS?`)) {
        return;
      }

      setSubmittingAll(true);
      let successCount = 0;
      let failCount = 0;
      const today = new Date().toISOString().split('T')[0];

      for (const [studentId, record] of recordsToSubmit) {
        const student = allStudents.find(s => s.pupil_id === studentId);
        if (!student) continue;

        try {
          const response = await fetch(`${server}/attendance/register-early-out`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              student_id: student.school_id,
              date: today,
              attendance_type: 'out_of_school',
              reason: record.reason || '',
              notes: record.notes || ''
            })
          });

          const result = await response.json();
          if (result.success) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          console.error(`Error submitting for ${student.full_name}:`, error);
          failCount++;
        }
      }

      setSubmittingAll(false);
      alert(`✅ Submitted ${successCount} records successfully${failCount > 0 ? `\n❌ ${failCount} records failed` : ''}`);
      
      // Clear submitted records
      if (successCount > 0) {
        setOutOfSchoolRecords({});
      }
    };

    // Out of School Modal Handlers
    const handleOutOfSchoolClick = (student) => {
      setOutOfSchoolModal({
        isOpen: true,
        student: student,
        reason: ''
      });
    };

    const handleOutOfSchoolSubmit = async () => {
      if (!outOfSchoolModal.reason.trim()) {
        alert('Please enter a reason for the student leaving school early.');
        return;
      }

      try {
        const today = new Date().toISOString().split('T')[0];
        const currentTime = new Date().toTimeString().slice(0, 5);
        
        const response = await fetch(`${server}/attendance/out-of-school`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            studentId: outOfSchoolModal.student.school_id,
            formId: outOfSchoolModal.student.form,
            reason: outOfSchoolModal.reason,
            date: today,
            time: currentTime,
            teacherEmail: localStorage.getItem('teacherEmail') || 'teacher@school.com'
          }),
        });

        const result = await response.json();
        
        if (result.success) {
          alert(`✅ Successfully recorded out-of-school for ${outOfSchoolModal.student.full_name}`);
          setOutOfSchoolModal({ isOpen: false, student: null, reason: '' });
        } else {
          alert(`❌ Error: ${result.error || 'Failed to submit'}`);
        }
      } catch (error) {
        console.error('Error submitting out-of-school:', error);
        alert('❌ Failed to submit. Please try again.');
      }
    };

    // Count attendance statistics for a form
    const getFormAttendanceStats = (formName) => {
      const formStudents = getStudentsForForm(formName);
      
      // Count pending changes for this form
      const pendingForForm = Object.values(pendingChanges).filter(change => change.formName === formName);
      
      // Count saved records
      const formRecords = attendanceRecords[formName];
      const savedRecords = formRecords?.records || [];
      
      // Merge pending and saved (pending takes priority)
      const allTaken = new Set();
      let present = 0;
      let late = 0;
      let absent = 0;
      
      // Add pending changes
      pendingForForm.forEach(change => {
        allTaken.add(change.student.pupil_id);
        if (change.status === 'present') present++;
        else if (change.status === 'late') late++;
        else absent++;
      });
      
      // Add saved records (only if not overridden by pending)
      savedRecords.forEach(record => {
        if (!allTaken.has(record.studentId)) {
          allTaken.add(record.studentId);
          if (record.status === 'present') present++;
          else if (record.status === 'late') late++;
          else absent++;
        }
      });
      
      return { 
        total: formStudents.length, 
        taken: allTaken.size, 
        present, 
        late,
        absent,
        pending: pendingForForm.length
      };
    };

    // Check if there are pending changes
    const hasPendingChanges = () => {
      return Object.keys(pendingChanges).length > 0;
    };

    if (loading) {
      return (
        <div className="h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading attendance data...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="h-screen bg-gray-50 flex flex-col">
        {/* Header */}
        <div className="bg-white shadow-sm border-b flex-shrink-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => goToHome()}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">✅ Attendance</h1>
                  <p className="text-gray-600">
                    Take attendance for all classes • Period: {currentPeriod?.name || 'AM Registration'}
                    {hasPendingChanges() && (
                      <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                        {Object.keys(pendingChanges).length} changes pending
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {/* Submit All Button - Only show on Attendance tab */}
                {activeTab === 'attendance' && (
                <button
                  onClick={submitAllAttendance}
                  disabled={!hasPendingChanges() || submittingAll}
                  className={`px-6 py-3 rounded-lg font-medium text-white transition-all ${
                    hasPendingChanges() && !submittingAll
                      ? 'bg-green-600 hover:bg-green-700 shadow-lg'
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  {submittingAll ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Submitting...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Submit All ({Object.keys(pendingChanges).length})</span>
                    </div>
                  )}
                </button>
                )}
                <button
                  onClick={() => goToHome()}
                  className="p-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 px-4 sm:px-6 lg:px-8">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab('attendance')}
                  className={`${
                    activeTab === 'attendance'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                >
                  📊 Attendance
                </button>
                <button
                  onClick={() => setActiveTab('out-of-school')}
                  className={`${
                    activeTab === 'out-of-school'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                >
                  🚪 Out of School ({oosStudentsToday.length})
                </button>
              </nav>
            </div>
          </div>
        </div>

        {/* Main Content */}
        {activeTab === 'attendance' && (
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            
            {/* Year Groups */}
            <div className="space-y-4">
              {yearGroups.map(yearGroup => {
                const isYearExpanded = expandedYears[yearGroup.key];
                
                // Get total students and attendance stats for year
                const yearStudents = allStudents.filter(s => 
                  yearGroup.forms.includes(s.form)
                );
                const yearStats = yearGroup.forms.reduce((acc, form) => {
                  const stats = getFormAttendanceStats(form);
                  acc.total += stats.total;
                  acc.taken += stats.taken;
                  acc.present += stats.present;
                  acc.absent += stats.absent;
                  acc.pending += stats.pending || 0;
                  return acc;
                }, { total: 0, taken: 0, present: 0, absent: 0, pending: 0 });

                return (
                  <div key={yearGroup.key} className="bg-white rounded-lg shadow-md">
                    {/* Year Header */}
                    <button
                      onClick={() => toggleYear(yearGroup.key)}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="text-2xl">
                          {isYearExpanded ? '📂' : '📁'}
                        </div>
                        <div className="text-left">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {yearGroup.name}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {yearGroup.forms.length} classes • {yearStats.total} students
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-900">
                            {yearStats.taken}/{yearStats.total} taken
                          </div>
                          <div className="text-xs text-gray-500">
                            {yearStats.present} present • {yearStats.absent} absent
                            {yearStats.pending > 0 && (
                              <span className="ml-1 text-yellow-600">• {yearStats.pending} pending</span>
                            )}
                          </div>
                        </div>
                        <svg 
                          className={`w-5 h-5 text-gray-400 transition-transform ${isYearExpanded ? 'rotate-180' : ''}`}
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>

                    {/* Year Content - Forms */}
                    {isYearExpanded && (
                      <div className="border-t border-gray-200 bg-gray-50">
                        <div className="px-6 py-4 space-y-3">
                          {yearGroup.forms.map(formName => {
                            const formStudents = getStudentsForForm(formName);
                            const isFormExpanded = expandedForms[formName];
                            const formStats = getFormAttendanceStats(formName);

                            if (formStudents.length === 0) return null;

                            return (
                              <div key={formName} className="bg-white rounded-lg border border-gray-200">
                                {/* Form Header */}
                                <button
                                  onClick={() => toggleForm(formName)}
                                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className="text-lg">
                                      {isFormExpanded ? '📖' : '📕'}
                                    </div>
                                    <div className="text-left">
                                      <h4 className="font-medium text-gray-900">
                                        {formName}
                                      </h4>
                                      <p className="text-sm text-gray-600">
                                        {formStats.total} students
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-3">
                                    <div className="text-right">
                                      <div className="text-sm font-medium text-gray-900">
                                        {formStats.taken}/{formStats.total}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {formStats.present}P • {formStats.late}L • {formStats.absent}A
                                        {formStats.pending > 0 && (
                                          <span className="text-yellow-600"> • {formStats.pending}P</span>
                                        )}
                                      </div>
                                    </div>
                                    <svg 
                                      className={`w-4 h-4 text-gray-400 transition-transform ${isFormExpanded ? 'rotate-180' : ''}`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </div>
                                </button>

                                {/* Students List */}
                                {isFormExpanded && (
                                  <div className="border-t border-gray-200 bg-gray-50">
                                    {loadingForms[formName] ? (
                                      <div className="p-8 text-center">
                                        <div className="inline-flex items-center space-x-3">
                                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                          <span className="text-gray-600">Loading attendance from iSAMS...</span>
                                        </div>
                                      </div>
                                    ) : (
                                    <div className="p-4">
                                      <div className="grid gap-3">
                                        {formStudents.map(student => {
                                          const attendanceTaken = hasAttendanceTaken(formName, student.pupil_id);
                                          const attendanceStatus = getStudentAttendanceStatus(formName, student.pupil_id);
                                          const absenceReason = getStudentAbsenceReason(formName, student.pupil_id);
                                          const attendanceKey = `${formName}-${student.pupil_id}`;
                                          const isSubmitting = submittingAttendance[attendanceKey];
                                          const hasPending = pendingChanges[attendanceKey];

                                          return (
                                            <div 
                                              key={student.pupil_id}
                                              className={`bg-white rounded-lg border-2 p-2 sm:p-3 transition-all ${
                                                hasPending
                                                  ? 'border-blue-300 bg-blue-50' 
                                                  : attendanceTaken 
                                                    ? 'border-yellow-300 bg-yellow-50' 
                                                    : 'border-gray-200 hover:border-gray-300'
                                              }`}
                                            >
                                              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                                                <div className="flex items-center space-x-2 sm:space-x-3 w-full sm:w-auto">
                                                  {/* Student Avatar */}
                                                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                                                    <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-xs sm:text-sm">
                                                      {student.full_name.charAt(0)}
                                                    </div>
                                                  </div>

                                                  {/* Student Info */}
                                                  <div className="flex-1 min-w-0">
                                                    <h5 className="font-medium text-gray-900 text-sm sm:text-base truncate">
                                                      {student.full_name}
                                                    </h5>
                                                    <p className="text-xs sm:text-sm text-gray-600">
                                                      ID: {student.school_id}
                                                    </p>
                                                    {(attendanceTaken || hasPending) && (
                                                      <p className={`text-xs font-medium ${hasPending ? 'text-blue-700' : 'text-yellow-700'}`}>
                                                        {hasPending ? '🔄' : '✅'} {attendanceStatus === 'present' ? 'Present' : `Absent${absenceReason ? ` - ${absenceReason}` : ''}`}
                                                        {hasPending && <span className="ml-1">(Pending)</span>}
                                                      </p>
                                                    )}
                                                  </div>
                                                </div>

                                                {/* Attendance Buttons */}
                                                <div className="flex flex-wrap items-center gap-1 sm:gap-2 w-full sm:w-auto">
                                                  {isSubmitting ? (
                                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                                                  ) : (
                                                    <>
                                                      {/* Present Button */}
                                                      <button
                                                        onClick={() => markStudentAttendance(formName, student, 'present')}
                                                        className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                                                          attendanceStatus === 'present'
                                                            ? hasPending 
                                                              ? 'bg-blue-600 text-white'
                                                              : 'bg-green-600 text-white'
                                                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                                                        }`}
                                                      >
                                                        Present
                                                      </button>

                                                      {/* Late Button with Minutes Input */}
                                                      <div className="flex items-center space-x-1">
                                                        <button
                                                          onClick={() => {
                                                            const minutes = lateMinutes[`${formName}-${student.pupil_id}`] || 5;
                                                            markStudentAttendance(formName, student, 'late', null, minutes);
                                                          }}
                                                          className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                                                            attendanceStatus === 'late'
                                                              ? hasPending 
                                                                ? 'bg-blue-600 text-white'
                                                                : 'bg-yellow-600 text-white'
                                                              : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                                          }`}
                                                        >
                                                          Late
                                                        </button>
                                                        <input
                                                          type="number"
                                                          min="1"
                                                          max="60"
                                                          value={lateMinutes[`${formName}-${student.pupil_id}`] || 5}
                                                          onChange={(e) => {
                                                            const minutes = parseInt(e.target.value) || 5;
                                                            setLateMinutes(prev => ({
                                                              ...prev,
                                                              [`${formName}-${student.pupil_id}`]: minutes
                                                            }));
                                                          }}
                                                          className="w-10 sm:w-12 px-1 py-1 text-xs border border-gray-300 rounded text-center"
                                                          placeholder="5"
                                                        />
                                                        <span className="text-xs text-gray-500 hidden sm:inline">min</span>
                                                      </div>

                                                      {/* Absent Dropdown */}
                                                      <div className="relative group">
                                                        <button
                                                          className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                                                            attendanceStatus !== 'present' && attendanceStatus !== 'late' && attendanceStatus !== null
                                                              ? hasPending 
                                                                ? 'bg-blue-600 text-white'
                                                                : 'bg-red-600 text-white'
                                                              : 'bg-red-100 text-red-700 hover:bg-red-200'
                                                          }`}
                                                        >
                                                          Absent ▼
                                                        </button>
                                                        <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                                                          {absenceCodes.map(code => (
                                                            <button
                                                              key={code.id}
                                                              onClick={() => markStudentAttendance(formName, student, code.code, code.description)}
                                                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                                                            >
                                                              <div className="font-medium">{code.code}</div>
                                                              <div className="text-xs text-gray-600">{code.description}</div>
                                                            </button>
                                                          ))}
                                                        </div>
                                                      </div>

                                                      {/* Out of School Door Icon Button */}
                                                      <button
                                                        onClick={() => handleOutOfSchoolClick(student)}
                                                        className="flex items-center px-2 sm:px-3 py-1 sm:py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                                                        title="Out of School / Early Out"
                                                      >
                                                        <span className="text-lg sm:text-xl mr-1">🚪</span>
                                                        <span className="text-xs sm:text-sm font-medium hidden sm:inline">Out</span>
                                                      </button>
                                                    </>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

        {/* Out of School Modal */}
        {outOfSchoolModal.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">🚪 Out of School / Early Out</h3>
                <button
                  onClick={() => setOutOfSchoolModal({ isOpen: false, student: null, reason: '' })}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="mb-4">
                <div className="flex items-center space-x-3 mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-blue-600 font-medium">
                      {outOfSchoolModal.student?.full_name?.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{outOfSchoolModal.student?.full_name}</div>
                    <div className="text-sm text-gray-500">ID: {outOfSchoolModal.student?.school_id}</div>
                  </div>
                </div>
                
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for leaving school early:
                </label>
                <textarea
                  value={outOfSchoolModal.reason}
                  onChange={(e) => setOutOfSchoolModal(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Enter reason (e.g., Medical appointment, Family emergency, etc.)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="4"
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setOutOfSchoolModal({ isOpen: false, student: null, reason: '' })}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleOutOfSchoolSubmit}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Submit to iSAMS
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
        )}

    {/* Out of School Tab */}
    {activeTab === 'out-of-school' && (
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          
          {/* Header with Refresh */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Students Out of School Today</h2>
              <p className="text-gray-600 mt-1">
                {new Date().toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
            <button
              onClick={loadOOSRecords}
              disabled={loadingOOS}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
            >
              {loadingOOS ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Refresh</span>
                </>
              )}
            </button>
          </div>

          {/* OOS Students List */}
          {loadingOOS ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : oosStudentsToday.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Students Out of School</h3>
              <p className="text-gray-600">All students are expected in school today.</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {oosStudentsToday.map((record, index) => {
                      // Find student details
                      const student = allStudents.find(s => s.school_id === record.studentId);
                      const studentName = student ? student.full_name : `ID: ${record.studentId}`;
                      const studentForm = student ? student.form : '';
                      
                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{studentName}</div>
                              {studentForm && <div className="text-sm text-gray-500">{studentForm}</div>}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              record.code === 'I' ? 'bg-red-100 text-red-800' :
                              record.code === 'M' ? 'bg-blue-100 text-blue-800' :
                              record.code === 'C' ? 'bg-green-100 text-green-800' :
                              record.code === 'O' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {record.code}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">{record.description || 'No reason provided'}</div>
                            {record.reason && record.reason !== record.description && (
                              <div className="text-sm text-gray-500 mt-1">{record.reason}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(record.startDate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(record.endDate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              record.authorised === 'True' ? 'bg-green-100 text-green-800' :
                              record.authorised === 'False' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {record.authorised === 'True' ? 'Authorised' : 
                               record.authorised === 'False' ? 'Unauthorised' : 'Unknown'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Summary Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    Total: {oosStudentsToday.length} student{oosStudentsToday.length !== 1 ? 's' : ''} out of school
                  </div>
                  <div className="text-xs text-gray-500">
                    Last updated: {new Date().toLocaleTimeString('en-GB')}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    )}
      </div>
    );
  };

  // Chat View Component
  const ChatView = () => {
    const [selectedParent, setSelectedParent] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [unreadCounts, setUnreadCounts] = useState({});
    const [latestTimestamps, setLatestTimestamps] = useState({});

    // Load unread message counts
    const loadUnreadCounts = async () => {
      try {
        const response = await fetch(buildApiUrl(`/messages/unread-by-contact/${teacherEmail}`));
        const data = await response.json();
        if (data?.unread_by_contact) {
          setUnreadCounts(data.unread_by_contact);
        }
      } catch (error) {
        console.error("Failed to load unread counts:", error);
      }
    };

    // Load latest message timestamps
    const loadLatestTimestamps = async () => {
      try {
        const response = await fetch(buildApiUrl(`/messages/latest-timestamps/${teacherEmail}`));
        const data = await response.json();
        if (data?.latest_timestamps) {
          setLatestTimestamps(data.latest_timestamps);
        }
      } catch (error) {
        console.error("Failed to load latest timestamps:", error);
      }
    };

    // Mark thread as read when opening a conversation
    const markThreadAsRead = async (studentId, parentEmail) => {
      try {
        await fetch(buildApiUrl(`/messages/mark-thread-read/${studentId}/${teacherEmail}/${parentEmail}`), {
          method: 'POST'
        });
        // Update local unread counts
        setUnreadCounts(prev => {
          const newCounts = { ...prev };
          delete newCounts[`${parentEmail}_${studentId}`];
          return newCounts;
        });
      } catch (error) {
        console.error("Failed to mark thread as read:", error);
      }
    };

    // Load unread counts and timestamps when component mounts
    useEffect(() => {
      if (teacherEmail) {
        loadUnreadCounts();
        loadLatestTimestamps();
        // Refresh every 2 seconds for faster unread badge updates
        const interval = setInterval(() => {
          loadUnreadCounts();
          loadLatestTimestamps();
        }, 2000);
        return () => clearInterval(interval);
      }
    }, [teacherEmail]);
    
    // Filter parents who have children in the school and create separate channels for each child
    const chatChannels = useMemo(() => {
      if (!parents.length || !students.length) return [];
      
      const channels = [];
      
      // ✅ NEW: Group students by parent first to avoid duplicates
      const parentToStudents = new Map();
      
      parents.forEach(parent => {
        // Find students that belong to this parent
        const parentStudents = students.filter(student => 
          parent.child_ids && parent.child_ids.includes(student.school_id)
        );
        
        if (parentStudents.length > 0) {
          parentToStudents.set(parent.email, {
            parent: parent,
            students: parentStudents
          });
        }
      });
      
      // ✅ Create one channel per parent with all their children listed
      parentToStudents.forEach(({ parent, students: parentStudents }) => {
        // Use the first student as the primary student for the channel
        const primaryStudent = parentStudents[0];
        
        // Create subtitle showing all children
        let subtitle;
        if (parentStudents.length === 1) {
          subtitle = primaryStudent.full_name;
        } else {
          // Show all children's names with last name
          const childNames = parentStudents.map(s => {
            const nameParts = s.full_name.split(' ');
            return nameParts[0]; // First name only
          }).join(' and ');
          // Get last name from first student
          const lastNameParts = primaryStudent.full_name.split(' ');
          const lastName = lastNameParts.length > 1 ? lastNameParts[lastNameParts.length - 1] : '';
          subtitle = lastName ? `${childNames} ${lastName}` : childNames;
        }
        
        channels.push({
          id: `${parent.email}_${primaryStudent.school_id}`,
          parentEmail: parent.email,
          parentName: parent.name,
          parentTitle: parent.title,
          studentId: primaryStudent.school_id,
          studentName: primaryStudent.full_name,
          studentForm: primaryStudent.form,
          allStudents: parentStudents, // ✅ NEW: Keep track of all students
          channelName: (parent.title && parent.name && !parent.name.startsWith(parent.title)) 
            ? `${parent.title} ${parent.name}` 
            : (parent.name || 'Parent'),
          subtitle: subtitle,
          avatar: parent.name?.charAt(0) || 'P'
        });
      });
      
      return channels;
    }, [parents, students]);

    // Filter channels based on search term and sort by latest message timestamp
    const searchFilteredChannels = useMemo(() => {
      let filtered = chatChannels;
      
      // Apply search filter if search term exists
      if (searchTerm.trim()) {
        filtered = chatChannels.filter(channel => 
          (channel.parentName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (channel.parentEmail || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (channel.studentName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (channel.studentForm || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      // Sort by latest message timestamp (most recent first)
      return filtered.sort((a, b) => {
        const keyA = `${a.parentEmail}_${a.studentId}`;
        const keyB = `${b.parentEmail}_${b.studentId}`;
        const timestampA = latestTimestamps[keyA] || '1970-01-01 00:00:00';
        const timestampB = latestTimestamps[keyB] || '1970-01-01 00:00:00';
        
        // Convert to Date objects for proper comparison
        const dateA = new Date(timestampA.replace(' ', 'T'));
        const dateB = new Date(timestampB.replace(' ', 'T'));
        
        return dateB - dateA; // Descending order (newest first)
      });
    }, [chatChannels, searchTerm, latestTimestamps]);
    
    // Debug: log filtered data and timestamps
    console.log("ChatView - Chat Channels:", chatChannels.length);
    console.log("ChatView - Search Filtered Channels:", searchFilteredChannels.length);
    console.log("ChatView - Latest Timestamps:", latestTimestamps);

    return (
      <div className="h-screen bg-gray-50 overflow-hidden">
        {/* Chat Container - Full Screen WhatsApp Style Layout */}
        <div className="max-w-7xl mx-auto h-full flex flex-col overflow-x-hidden">
          <div className="flex-1 bg-white shadow-sm overflow-hidden flex flex-col">
            <div className="flex h-full overflow-hidden">
              
              {/* Left Side - Parent Contacts (Mobile: Full Width, Desktop: Sidebar) */}
              <div className={`bg-white border-r border-gray-200 flex flex-col ${
                selectedParent ? 'hidden lg:flex lg:w-80 xl:w-96' : 'w-full lg:w-80 xl:w-96'
              }`}>
                {/* Mobile Header with Back Button */}
                <div className="lg:hidden bg-green-600 text-white px-4 py-3 flex items-center gap-3">
                  <button
                    onClick={() => goToHome()}
                    className="p-1 hover:bg-green-700 rounded transition-colors"
                    title="Back to Home"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                  </button>
                  <h2 className="text-lg font-semibold">Parent Chat</h2>
                </div>
                
                {/* Desktop Header with Back Button */}
                <div className="hidden lg:flex bg-white border-b border-gray-200 px-4 py-3 items-center gap-3">
                  <button
                    onClick={() => goToHome()}
                    className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                    title="Back to Home"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                  </button>
                  <h2 className="text-lg font-semibold text-gray-900">Parent Chat</h2>
                </div>
                
                {/* Search Bar */}
                <div className="p-3 lg:p-4 border-b border-gray-200 bg-gray-50 lg:bg-white">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search parents or students..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 lg:py-3 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <svg className="w-4 h-4 text-gray-400 absolute left-3 top-2.5 lg:top-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>

                {/* Parent List - Mobile Optimized */}
                <div className="flex-1 overflow-y-auto">
                  {searchFilteredChannels.map((channel, index) => {
                    const unreadKey = `${channel.parentEmail}_${channel.studentId}`;
                    const unreadCount = unreadCounts[unreadKey] || 0;
                    const latestTimestamp = latestTimestamps[unreadKey];
                    
                    return (
                      <div
                        key={channel.id}
                        onClick={() => {
                          setSelectedParent(channel);
                          if (unreadCount > 0) {
                            markThreadAsRead(channel.studentId, channel.parentEmail);
                          }
                        }}
                        className={`flex items-center p-3 lg:p-4 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0 ${
                          selectedParent?.id === channel.id ? 'bg-green-50 border-r-4 border-r-green-500' : ''
                        }`}
                      >
                        {/* Avatar */}
                        <div className="w-12 h-12 lg:w-14 lg:h-14 bg-green-500 rounded-full flex items-center justify-center text-white font-semibold text-lg mr-3 flex-shrink-0 relative">
                          {channel.avatar.toUpperCase()}
                          {unreadCount > 0 && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 lg:w-6 lg:h-6 bg-red-500 rounded-full flex items-center justify-center">
                              <span className="text-xs lg:text-sm text-white font-bold">
                                {unreadCount > 9 ? '9+' : unreadCount}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {/* Contact Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h3 className={`text-sm lg:text-base font-medium truncate ${
                              unreadCount > 0 ? 'text-gray-900 font-semibold' : 'text-gray-900'
                            }`}>
                              {channel.channelName}
                            </h3>
                            {latestTimestamp && (
                              <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                                {new Date(latestTimestamp.replace(' ', 'T')).toLocaleTimeString([], { 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                })}
                              </span>
                            )}
                          </div>
                          <p className="text-xs lg:text-sm text-gray-600 truncate mt-1">
                            {channel.subtitle}
                          </p>
                          {/* Mobile: Show unread badge below */}
                          {unreadCount > 0 && (
                            <div className="mt-2 lg:hidden">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                {unreadCount} new message{unreadCount > 1 ? 's' : ''}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {/* Desktop unread badge */}
                        <div className="hidden lg:block">
                          {unreadCount > 0 && (
                            <div className="ml-2 px-2 py-1 bg-red-500 text-white rounded-full">
                              <span className="text-xs font-medium">
                                {unreadCount > 9 ? '9+' : unreadCount}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {/* Mobile arrow indicator */}
                        <svg className="w-5 h-5 text-gray-400 ml-2 lg:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    );
                  })}

                  {searchFilteredChannels.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-32 text-gray-500 p-4">
                      <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2m5-8a3 3 0 110-6 3 3 0 010 6zm5 3.5a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                      <p className="text-sm text-center">No parents found</p>
                      <p className="text-xs text-center mt-1">Try adjusting your search</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Side - Chat Area (Mobile: Full Screen, Desktop: Main Area) */}
              <div className={`flex-1 flex flex-col overflow-hidden ${
                selectedParent ? 'flex' : 'hidden lg:flex'
              }`}>
                {selectedParent ? (
                  <div className="h-full flex flex-col overflow-hidden">
                    {/* Mobile Chat Header */}
                    <div className="bg-green-600 text-white px-4 py-3 flex items-center lg:hidden flex-shrink-0">
                      <button 
                        onClick={() => setSelectedParent(null)}
                        className="text-white hover:bg-green-500 rounded-full p-1 mr-3"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                      </button>
                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-semibold mr-3">
                        {selectedParent.avatar.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-white truncate">
                          {selectedParent.channelName}
                        </h3>
                        <p className="text-sm text-green-100 truncate">{selectedParent.subtitle}</p>
                      </div>
                    </div>
                    
                    {/* Desktop Chat Header */}
                    <div className="hidden lg:flex p-4 border-b bg-gray-50 items-center space-x-3 flex-shrink-0">
                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-semibold">
                        {selectedParent.avatar.toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">
                          {selectedParent.channelName}
                        </h3>
                        <p className="text-sm text-gray-500">{selectedParent.subtitle}</p>
                      </div>
                    </div>
                    
                    {/* Chat Messages Area */}
                    <div className="flex-1 overflow-hidden min-h-0">
                      <ChannelChat
                        teacherId={teacherEmail}
                        teacherName={teacherName}
                        parentId={selectedParent.parentId}
                        parentName={selectedParent.parentName}
                        parentEmail={selectedParent.parentEmail}
                        studentId={selectedParent.studentId}
                        studentName={selectedParent.studentName}
                        channelId={selectedParent.id}
                      />
                    </div>
                  </div>
                ) : (
                  /* Welcome Message - Hidden on Mobile */
                  <div className="hidden lg:flex flex-1 flex-col items-center justify-center p-8 text-center">
                    <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-medium text-gray-900 mb-2">Welcome to Parent Chat</h3>
                    <p className="text-gray-600 mb-4">Select a parent from the list to start a conversation about their child</p>
                    <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4 max-w-md">
                      <p className="mb-2">💡 <strong>Tips:</strong></p>
                      <ul className="text-left space-y-1">
                        <li>• Each conversation is specific to one child</li>
                        <li>• Use the search bar to quickly find parents</li>
                        <li>• Messages are saved and can be viewed anytime</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Fire Registers View - Emergency fire drill attendance
  const FireRegistersView = () => {
    const [fireFormData, setFireFormData] = useState({
      classSelection: '',
      students: [],
      missingStudents: [],
      presentStudents: [],
      lastUpdated: null
    });
    const [ownedClasses, setOwnedClasses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingStudents, setLoadingStudents] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertSent, setAlertSent] = useState(false);
    const [currentEventId, setCurrentEventId] = useState(null);

    // Load owned classes and current event on component mount
    useEffect(() => {
      loadOwnedClasses();
      loadCurrentEvent();
      
      // Poll for active fire drill events every 5 seconds
      const eventPollInterval = setInterval(() => {
        loadCurrentEvent();
      }, 5000);
      
      return () => clearInterval(eventPollInterval);
    }, [teacherEmail]);

    const loadCurrentEvent = async () => {
      try {
        const response = await fetch(buildApiUrl(`/fire-drill/events?t=${Date.now()}`));
        const data = await response.json();
        console.log('[Fire Registers] Loaded events:', data.events?.length || 0);
        if (data.success && data.events && data.events.length > 0) {
          const activeEvent = data.events.find(e => e.status === 'active');
          console.log('[Fire Registers] Active event found:', activeEvent ? activeEvent.event_id : 'none');
          if (activeEvent) {
            setCurrentEventId(activeEvent.event_id);
          } else {
            // No active event found, clear it
            console.log('[Fire Registers] Clearing currentEventId - no active event');
            setCurrentEventId(null);
          }
        } else {
          // No events at all, clear current event
          console.log('[Fire Registers] No events found, clearing currentEventId');
          setCurrentEventId(null);
        }
      } catch (error) {
        console.error('[Fire Registers] Error loading current event:', error);
        // On error, clear the event to prevent showing false active state
        setCurrentEventId(null);
      }
    };

    const loadOwnedClasses = async () => {
      try {
        const response = await fetch(buildApiUrl(`/teacher-owned-classes/${encodeURIComponent(teacherEmail)}`));
        const data = await response.json();
        setOwnedClasses(data.classes || []);
      } catch (error) {
        console.error('Error loading owned classes:', error);
        setOwnedClasses([]);
      }
    };

    const loadStudentsForClass = async (classForm) => {
      if (!classForm) return;
      
      // Check if there's an active fire drill
      if (!currentEventId) {
        alert('No active fire drill event. Please wait for the Fire Marshal to start a fire drill before taking attendance.');
        return;
      }
      
      setLoadingStudents(true);
      try {
        // Use the new iSAMS-integrated fire drill endpoint
        const response = await fetch(buildApiUrl(`/fire-drill/present-students/${encodeURIComponent(classForm)}`));
        const data = await response.json();
        
        if (data.success) {
          // Combine present and late students
          const allStudents = [...(data.present_students || []), ...(data.late_students || [])];
          
          if (allStudents.length === 0) {
            alert(`No students are present or late in ${classForm} today according to iSAMS registration. Please check if attendance has been taken.`);
            return;
          }
          
          console.log(`🔥 Fire Drill: Loaded ${allStudents.length} present/late students for ${classForm} from iSAMS (Present: ${data.present_students?.length || 0}, Late: ${data.late_students?.length || 0})`);
          
          // Initialize fire drill attendance - all students start as "unknown"
          setFireFormData(prev => ({
            ...prev,
            classSelection: classForm,
            students: allStudents,
            missingStudents: [],
            presentStudents: [],
            lastUpdated: new Date().toISOString(),
            source: data.source || 'iSAMS_API',
            period_id: data.period_id
          }));
          
          // Reset alert status when switching to a new class
          setAlertSent(false);
        } else {
          console.error('Failed to load present students from iSAMS:', data.error);
          alert('Error loading students from iSAMS: ' + (data.error || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error loading present students:', error);
        alert('Error loading students present today: ' + error.message);
      } finally {
        setLoadingStudents(false);
      }
    };

    const markStudentAttendance = (student, status) => {
      const studentId = student.school_id;
      
      setFireFormData(prev => {
        let newMissing = [...prev.missingStudents];
        let newPresent = [...prev.presentStudents];
        
        // Remove from both arrays first
        newMissing = newMissing.filter(id => id !== studentId);
        newPresent = newPresent.filter(id => id !== studentId);
        
        // Add to appropriate array
        if (status === 'missing') {
          newMissing.push(studentId);
        } else if (status === 'present') {
          newPresent.push(studentId);
        }
        
        return {
          ...prev,
          missingStudents: newMissing,
          presentStudents: newPresent
        };
      });
      
      // Reset alert sent status when student status changes
      // This allows sending multiple alerts for different students
      setAlertSent(false);
    };

    const getStudentStatus = (studentId) => {
      if (fireFormData.missingStudents.includes(studentId)) return 'missing';
      if (fireFormData.presentStudents.includes(studentId)) return 'present';
      return 'unknown';
    };

    const sendMissingStudentAlert = async () => {
      if (fireFormData.missingStudents.length === 0) {
        alert('No missing students to report');
        return;
      }
      
      setLoading(true);
      try {
        const missingStudentNames = fireFormData.missingStudents.map(studentId => {
          const student = fireFormData.students.find(s => s.school_id === studentId);
          return student ? student.full_name : studentId;
        });

        const response = await fetch(buildApiUrl('/fire-drill/emergency-alert'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            teacher_email: teacherEmail,
            teacher_name: teacherName || 'Teacher',
            form: fireFormData.classSelection,
            missing_students: missingStudentNames,
            present_students: fireFormData.presentStudents.length,
            total_students: fireFormData.students.length,
            timestamp: new Date().toISOString(),
            drill_time: new Date().toISOString()
          })
        });
        
        const result = await response.json();
        
        if (result.success) {
          setAlertSent(true);
          setShowAlert(true);
          setTimeout(() => setShowAlert(false), 5000);
          
          // Reset alert status after 30 seconds to allow follow-up alerts
          setTimeout(() => {
            setAlertSent(false);
          }, 30000);
        } else {
          alert('Failed to send emergency alert: ' + (result.error || 'Unknown error'));
        }
      } catch (error) {
        console.error('Emergency alert error:', error);
        alert('Error sending emergency alert: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    const submitFireDrillReport = async () => {
      if (!fireFormData.classSelection) {
        alert('Please select a class first');
        return;
      }

      // Get active fire drill event ID from state (queried from backend every time component loads)
      if (!currentEventId) {
        alert('No active fire drill event. Please ask the Fire Marshal to start a fire drill event first.');
        return;
      }

      const unknownCount = fireFormData.students.length - fireFormData.presentStudents.length - fireFormData.missingStudents.length;
      if (unknownCount > 0) {
        const confirmSubmit = window.confirm(
          `${unknownCount} student(s) have not been marked. Are you sure you want to submit the report?`
        );
        if (!confirmSubmit) return;
      }

      setLoading(true);
      try {
        const response = await fetch(buildApiUrl('/fire-drill/submit-report'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            event_id: currentEventId,
            form: fireFormData.classSelection,
            teacher_email: teacherEmail,
            teacher_name: teacherName || 'Teacher',
            present_students: fireFormData.presentStudents,
            missing_students: fireFormData.missingStudents,
            total_students: fireFormData.students.length,
            unknown_students: unknownCount,
            event_date: new Date().toISOString().split('T')[0],
            event_time: new Date().toISOString(),
            submission_timestamp: new Date().toISOString()
          })
        });

        const result = await response.json();

        if (result.success) {
          alert('✅ Fire drill report submitted successfully!');
          // Reset the form
          setFireFormData({
            classSelection: '',
            students: [],
            missingStudents: [],
            presentStudents: [],
            lastUpdated: null
          });
          setAlertSent(false);
        } else {
          alert('Failed to submit report: ' + (result.error || 'Unknown error'));
        }
      } catch (error) {
        console.error('Report submission error:', error);
        alert('Error submitting report: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 p-4 lg:p-6">
        {/* Alert Banner */}
        {showAlert && (
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg">
            ✅ Emergency alert sent to all staff members successfully!
          </div>
        )}
        
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 to-orange-600 p-6 text-white">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🚨</span>
                </div>
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold">Fire Registers</h1>
                  <p className="text-red-100 mt-1">Emergency fire drill attendance tracking</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              {/* Fire Drill Status Banner */}
              <div className={`mb-6 rounded-lg p-4 border-2 ${
                currentEventId 
                  ? 'bg-red-50 border-red-500' 
                  : 'bg-gray-50 border-gray-300'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{currentEventId ? '🚨' : '⏸️'}</span>
                    <div>
                      <h3 className={`font-semibold ${currentEventId ? 'text-red-700' : 'text-gray-700'}`}>
                        {currentEventId ? 'FIRE DRILL ACTIVE' : 'No Active Fire Drill'}
                      </h3>
                      <p className={`text-sm ${currentEventId ? 'text-red-600' : 'text-gray-600'}`}>
                        {currentEventId 
                          ? 'You can now take attendance for your class' 
                          : 'Waiting for Fire Marshal to start a fire drill event'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={loadCurrentEvent}
                    className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                    title="Refresh status"
                  >
                    🔄 Refresh
                  </button>
                </div>
              </div>

              {/* Class Selection */}
              {ownedClasses.length > 0 ? (
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Select Your Class</h2>
                  
                  {/* Emergency Procedure Notice */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <span className="text-2xl">⚠️</span>
                      <h3 className="font-medium text-yellow-800">Emergency Procedure</h3>
                    </div>
                    <p className="text-yellow-700 text-sm">
                      During a fire drill, take attendance for your assigned class and report any missing students immediately.
                      <br />
                      <strong>Only students marked as PRESENT or LATE today are shown below.</strong>
                    </p>
                  </div>

                  {/* Class Selection Buttons */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
                    {ownedClasses.map((classForm) => (
                      <button
                        key={classForm}
                        onClick={() => loadStudentsForClass(classForm)}
                        disabled={loadingStudents || !currentEventId}
                        className={`p-4 rounded-lg border-2 transition-all font-medium ${
                          fireFormData.classSelection === classForm
                            ? 'border-red-500 bg-red-50 text-red-700'
                            : 'border-gray-200 hover:border-red-300 hover:bg-red-50 text-gray-700'
                        } ${(loadingStudents || !currentEventId) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div className="flex items-center justify-center space-x-2">
                          <span className="text-lg">👑</span>
                          <span>{classForm}</span>
                        </div>
                        <div className="text-xs mt-1 opacity-70">Class Owner</div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">👑</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No Owned Classes</h3>
                  <p className="text-gray-600">You are not assigned as a class owner for any classes.</p>
                  <p className="text-sm text-gray-500 mt-2">Only class owners can access Fire Registers for emergency attendance.</p>
                </div>
              )}

              {/* Student List */}
              {fireFormData.classSelection && fireFormData.students.length > 0 && (
                <div className="grid lg:grid-cols-2 gap-8">
                  {/* Left Column - Student Attendance */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Class {fireFormData.classSelection} - Fire Register
                      </h3>
                      <div className="text-sm text-gray-500">
                        {fireFormData.students.length} students present/late today
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                      <div className="space-y-2">
                        {fireFormData.students.map((student) => {
                          const status = getStudentStatus(student.school_id);
                          return (
                            <div key={student.school_id} className="flex items-center justify-between p-3 bg-white rounded border">
                              <div>
                                <div className="font-medium text-gray-900">{student.full_name}</div>
                                <div className="text-sm text-gray-500">ID: {student.school_id}</div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => markStudentAttendance(student, 'present')}
                                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                    status === 'present'
                                      ? 'bg-green-500 text-white'
                                      : 'bg-gray-200 text-gray-700 hover:bg-green-100'
                                  }`}
                                >
                                  PRESENT
                                </button>
                                <button
                                  onClick={() => markStudentAttendance(student, 'missing')}
                                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                    status === 'missing'
                                      ? 'bg-red-500 text-white'
                                      : 'bg-gray-200 text-gray-700 hover:bg-red-100'
                                  }`}
                                >
                                  MISSING
                                </button>
                                {status !== 'unknown' && (
                                  <button
                                    onClick={() => markStudentAttendance(student, 'unknown')}
                                    className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
                                    title="Reset"
                                  >
                                    ↺
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Actions & Summary */}
                  <div className="space-y-6">
                    {/* Attendance Summary */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Attendance Summary</h4>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold text-green-600">{fireFormData.presentStudents.length}</div>
                          <div className="text-sm text-gray-600">Present</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-red-600">{fireFormData.missingStudents.length}</div>
                          <div className="text-sm text-gray-600">Missing</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-gray-600">
                            {fireFormData.students.length - fireFormData.presentStudents.length - fireFormData.missingStudents.length}
                          </div>
                          <div className="text-sm text-gray-600">Unknown</div>
                        </div>
                      </div>

                      {/* Submit Report Button */}
                      <button
                        onClick={submitFireDrillReport}
                        disabled={loading}
                        className="w-full mt-4 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center space-x-2"
                      >
                        {loading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Submitting Report...</span>
                          </>
                        ) : (
                          <>
                            <span>💾</span>
                            <span>Submit Report</span>
                          </>
                        )}
                      </button>
                    </div>

                    {/* Missing Students Alert */}
                    {fireFormData.missingStudents.length > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center space-x-2 mb-3">
                          <span className="text-2xl">🚨</span>
                          <h3 className="font-medium text-red-800">Emergency Alert Required</h3>
                        </div>
                        <p className="text-red-700 text-sm mb-3">
                          {fireFormData.missingStudents.length} student(s) are missing. Send emergency alert immediately.
                        </p>
                        <div className="mb-3">
                          <h4 className="text-sm font-medium text-red-800 mb-1">Missing Students:</h4>
                          <ul className="text-sm text-red-700">
                            {fireFormData.missingStudents.map(studentId => {
                              const student = fireFormData.students.find(s => s.school_id === studentId);
                              return (
                                <li key={studentId}>• {student ? student.full_name : studentId}</li>
                              );
                            })}
                          </ul>
                        </div>
                        <button
                          onClick={sendMissingStudentAlert}
                          disabled={loading || alertSent}
                          className="w-full bg-red-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center space-x-2"
                        >
                          {loading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              <span>Sending Alert...</span>
                            </>
                          ) : alertSent ? (
                            <>
                              <span>✅</span>
                              <span>Alert Sent (Available again in 30s)</span>
                            </>
                          ) : (
                            <>
                              <span>🚨</span>
                              <span>SEND EMERGENCY ALERT</span>
                            </>
                          )}
                        </button>
                      </div>
                    )}

                    {/* All Present Status */}
                    {fireFormData.missingStudents.length === 0 && 
                     fireFormData.presentStudents.length === fireFormData.students.length && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center space-x-2 mb-3">
                          <span className="text-2xl">✅</span>
                          <h3 className="font-medium text-green-800">All Students Present</h3>
                        </div>
                        <p className="text-green-700 text-sm">
                          All students in {fireFormData.classSelection} are accounted for and safe.
                        </p>
                      </div>
                    )}

                    {/* Fire Drill Instructions */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h3 className="font-medium text-blue-800 mb-3">🏃‍♂️ Fire Drill Instructions</h3>
                      <ul className="text-blue-700 text-sm space-y-1">
                        <li>1. Evacuate calmly to designated assembly point</li>
                        <li>2. Take attendance immediately upon arrival</li>
                        <li>3. Mark students as PRESENT or MISSING</li>
                        <li>4. Send emergency alert for any missing students</li>
                        <li>5. Wait for all-clear from fire marshals</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Loading Students */}
              {loadingStudents && (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading students for {fireFormData.classSelection}...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 📅 NEW: Teacher Timetable View Component
  const TimetableView = () => {
    const [isPrinting, setIsPrinting] = useState(false);

    const formatTime = (timeStr) => {
      if (!timeStr) return '';
      // Convert 24-hour format to 12-hour format
      const [hours, minutes] = timeStr.split(':');
      const hour12 = parseInt(hours) % 12 || 12;
      const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
      return `${hour12}:${minutes} ${ampm}`;
    };

    const getDaySchedule = (day) => {
      if (!teacherTimetable || !teacherTimetable.days) return [];
      return teacherTimetable.days[day] || [];
    };

    const handlePrintTimetable = () => {
      try {
        const doc = new jsPDF('landscape');
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 15;
        let yPosition = 20;

        // Title
        doc.setFontSize(18);
        doc.text('Teacher Weekly Schedule', margin, yPosition);
        yPosition += 10;

        // Teacher info
        doc.setFontSize(10);
        doc.text(`Teacher: ${teacherEmail || 'N/A'}`, margin, yPosition);
        yPosition += 6;
        doc.text(`Generated: ${new Date().toLocaleDateString()}`, margin, yPosition);
        yPosition += 10;

        // Days and schedule
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'];
        const columnWidth = (pageWidth - 2 * margin) / 5;
        
        // Draw day headers
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        days.forEach((day, index) => {
          const xPos = margin + (index * columnWidth);
          doc.text(day, xPos + 2, yPosition);
        });
        yPosition += 8;

        // Find max periods in any day
        let maxPeriods = 0;
        days.forEach(day => {
          const daySchedule = getDaySchedule(day);
          if (daySchedule.length > maxPeriods) maxPeriods = daySchedule.length;
        });

        // Draw schedule for each period slot
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        
        for (let periodIndex = 0; periodIndex < maxPeriods; periodIndex++) {
          // Check if we need a new page
          if (yPosition > 180) {
            doc.addPage();
            yPosition = 20;
          }

          days.forEach((day, dayIndex) => {
            const daySchedule = getDaySchedule(day);
            const period = daySchedule[periodIndex];
            const xPos = margin + (dayIndex * columnWidth);

            if (period) {
              const time = `${formatTime(period.period?.startTime)} - ${formatTime(period.period?.endTime)}`;
              const subject = period.subject || 'No Subject';
              const classInfo = `${period.class} (${period.form})`;
              const room = period.room ? `Room: ${period.room}` : '';

              // Draw period content
              doc.text(time, xPos + 2, yPosition);
              doc.text(subject, xPos + 2, yPosition + 4);
              doc.text(classInfo, xPos + 2, yPosition + 8);
              if (room) {
                doc.text(room, xPos + 2, yPosition + 12);
              }
            }
          });

          yPosition += 18;
        }

        // Save PDF
        const fileName = `Timetable_${teacherEmail?.split('@')[0] || 'teacher'}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
      } catch (error) {
        console.error('Error exporting timetable to PDF:', error);
        alert('Failed to export timetable. Please try again.');
      }
    };

    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'];

    return (
      <div className="p-4 bg-white flex justify-center">
        <div className="w-full max-w-7xl">
          {/* Print Styles */}
          <style>{`
            @media print {
              @page {
                size: A4 landscape;
                margin: 10mm;
              }
              
              body {
                print-color-adjust: exact;
                -webkit-print-color-adjust: exact;
              }
              
              .no-print {
                display: none !important;
              }
              
              .print-title {
                text-align: center;
                margin-bottom: 10px;
                font-size: 18px;
                font-weight: bold;
              }
              
              .print-info {
                text-align: center;
                margin-bottom: 15px;
                font-size: 12px;
              }
            }
          `}</style>

          <div className="flex justify-between items-center mb-4 no-print">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">📅 My Teaching Schedule</h1>
            <p className="text-sm text-gray-600">Your personal weekly timetable</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrintTimetable}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
            >
              <span>�</span>
              <span>Export to PDF</span>
            </button>
            <button
              onClick={() => goToHome()}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              ← Back
            </button>
          </div>
        </div>

        {/* Print Header - Only visible when printing */}
        {isPrinting && teacherTimetable && (
          <div className="hidden print:block mb-4">
            <div className="print-title">Teacher Weekly Schedule</div>
            <div className="print-info">
              <div>{teacherEmail}</div>
              <div>Generated: {new Date().toLocaleDateString()}</div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loadingTimetable && (
          <div className="text-center py-8 no-print">
            <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-sm text-gray-600">Loading your timetable...</p>
          </div>
        )}

        {/* Error State */}
        {timetableError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center no-print">
            <div className="text-red-600 text-4xl mb-2">⚠️</div>
            <h3 className="text-base font-semibold text-red-800 mb-2">Timetable Not Available</h3>
            <p className="text-sm text-red-700 mb-4">{timetableError}</p>
            <button
              onClick={loadTeacherTimetable}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
            >
              🔄 Try Again
            </button>
          </div>
        )}

        {/* Timetable Content */}
        {!loadingTimetable && !timetableError && teacherTimetable && (
          <div className="space-y-3">
            {/* Weekly Timetable Grid - Compact */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-2">
              {days.map((day) => {
                const daySchedule = getDaySchedule(day);
                return (
                  <div key={day} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    {/* Day Header - Compact */}
                    <div className="bg-indigo-600 text-white p-2 text-center">
                      <h3 className="font-semibold text-sm">{day}</h3>
                      <p className="text-xs opacity-90">{daySchedule.length} classes</p>
                    </div>

                    {/* Day Schedule - Compact */}
                    <div className="p-2 space-y-2">
                      {daySchedule.length === 0 ? (
                        <div className="text-center py-4 text-gray-400">
                          <div className="text-lg mb-1">🏖️</div>
                          <p className="text-xs">No classes</p>
                        </div>
                      ) : (
                        daySchedule.map((period, index) => (
                          <div
                            key={period.id}
                            className="bg-gray-50 border border-gray-200 rounded p-2 hover:bg-gray-100 transition-colors"
                          >
                            {/* Time - Compact */}
                            <div className="flex justify-between items-start mb-1">
                              <div className="text-[10px] text-gray-500">
                                {formatTime(period.period?.startTime)} - {formatTime(period.period?.endTime)}
                              </div>
                            </div>

                            {/* Subject and Class - Compact */}
                            <div className="mb-1">
                              <div className="font-semibold text-gray-800 text-xs">
                                {period.subject || 'No Subject'}
                              </div>
                              <div className="text-[10px] text-gray-600">
                                {period.class} ({period.form})
                              </div>
                            </div>

                            {/* Room - Compact */}
                            {period.room && (
                              <div className="text-[10px] text-gray-600">
                                Room: {period.room}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Refresh Button */}
            <div className="text-center pt-3 no-print">
              <button
                onClick={loadTeacherTimetable}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
              >
                🔄 Refresh Timetable
              </button>
            </div>
          </div>
        )}
        </div>
      </div>
    );
  };

  // Course Progress Analytics View - Display student access to nuggets
  // Main render logic
  function renderCurrentView() {
    if (currentView === "home") return <HomePage />;
    if (currentView === "overview") return <OverviewView />;
    if (currentView === "classes") return <ClassesView />;
    if (currentView === "monitor") return <MonitorView />;
    if (currentView === "assignments") return <AssignmentsView />;
  if (currentView === "analytics") return <AnalyticsView />;
    if (currentView === "planning") return <PlanningView />;
    if (currentView === "design") return <DesignView />;
    if (currentView === "whiteboard") return <WhiteboardView />;
    if (currentView === "posts") return (
      <div className="h-screen bg-gray-50 flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 via-purple-900 to-slate-900 shadow-lg border-b border-purple-500/30 sticky top-0 z-50 flex-shrink-0">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {/* Hamburger Menu for mobile */}
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="lg:hidden p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors flex-shrink-0"
                  title="Filters"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>

                <div className="flex-1 min-w-0">
                  <h1 className="text-base md:text-xl font-bold text-white truncate">
                    📰 School Posts
                  </h1>
                  <p className="text-purple-200 text-xs">Teacher View</p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Home Icon */}
                <button
                  onClick={() => goToHome()}
                  className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all"
                  title="Home"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </button>
                {/* Notification Icon */}
                <button
                  className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all relative"
                  title="Notifications"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <SchoolPostsTab
                studentForm="all"
                isMobileMenuOpen={isMobileMenuOpen}
                setIsMobileMenuOpen={setIsMobileMenuOpen}
              />
            </div>
          </div>
        </div>
      </div>
    );
    if (currentView === "chat") return <ChatView />;
    if (currentView === "attendance") return <AttendanceView />;
    if (currentView === "timetable") return <TimetableView />;
    if (currentView === "my-clubs") return <MyClubs teacherEmail={teacherEmail} onBack={() => setCurrentView("home")} />;
    if (currentView === "education-games") return <EducationGames teacherEmail={teacherEmail} teacherName={teacherName} onBack={() => setCurrentView("home")} />;
    if (currentView === "parents-evening") return <ParentsEveningSystem userRole="teacher" userEmail={teacherEmail} userName={teacherName} onBack={() => handleViewChange('home')} />;
    if (currentView === "fire-registers") return <FireRegistersView />;
    if (currentView === "fire-marshal") return <FireMarshalDashboard />;
    if (currentView === "school-manager") return <SchoolManager onBack={() => handleViewChange('home')} />;
    if (currentView === "reports") return <Reports onBack={() => handleViewChange('home')} />;
    if (currentView === "user-management") return <div className="bg-white rounded-lg shadow-lg p-6"><h2 className="text-2xl font-bold text-gray-800 mb-4">User Management</h2><p>Manage user accounts, permissions, and access rights.</p></div>;
    if (currentView === "system-settings") return <div className="bg-white rounded-lg shadow-lg p-6"><h2 className="text-2xl font-bold text-gray-800 mb-4">System Settings</h2><p>Configure system-wide settings and preferences.</p></div>;
    if (currentView === "homework-bank") return (
      <HomeworksBank 
        onSelectHomework={(homework) => {
          // Navigate to homework creation page with the selected homework for editing/reuse
          navigate('/homeworks?tab=create', { 
            state: { 
              loadFromBank: true, 
              homeworkData: homework 
            } 
          });
        }}
        onClose={() => {
          setCurrentView("assignments");
          setSearchParams({ 
            module: 'assignments', 
            mainTab: 'assignments', 
            tab: 'my-assignments' 
          });
        }}
      />
    );
    return <HomePage />;
  }

  // ✅ ADD: Click outside handler to close options menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAssignmentOptions && !event.target.closest('.relative')) {
        setShowAssignmentOptions(null);
      }
      // Close submissions viewer when clicking outside
      if (viewingSubmissions && event.target.classList.contains('bg-black')) {
        setViewingSubmissions(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showAssignmentOptions, viewingSubmissions]);

  // ✅ ADD: Delete Confirmation Modal Component
  const DeleteConfirmationModal = () => {
    if (!showDeleteConfirm) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Assignment</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{showDeleteConfirm.title}"? This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteAssignment(showDeleteConfirm.id)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ✅ ADD: Submissions Viewer Modal Component
  const SubmissionsViewerModal = () => {
    if (!viewingSubmissions) return null;

    // Get students assigned to this homework
    const assignedStudents = students.filter(s => 
      Array.isArray(viewingSubmissions.assignedTo) && viewingSubmissions.assignedTo.includes(s.form)
    );

    // Get submission status for each student
    const getSubmissionStatus = (studentId, homeworkId) => {
      const submission = submissions[studentId]?.[homeworkId];
      if (submission?.submitted_at) {
        return {
          submitted: true,
          date: submission.submitted_at,
          score: submission.result?.score,
          total: submission.result?.total,
          answers: submission.result?.answers || [],
          file_url: submission.file_url || null
        };
      }
      return { submitted: false };
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{viewingSubmissions.title}</h3>
                <p className="text-gray-600 mt-1">
                  Submissions • Due: {new Date(viewingSubmissions.dueDate).toLocaleDateString()}
                </p>
              </div>
              <button
                onClick={() => setViewingSubmissions(null)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {assignedStudents.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">👥</span>
                </div>
                <p className="text-gray-500">No students assigned to this homework.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="mb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-2">
                    Submission Summary
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-2xl font-bold text-blue-600">
                        {assignedStudents.length}
                      </div>
                      <div className="text-sm text-blue-800">Total Students</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="text-2xl font-bold text-green-600">
                        {assignedStudents.filter(s => getSubmissionStatus(s.school_id, viewingSubmissions.id).submitted).length}
                      </div>
                      <div className="text-sm text-green-800">Submitted</div>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4">
                      <div className="text-2xl font-bold text-red-600">
                        {assignedStudents.filter(s => !getSubmissionStatus(s.school_id, viewingSubmissions.id).submitted).length}
                      </div>
                      <div className="text-sm text-red-800">Not Submitted</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">
                    Student Details
                  </h4>
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Student
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Submitted Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Score
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Work
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {assignedStudents.map((student) => {
                          const status = getSubmissionStatus(student.school_id, viewingSubmissions.id);
                          return (
                            <tr key={student.school_id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {student.full_name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    ID: {student.school_id} • Form: {student.form}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  status.submitted 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {status.submitted ? '✅ Submitted' : '❌ Not Submitted'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {status.submitted 
                                  ? new Date(status.date).toLocaleDateString() + ' ' + new Date(status.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                                  : '-'
                                }
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {status.submitted && status.score !== undefined && status.score > 0
                                  ? `${status.score}/${status.total || '?'}`
                                  : '-'
                                }
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {status.submitted && status.file_url ? (
                                  <a
                                    href={`${server}${status.file_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-xs font-medium"
                                  >
                                    📎 View File
                                  </a>
                                ) : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-500">
                Last updated: {new Date().toLocaleString()}
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => handleExportSubmissions(viewingSubmissions)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  📤 Export CSV
                </button>
                <button
                  onClick={() => setViewingSubmissions(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ✅ ADD: Saved Assignments Modal Component
  const SavedAssignmentsModal = () => {
    if (!showSavedAssignments) return null;

    const handleLoadAssignment = (assignment) => {
      // Navigate to homework creation page with the saved assignment data and tab parameter
      navigate('/homeworks?tab=generate', { 
        state: { 
          loadFromDraft: true,
          draftData: assignment 
        } 
      });
      setShowSavedAssignments(false);
    };

    const handleDeleteSavedAssignment = async (assignmentId) => {
      try {
        const response = await fetch(buildApiUrl(`/saved-homework-draft/${assignmentId}`), {
          method: 'DELETE',
        });

        if (response.ok) {
          // Refresh the saved assignments list
          loadSavedAssignments();
        } else {
          console.error('Failed to delete assignment');
        }
      } catch (error) {
        console.error('Error deleting assignment:', error);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-gradient-to-br from-white to-purple-50 rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden border-2 border-purple-200">
          {/* Header with Space Theme */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                  <span className="text-2xl">💾</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Saved Drafts</h2>
                  <p className="text-green-100 text-xs mt-0.5">{Array.isArray(savedAssignments) ? savedAssignments.length : 0} draft{savedAssignments?.length !== 1 ? 's' : ''} ready to launch</p>
                </div>
              </div>
              <button
                onClick={() => setShowSavedAssignments(false)}
                className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 transition-all duration-300 flex items-center justify-center"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
            {!Array.isArray(savedAssignments) || savedAssignments.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-24 h-24 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-5xl">💾</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">No Saved Drafts</h3>
                <p className="text-gray-600">Your saved assignment drafts will appear here</p>
              </div>
            ) : (
              <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden border border-green-200/50">
                {/* Table Header */}
                <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-4 py-2">
                  <h3 className="text-white font-bold text-base flex items-center">
                    <span className="mr-2 text-lg">📝</span>
                    Draft Assignments
                  </h3>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-bold text-green-900">Title/Prompt</th>
                        <th className="px-3 py-2 text-left text-xs font-bold text-green-900">Created</th>
                        <th className="px-3 py-2 text-center text-xs font-bold text-green-900">Questions</th>
                        <th className="px-3 py-2 text-center text-xs font-bold text-green-900">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-green-100">
                      {savedAssignments.map((assignment, index) => (
                        <tr 
                          key={assignment.id} 
                          className="hover:bg-green-50/50 transition-colors group"
                          style={{ animationDelay: `${index * 50}ms` }}
                        >
                          <td className="px-3 py-2">
                            <div className="flex items-start space-x-2">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                                <span className="text-white text-sm">📄</span>
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-gray-900 mb-1 text-sm">
                                  {assignment.prompt && assignment.prompt.length > 60 
                                    ? `${assignment.prompt.substring(0, 60)}...` 
                                    : assignment.prompt || 'Untitled Draft'}
                                </div>
                                {assignment.questions && assignment.questions.length > 0 && (
                                  <div className="text-xs text-gray-600 space-y-0.5 mt-1 bg-gray-50 rounded p-1.5">
                                    {assignment.questions.slice(0, 2).map((question, qIdx) => (
                                      <div key={qIdx} className="flex items-start">
                                        <span className="text-green-600 font-medium mr-1.5 text-xs">{qIdx + 1}.</span>
                                        <span className="flex-1 text-xs">
                                          {question?.question 
                                            ? (question.question.length > 50 
                                                ? `${question.question.substring(0, 50)}...` 
                                                : question.question)
                                            : 'No question text'}
                                        </span>
                                      </div>
                                    ))}
                                    {assignment.questions.length > 2 && (
                                      <div className="text-gray-500 italic text-center pt-0.5 text-xs">
                                        +{assignment.questions.length - 2} more
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center space-x-1.5">
                              <span className="text-lg">📅</span>
                              <span className="text-xs font-medium text-gray-700">
                                {assignment.createdAt ? new Date(assignment.createdAt).toLocaleDateString() : 'Unknown'}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <div className="inline-flex items-center space-x-1.5 px-2 py-1 bg-emerald-50 rounded border border-emerald-200">
                              <span className="text-sm">❓</span>
                              <span className="font-bold text-emerald-700 text-sm">
                                {assignment.questions?.length || 0}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center justify-center space-x-1.5">
                              <button
                                onClick={() => handleLoadAssignment(assignment)}
                                className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-lg transition-all duration-300 shadow-sm hover:shadow-md font-medium group-hover:scale-105 transform flex items-center space-x-1.5 text-xs"
                              >
                                <span className="text-sm">📝</span>
                                <span>Load</span>
                              </button>
                              <button
                                onClick={() => handleDeleteSavedAssignment(assignment.id)}
                                className="p-1.5 bg-red-100 hover:bg-red-200 rounded-lg transition-colors group-hover:scale-105 transform duration-200"
                                title="Delete Draft"
                              >
                                <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Add Course Modal Component
  const AddCourseModal = () => {
    const [courseName, setCourseName] = useState('');
    const [courseDescription, setCourseDescription] = useState('');
    const [courseSubject, setCourseSubject] = useState('');
    const [selectedYears, setSelectedYears] = useState([]);

    const subjectOptions = ['English', 'Mathematics', 'Arabic', 'Islamic', 'Qatar History', 'Science', 'History'];

    if (!showAddCourseModal) return null;

    const years = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13'];

    const handleAddCourse = async () => {
      if (!courseName.trim()) {
        alert('Please enter a course name');
        return;
      }

      if (selectedYears.length === 0) {
        alert('Please select at least one year');
        return;
      }

      try {
        const payload = {
            teacher_email: teacherEmail,
            name: courseName,
            description: courseDescription,
            subject: courseSubject,
            years: selectedYears
        };
        console.log('📚 [CREATE COURSE] Sending payload:', JSON.stringify(payload, null, 2));
        console.log('📚 [CREATE COURSE] API URL:', buildApiUrl('/api/courses'));
        
        const response = await fetch(buildApiUrl('/api/courses'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        console.log('📚 [CREATE COURSE] Response status:', response.status, response.statusText);

        if (response.ok) {
          const newCourse = await response.json();
          console.log('📚 [CREATE COURSE] Success:', JSON.stringify(newCourse, null, 2));
          newCourse.topics = [];
          setCourses([...courses, newCourse]);
          setCourseName('');
          setCourseDescription('');
          setCourseSubject('');
          setSelectedYears([]);
          setShowAddCourseModal(false);
        } else {
          const errorText = await response.text();
          console.error('📚 [CREATE COURSE] Error response:', response.status, errorText);
          try {
            const error = JSON.parse(errorText);
            alert(error.detail || error.error || 'Failed to create course');
          } catch {
            alert('Failed to create course: ' + errorText);
          }
        }
      } catch (error) {
        console.error('📚 [CREATE COURSE] Network/JS error:', error);
        alert('Failed to create course: ' + error.message);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>📚</span> Add New Course
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Course Name</label>
              <input
                type="text"
                value={courseName}
                onChange={(e) => setCourseName(e.target.value)}
                placeholder="e.g., Mathematics Grade 10"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
              <select
                value={courseSubject}
                onChange={(e) => setCourseSubject(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
              >
                <option value="">Select a subject...</option>
                {subjectOptions.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={courseDescription}
                onChange={(e) => setCourseDescription(e.target.value)}
                placeholder="Brief description of the course..."
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Assign to Year Group *</label>
              <select
                value={selectedYears[0] || ''}
                onChange={(e) => setSelectedYears(e.target.value ? [e.target.value] : [])}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
              >
                <option value="">Select a year group...</option>
                {years.map(year => (
                  <option key={year} value={year}>Year {year}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => {
                setShowAddCourseModal(false);
                setCourseName('');
                setCourseDescription('');
                setCourseSubject('');
                setSelectedYears([]);
              }}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleAddCourse}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all font-medium shadow-lg"
            >
              Add Course
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add Topic Modal Component
  const AddTopicModal = () => {
    const [topicName, setTopicName] = useState('');
    const [topicDescription, setTopicDescription] = useState('');

    if (!showAddTopicModal) return null;

    const handleAddTopic = async () => {
      if (!topicName.trim()) {
        alert('Please enter a topic name');
        return;
      }

      try {
        const response = await fetch(buildApiUrl(`/api/courses/${selectedCourse.id}/topics`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: topicName,
            description: topicDescription
          })
        });

        if (response.ok) {
          const newTopic = await response.json();
          newTopic.nuggets = [];

          const updatedCourses = courses.map(course => {
            if (course.id === selectedCourse.id) {
              return {
                ...course,
                topics: [...(course.topics || []), newTopic]
              };
            }
            return course;
          });

          setCourses(updatedCourses);
          setSelectedCourse(updatedCourses.find(c => c.id === selectedCourse.id));
          setTopicName('');
          setTopicDescription('');
          setShowAddTopicModal(false);
        } else {
          const error = await response.json();
          alert(error.error || 'Failed to create topic');
        }
      } catch (error) {
        console.error('Error creating topic:', error);
        alert('Failed to create topic');
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>📑</span> Add New Topic
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Topic Name</label>
              <input
                type="text"
                value={topicName}
                onChange={(e) => setTopicName(e.target.value)}
                placeholder="e.g., Algebra Basics"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={topicDescription}
                onChange={(e) => setTopicDescription(e.target.value)}
                placeholder="Brief description of the topic..."
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => {
                setShowAddTopicModal(false);
                setTopicName('');
                setTopicDescription('');
              }}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleAddTopic}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all font-medium shadow-lg"
            >
              Add Topic
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Edit Course Handler
  const handleEditCourse = async (courseId, updatedData) => {
    try {
      const response = await fetch(buildApiUrl(`/api/courses/${courseId}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedData)
      });

      if (response.ok) {
        const updatedCourse = await response.json();
        updatedCourse.topics = courses.find(c => c.id === courseId)?.topics || [];
        
        const updatedCourses = courses.map(c => 
          c.id === courseId ? { ...c, ...updatedCourse } : c
        );
        
        setCourses(updatedCourses);
        
        if (selectedCourse?.id === courseId) {
          setSelectedCourse(updatedCourses.find(c => c.id === courseId));
        }
        
        alert('✅ Course updated successfully');
        return true;
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update course');
        return false;
      }
    } catch (error) {
      console.error('Error updating course:', error);
      alert('Failed to update course');
      return false;
    }
  };

  // Delete Course Handler
  const handleDeleteCourse = async (courseId) => {
    try {
      const response = await fetch(buildApiUrl(`/api/courses/${courseId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        setCourses(courses.filter(c => c.id !== courseId));
        
        if (selectedCourse?.id === courseId) {
          setSelectedCourse(null);
          setSelectedTopic(null);
          setSelectedNugget(null);
        }
        
        alert('✅ Course deleted successfully');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete course');
      }
    } catch (error) {
      console.error('Error deleting course:', error);
      alert('Failed to delete course');
    }
  };

  // Edit Topic Handler
  const handleEditTopic = async (topicId, updatedData) => {
    try {
      const response = await fetch(buildApiUrl(`/api/topics/${topicId}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedData)
      });

      if (response.ok) {
        const updatedTopic = await response.json();
        
        const updatedCourses = courses.map(course => {
          if (course.id === selectedCourse.id) {
            return {
              ...course,
              topics: course.topics.map(topic => 
                topic.id === topicId ? { ...topic, ...updatedTopic } : topic
              )
            };
          }
          return course;
        });

        setCourses(updatedCourses);
        const updatedCourse = updatedCourses.find(c => c.id === selectedCourse.id);
        setSelectedCourse(updatedCourse);
        
        if (selectedTopic?.id === topicId) {
          setSelectedTopic(updatedCourse.topics.find(t => t.id === topicId));
        }
        
        alert('✅ Topic updated successfully');
        return true;
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update topic');
        return false;
      }
    } catch (error) {
      console.error('Error updating topic:', error);
      alert('Failed to update topic');
      return false;
    }
  };

  // Delete Topic Handler
  const handleDeleteTopic = async (topicId) => {
    try {
      const response = await fetch(buildApiUrl(`/api/topics/${topicId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        const updatedCourses = courses.map(course => {
          if (course.id === selectedCourse.id) {
            return {
              ...course,
              topics: course.topics.filter(t => t.id !== topicId)
            };
          }
          return course;
        });

        setCourses(updatedCourses);
        setSelectedCourse(updatedCourses.find(c => c.id === selectedCourse.id));
        
        if (selectedTopic?.id === topicId) {
          setSelectedTopic(null);
          setSelectedNugget(null);
        }
        
        alert('✅ Topic deleted successfully');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete topic');
      }
    } catch (error) {
      console.error('Error deleting topic:', error);
      alert('Failed to delete topic');
    }
  };

  // Edit Course Modal Component
  const EditCourseModal = () => {
    const [courseName, setCourseName] = useState(editingCourse?.name || '');
    const [courseDescription, setCourseDescription] = useState(editingCourse?.description || '');
    const [courseSubject, setCourseSubject] = useState(editingCourse?.subject || '');
    const [selectedYears, setSelectedYears] = useState(editingCourse?.assigned_years || []);

    const subjectOptions = ['English', 'Mathematics', 'Arabic', 'Islamic', 'Qatar History', 'Science', 'History'];

    if (!showEditCourseModal || !editingCourse) return null;

    const years = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13'];

    const handleSave = async () => {
      if (!courseName.trim()) {
        alert('Please enter a course name');
        return;
      }

      if (selectedYears.length === 0) {
        alert('Please select at least one year');
        return;
      }

      const success = await handleEditCourse(editingCourse.id, {
        teacher_email: teacherEmail,
        name: courseName,
        description: courseDescription,
        subject: courseSubject,
        years: selectedYears
      });

      if (success) {
        setShowEditCourseModal(false);
        setEditingCourse(null);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>✏️</span> Edit Course
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Course Name</label>
              <input
                type="text"
                value={courseName}
                onChange={(e) => setCourseName(e.target.value)}
                placeholder="e.g., Mathematics Grade 10"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
              <select
                value={courseSubject}
                onChange={(e) => setCourseSubject(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
              >
                <option value="">Select a subject...</option>
                {subjectOptions.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={courseDescription}
                onChange={(e) => setCourseDescription(e.target.value)}
                placeholder="Brief description of the course..."
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Assign to Year Group *</label>
              <select
                value={selectedYears[0] || ''}
                onChange={(e) => setSelectedYears(e.target.value ? [e.target.value] : [])}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
              >
                <option value="">Select a year group...</option>
                {years.map(year => (
                  <option key={year} value={year}>Year {year}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => {
                setShowEditCourseModal(false);
                setEditingCourse(null);
              }}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all font-medium shadow-lg"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Edit Topic Modal Component
  const EditTopicModal = () => {
    const [topicName, setTopicName] = useState(editingTopic?.name || '');
    const [topicDescription, setTopicDescription] = useState(editingTopic?.description || '');

    if (!showEditTopicModal || !editingTopic) return null;

    const handleSave = async () => {
      if (!topicName.trim()) {
        alert('Please enter a topic name');
        return;
      }

      const success = await handleEditTopic(editingTopic.id, {
        name: topicName,
        description: topicDescription
      });

      if (success) {
        setShowEditTopicModal(false);
        setEditingTopic(null);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>✏️</span> Edit Topic
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Topic Name</label>
              <input
                type="text"
                value={topicName}
                onChange={(e) => setTopicName(e.target.value)}
                placeholder="e.g., Algebra Basics"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={topicDescription}
                onChange={(e) => setTopicDescription(e.target.value)}
                placeholder="Brief description of the topic..."
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => {
                setShowEditTopicModal(false);
                setEditingTopic(null);
              }}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all font-medium shadow-lg"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Delete Nugget Handler
  const handleDeleteNugget = async (nuggetId) => {
    try {
      const response = await fetch(buildApiUrl(`/api/nuggets/${nuggetId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        // Update local state
        const updatedCourses = courses.map(course => {
          if (course.id === selectedCourse.id) {
            return {
              ...course,
              topics: course.topics.map(topic => {
                if (topic.id === selectedTopic.id) {
                  return {
                    ...topic,
                    nuggets: (topic.nuggets || []).filter(n => n.id !== nuggetId)
                  };
                }
                return topic;
              })
            };
          }
          return course;
        });

        setCourses(updatedCourses);
        const updatedCourse = updatedCourses.find(c => c.id === selectedCourse.id);
        const updatedTopic = updatedCourse.topics.find(t => t.id === selectedTopic.id);
        setSelectedCourse(updatedCourse);
        setSelectedTopic(updatedTopic);
        
        alert('✅ Nugget deleted successfully');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete nugget');
      }
    } catch (error) {
      console.error('Error deleting nugget:', error);
      alert('Failed to delete nugget');
    }
  };

  // Add Nugget Modal Component
  const AddNuggetModal = () => {
    const [nuggetName, setNuggetName] = useState('');
    const [nuggetDescription, setNuggetDescription] = useState('');

    if (!showAddNuggetModal) return null;

    const handleAddNugget = async () => {
      if (!nuggetName.trim()) {
        alert('Please enter a nugget name');
        return;
      }

      try {
        const response = await fetch(buildApiUrl(`/api/topics/${selectedTopic.id}/nuggets`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: nuggetName,
            description: nuggetDescription
          })
        });

        if (response.ok) {
          const newNugget = await response.json();
          newNugget.slides = [];
          newNugget.slide_count = 0;

          const updatedCourses = courses.map(course => {
            if (course.id === selectedCourse.id) {
              return {
                ...course,
                topics: course.topics.map(topic => {
                  if (topic.id === selectedTopic.id) {
                    return {
                      ...topic,
                      nuggets: [...(topic.nuggets || []), newNugget]
                    };
                  }
                  return topic;
                })
              };
            }
            return course;
          });

          setCourses(updatedCourses);
          const updatedCourse = updatedCourses.find(c => c.id === selectedCourse.id);
          const updatedTopic = updatedCourse.topics.find(t => t.id === selectedTopic.id);
          setSelectedCourse(updatedCourse);
          setSelectedTopic(updatedTopic);
          setNuggetName('');
          setNuggetDescription('');
          setShowAddNuggetModal(false);
        } else {
          const error = await response.json();
          alert(error.error || 'Failed to create nugget');
        }
      } catch (error) {
        console.error('Error creating nugget:', error);
        alert('Failed to create nugget');
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>💎</span> Add New Nugget
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nugget Name</label>
              <input
                type="text"
                value={nuggetName}
                onChange={(e) => setNuggetName(e.target.value)}
                placeholder="e.g., Introduction to Variables"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={nuggetDescription}
                onChange={(e) => setNuggetDescription(e.target.value)}
                placeholder="Brief description of the nugget..."
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => {
                setShowAddNuggetModal(false);
                setNuggetName('');
                setNuggetDescription('');
              }}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleAddNugget}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all font-medium shadow-lg"
            >
              Add Nugget
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add Video Modal Component
  const AddVideoModal = () => {
    const [videoTitle, setVideoTitle] = useState('');
    const [videoUrl, setVideoUrl] = useState('');
    const [uploadMethod, setUploadMethod] = useState('url'); // 'url' or 'upload'
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    if (!showAddVideoModal) return null;

    const handleAddVideo = async () => {
      if (!videoTitle.trim()) {
        alert('Please enter video title');
        return;
      }

      let finalVideoUrl = videoUrl;

      // If upload method, upload file first
      if (uploadMethod === 'upload') {
        if (!selectedFile) {
          alert('Please select a video file');
          return;
        }

        setUploading(true);
        try {
          const formData = new FormData();
          formData.append('file', selectedFile);
          formData.append('media_type', 'video');

          const response = await fetch(buildApiUrl('/api/courses/upload-media'), {
            method: 'POST',
            body: formData
          });

          if (!response.ok) {
            throw new Error('Upload failed');
          }

          const data = await response.json();
          finalVideoUrl = buildApiUrl(data.url);
        } catch (error) {
          console.error('Error uploading video:', error);
          alert('Failed to upload video');
          setUploading(false);
          return;
        }
        setUploading(false);
      } else {
        if (!videoUrl.trim()) {
          alert('Please enter video URL');
          return;
        }
      }

      const newVideo = {
        type: 'video',
        title: videoTitle,
        url: finalVideoUrl
      };

      // Add to current slide
      const newSlides = [...nuggetSlides];
      if (newSlides[currentSlideIndex]) {
        newSlides[currentSlideIndex].elements.push(newVideo);
        setNuggetSlides(newSlides);
      }

      setVideoTitle('');
      setVideoUrl('');
      setSelectedFile(null);
      setUploadMethod('url');
      setShowAddVideoModal(false);
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>🎥</span> Add Video
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Video Title</label>
              <input
                type="text"
                value={videoTitle}
                onChange={(e) => setVideoTitle(e.target.value)}
                placeholder="e.g., Introduction to the Topic"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Upload Method</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="videoMethod"
                    value="url"
                    checked={uploadMethod === 'url'}
                    onChange={(e) => setUploadMethod(e.target.value)}
                    className="w-4 h-4 text-red-600"
                  />
                  <span>URL</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="videoMethod"
                    value="upload"
                    checked={uploadMethod === 'upload'}
                    onChange={(e) => setUploadMethod(e.target.value)}
                    className="w-4 h-4 text-red-600"
                  />
                  <span>Upload from PC</span>
                </label>
              </div>
            </div>

            {uploadMethod === 'url' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Video URL</label>
                <input
                  type="url"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Video File</label>
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
                {selectedFile && (
                  <p className="text-sm text-gray-600 mt-2">Selected: {selectedFile.name} ({(selectedFile.size / (1024*1024)).toFixed(2)} MB)</p>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => {
                setShowAddVideoModal(false);
                setVideoTitle('');
                setVideoUrl('');
              }}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleAddVideo}
              disabled={uploading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-lg hover:from-red-700 hover:to-orange-700 transition-all font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? 'Uploading...' : 'Add Video'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add Question Modal Component
  const AddQuestionModal = () => {
    const [questionText, setQuestionText] = useState('');
    const [questionType, setQuestionType] = useState('multiple-choice');
    const [options, setOptions] = useState(['', '', '', '']);
    const [correctAnswer, setCorrectAnswer] = useState('');
    const [questionImage, setQuestionImage] = useState(null);
    const [questionImageUrl, setQuestionImageUrl] = useState('');
    const [optionImages, setOptionImages] = useState([null, null, null, null]);
    const [optionImageUrls, setOptionImageUrls] = useState(['', '', '', '']);
    const [uploadingImage, setUploadingImage] = useState(false);

    if (!showAddQuestionModal) return null;

    const handleImageUpload = async (file, type, index = null) => {
      if (!file) return null;
      
      setUploadingImage(true);
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await fetch(buildApiUrl('/api/upload-question-image'), {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          setUploadingImage(false);
          return data.url;
        } else {
          alert('Failed to upload image');
          setUploadingImage(false);
          return null;
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Error uploading image');
        setUploadingImage(false);
        return null;
      }
    };

    const handleAddQuestion = () => {
      if (!questionText.trim()) {
        alert('Please enter a question');
        return;
      }

      const newQuestion = {
        type: 'question',
        text: questionText,
        questionType: questionType,
        options: questionType === 'multiple-choice' ? options.filter(o => o.trim()) : [],
        correctAnswer: correctAnswer,
        questionImage: questionImageUrl,
        optionImages: questionType === 'multiple-choice' ? optionImageUrls : []
      };

      // Add to current slide
      const newSlides = [...nuggetSlides];
      if (newSlides[currentSlideIndex]) {
        newSlides[currentSlideIndex].elements.push(newQuestion);
        setNuggetSlides(newSlides);
      }

      setQuestionText('');
      setQuestionType('multiple-choice');
      setOptions(['', '', '', '']);
      setCorrectAnswer('');
      setQuestionImage(null);
      setQuestionImageUrl('');
      setOptionImages([null, null, null, null]);
      setOptionImageUrls(['', '', '', '']);
      setShowAddQuestionModal(false);
    };

    return (
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>❓</span> Add Question
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Question Type</label>
              <select
                value={questionType}
                onChange={(e) => setQuestionType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="multiple-choice">Multiple Choice</option>
                <option value="text">Text Answer</option>
                <option value="true-false">True/False</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Question Text</label>
              <textarea
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                placeholder="Enter your question..."
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Question Image (Optional)</label>
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setQuestionImage(file);
                    const url = await handleImageUpload(file, 'question');
                    if (url) setQuestionImageUrl(url);
                  }
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {questionImageUrl && (
                <div className="mt-2">
                  <img src={buildApiUrl(questionImageUrl)} alt="Question" className="max-h-32 rounded-lg" />
                </div>
              )}
            </div>
            {questionType === 'multiple-choice' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
                  {options.map((option, idx) => (
                    <div key={idx} className="mb-4 p-3 border border-gray-200 rounded-lg bg-gray-50">
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...options];
                          newOptions[idx] = e.target.value;
                          setOptions(newOptions);
                        }}
                        placeholder={`Option ${idx + 1}`}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
                      />
                      <label className="block text-xs font-medium text-gray-600 mb-1">Option {idx + 1} Image (Optional)</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const newImages = [...optionImages];
                            newImages[idx] = file;
                            setOptionImages(newImages);
                            const url = await handleImageUpload(file, 'option', idx);
                            if (url) {
                              const newUrls = [...optionImageUrls];
                              newUrls[idx] = url;
                              setOptionImageUrls(newUrls);
                            }
                          }
                        }}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      {optionImageUrls[idx] && (
                        <div className="mt-2">
                          <img src={buildApiUrl(optionImageUrls[idx])} alt={`Option ${idx + 1}`} className="max-h-24 rounded-lg" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Correct Answer</label>
                  <input
                    type="text"
                    value={correctAnswer}
                    onChange={(e) => setCorrectAnswer(e.target.value)}
                    placeholder="Enter the correct answer"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </>
            )}
            {questionType === 'text' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Expected Answer (Optional)</label>
                <input
                  type="text"
                  value={correctAnswer}
                  onChange={(e) => setCorrectAnswer(e.target.value)}
                  placeholder="Enter expected answer..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}
            {questionType === 'true-false' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Correct Answer</label>
                <select
                  value={correctAnswer}
                  onChange={(e) => setCorrectAnswer(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select answer...</option>
                  <option value="true">True</option>
                  <option value="false">False</option>
                </select>
              </div>
            )}
          </div>
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => {
                setShowAddQuestionModal(false);
                setQuestionText('');
                setQuestionType('multiple-choice');
                setOptions(['', '', '', '']);
                setCorrectAnswer('');
                setQuestionImage(null);
                setQuestionImageUrl('');
                setOptionImages([null, null, null, null]);
                setOptionImageUrls(['', '', '', '']);
              }}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleAddQuestion}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all font-medium shadow-lg"
            >
              Add Question
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add Slide Modal Component
  const AddSlideModal = () => {
    const [slideTitle, setSlideTitle] = useState('');
    const [slideContent, setSlideContent] = useState('');

    if (!showAddSlideModal) return null;

    const handleAddSlide = () => {
      if (!slideTitle.trim()) {
        alert('Please enter a title');
        return;
      }

      const newTextElement = {
        type: 'text',
        title: slideTitle,
        content: slideContent
      };

      // Add to current slide
      const newSlides = [...nuggetSlides];
      if (newSlides[currentSlideIndex]) {
        newSlides[currentSlideIndex].elements.push(newTextElement);
        setNuggetSlides(newSlides);
      }

      setSlideTitle('');
      setSlideContent('');
      setShowAddSlideModal(false);
    };

    return (
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>📊</span> Add Slide
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Slide Title</label>
              <input
                type="text"
                value={slideTitle}
                onChange={(e) => setSlideTitle(e.target.value)}
                placeholder="e.g., Key Concepts"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Slide Content</label>
              <textarea
                value={slideContent}
                onChange={(e) => setSlideContent(e.target.value)}
                placeholder="Enter slide content (Markdown supported)..."
                rows="10"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Tip: You can use Markdown formatting for better presentation</p>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => {
                setShowAddSlideModal(false);
                setSlideTitle('');
                setSlideContent('');
              }}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleAddSlide}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all font-medium shadow-lg"
            >
              Add Text
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add Image Modal Component
  const AddImageModal = () => {
    const [imageTitle, setImageTitle] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [uploadMethod, setUploadMethod] = useState('url'); // 'url' or 'upload'
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');

    if (!showAddImageModal) return null;

    const handleAddImage = async () => {
      let finalImageUrl = imageUrl;

      // If upload method, upload file first
      if (uploadMethod === 'upload') {
        if (!selectedFile) {
          alert('Please select an image file');
          return;
        }

        setUploading(true);
        try {
          const formData = new FormData();
          formData.append('file', selectedFile);
          formData.append('media_type', 'image');

          const response = await fetch(buildApiUrl('/api/courses/upload-media'), {
            method: 'POST',
            body: formData
          });

          if (!response.ok) {
            throw new Error('Upload failed');
          }

          const data = await response.json();
          finalImageUrl = buildApiUrl(data.url);
        } catch (error) {
          console.error('Error uploading image:', error);
          alert('Failed to upload image');
          setUploading(false);
          return;
        }
        setUploading(false);
      } else {
        if (!imageUrl.trim()) {
          alert('Please enter an image URL');
          return;
        }
      }

      const newImage = {
        type: 'image',
        title: imageTitle,
        url: finalImageUrl
      };

      // Add to current slide
      const newSlides = [...nuggetSlides];
      if (newSlides[currentSlideIndex]) {
        newSlides[currentSlideIndex].elements.push(newImage);
        setNuggetSlides(newSlides);
      }

      setImageTitle('');
      setImageUrl('');
      setSelectedFile(null);
      setPreviewUrl('');
      setUploadMethod('url');
      setShowAddImageModal(false);
    };

    return (
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>🖼️</span> Add Image
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Image Title (Optional)</label>
              <input
                type="text"
                value={imageTitle}
                onChange={(e) => setImageTitle(e.target.value)}
                placeholder="e.g., Diagram of the Topic"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Upload Method</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="imageMethod"
                    value="url"
                    checked={uploadMethod === 'url'}
                    onChange={(e) => setUploadMethod(e.target.value)}
                    className="w-4 h-4 text-pink-600"
                  />
                  <span>URL</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="imageMethod"
                    value="upload"
                    checked={uploadMethod === 'upload'}
                    onChange={(e) => setUploadMethod(e.target.value)}
                    className="w-4 h-4 text-pink-600"
                  />
                  <span>Upload from PC</span>
                </label>
              </div>
            </div>

            {uploadMethod === 'url' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
                <input
                  type="url"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Paste the URL of an image from the web</p>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Image File</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    setSelectedFile(file);
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setPreviewUrl(reader.result);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
                {selectedFile && (
                  <p className="text-sm text-gray-600 mt-2">Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)</p>
                )}
              </div>
            )}
            
            {(imageUrl || previewUrl) && (
              <div className="border border-gray-200 rounded-lg p-2">
                <p className="text-xs text-gray-600 mb-2">Preview:</p>
                <img src={uploadMethod === 'url' ? imageUrl : previewUrl} alt="Preview" className="w-full rounded" onError={(e) => e.target.style.display = 'none'} />
              </div>
            )}
          </div>
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => {
                setShowAddImageModal(false);
                setImageTitle('');
                setImageUrl('');
              }}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleAddImage}
              disabled={uploading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-pink-600 to-rose-600 text-white rounded-lg hover:from-pink-700 hover:to-rose-700 transition-all font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? 'Uploading...' : 'Add Image'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Assignment Modal Component
  const AssignCourseModal = () => {
    const [allStudents, setAllStudents] = useState([]);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [assignmentType, setAssignmentType] = useState('year'); // 'year', 'form', 'student'
    const [selectedYears, setSelectedYears] = useState([]);
    const [selectedForms, setSelectedForms] = useState([]);
    const [selectedStudentIds, setSelectedStudentIds] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      if (showAssignCourseModal) {
        loadStudents();
      }
    }, [showAssignCourseModal]);

    useEffect(() => {
      // Filter students based on search query
      if (searchQuery.trim() === '') {
        setFilteredStudents(allStudents);
      } else {
        const query = searchQuery.toLowerCase();
        const filtered = allStudents.filter(student =>
          student.full_name?.toLowerCase().includes(query) ||
          student.school_id?.toLowerCase().includes(query) ||
          getYearLabel(student.nc_year)?.toLowerCase().includes(query) ||
          student.division_name?.toLowerCase().includes(query)
        );
        setFilteredStudents(filtered);
      }
    }, [searchQuery, allStudents]);

    const loadStudents = async () => {
      try {
        const response = await fetch(buildApiUrl('/synced-students'));
        if (response.ok) {
          const data = await response.json();
          setAllStudents(data.students || []);
          setFilteredStudents(data.students || []);
        }
      } catch (error) {
        console.error('Error loading students:', error);
      }
    };

    const handleYearToggle = (year) => {
      const isYearSelected = selectedYears.includes(year);
      
      if (isYearSelected) {
        // Deselect year and remove all forms in that year
        setSelectedYears(selectedYears.filter(y => y !== year));
        const formsInYear = allStudents
          .filter(s => getYearLabel(s.nc_year) === year)
          .map(s => s.division_name)
          .filter(Boolean);
        setSelectedForms(selectedForms.filter(f => !formsInYear.includes(f)));
      } else {
        // Select year and add all forms in that year
        setSelectedYears([...selectedYears, year]);
        const formsInYear = [...new Set(
          allStudents
            .filter(s => getYearLabel(s.nc_year) === year)
            .map(s => s.division_name)
            .filter(Boolean)
        )];
        setSelectedForms([...new Set([...selectedForms, ...formsInYear])]);
      }
    };

    const handleFormToggle = (form) => {
      if (selectedForms.includes(form)) {
        setSelectedForms(selectedForms.filter(f => f !== form));
      } else {
        setSelectedForms([...selectedForms, form]);
      }
    };

    const handleStudentToggle = (studentId) => {
      if (selectedStudentIds.includes(studentId)) {
        setSelectedStudentIds(selectedStudentIds.filter(id => id !== studentId));
      } else {
        setSelectedStudentIds([...selectedStudentIds, studentId]);
      }
    };

    const handleAssign = async () => {
      if (!courseToAssign) return;

      let assignedToType = '';
      let assignedToValues = [];

      if (assignmentType === 'year') {
        if (selectedYears.length === 0) {
          alert('Please select at least one year');
          return;
        }
        assignedToType = 'year';
        assignedToValues = selectedYears;
      } else if (assignmentType === 'form') {
        if (selectedForms.length === 0) {
          alert('Please select at least one form');
          return;
        }
        assignedToType = 'form';
        assignedToValues = selectedForms;
      } else if (assignmentType === 'student') {
        if (selectedStudentIds.length === 0) {
          alert('Please select at least one student');
          return;
        }
        assignedToType = 'student';
        assignedToValues = selectedStudentIds;
      }

      try {
        setLoading(true);
        const response = await fetch(buildApiUrl(`/api/courses/${courseToAssign.id}/assign`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            assigned_to_type: assignedToType,
            assigned_to_values: assignedToValues
          })
        });

        if (response.ok) {
          alert('Course assigned successfully!');
          handleCloseModal();
        } else {
          const error = await response.json();
          alert(error.error || 'Failed to assign course');
        }
      } catch (error) {
        console.error('Error assigning course:', error);
        alert('Failed to assign course');
      } finally {
        setLoading(false);
      }
    };

    const handleCloseModal = () => {
      setShowAssignCourseModal(false);
      setCourseToAssign(null);
      setAssignmentType('year');
      setSelectedYears([]);
      setSelectedForms([]);
      setSelectedStudentIds([]);
      setSearchQuery('');
    };

    if (!showAssignCourseModal || !courseToAssign) return null;

    const years = [...new Set(allStudents.map(s => getYearLabel(s.nc_year)).filter(Boolean))].sort((a, b) => {
      const order = ['Pre-School', 'Reception', ...Array.from({length: 13}, (_, i) => `Year ${i+1}`)];
      return order.indexOf(a) - order.indexOf(b);
    });
    const forms = [...new Set(allStudents.map(s => s.division_name).filter(Boolean))].sort();

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <span>👥</span> Assign Course: {courseToAssign.name}
            </h3>
            <p className="text-sm text-gray-600 mt-1">Select who should have access to this course</p>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
            {/* Assignment Type Selection */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setAssignmentType('year')}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                  assignmentType === 'year'
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                By Year
              </button>
              <button
                onClick={() => setAssignmentType('form')}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                  assignmentType === 'form'
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                By Form
              </button>
              <button
                onClick={() => setAssignmentType('student')}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                  assignmentType === 'student'
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Individual Students
              </button>
            </div>

            {/* Year Selection */}
            {assignmentType === 'year' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Select Years (forms will be auto-selected)</label>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {years.map(year => (
                    <button
                      key={year}
                      onClick={() => handleYearToggle(year)}
                      className={`px-4 py-3 rounded-lg font-medium transition-all ${
                        selectedYears.includes(year)
                          ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {year}
                    </button>
                  ))}
                </div>
                {selectedForms.length > 0 && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-blue-900 mb-2">Selected Forms ({selectedForms.length}):</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedForms.map(form => (
                        <span key={form} className="px-3 py-1 bg-blue-200 text-blue-800 rounded-full text-sm">
                          {form}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Form Selection */}
            {assignmentType === 'form' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Select Forms</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-96 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                  {forms.map(form => (
                    <button
                      key={form}
                      onClick={() => handleFormToggle(form)}
                      className={`px-4 py-2 rounded-lg font-medium transition-all text-left ${
                        selectedForms.includes(form)
                          ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {form}
                    </button>
                  ))}
                </div>
                {selectedForms.length > 0 && (
                  <p className="mt-3 text-sm text-gray-600">
                    Selected: {selectedForms.length} form{selectedForms.length !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            )}

            {/* Student Selection */}
            {assignmentType === 'student' && (
              <div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Search Students</label>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name, ID, year, or form..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredStudents.map(student => (
                    <div
                      key={student.school_id}
                      onClick={() => handleStudentToggle(student.school_id)}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedStudentIds.includes(student.school_id)
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{student.full_name}</p>
                          <p className="text-sm text-gray-600">
                            {student.school_id} • {getYearLabel(student.nc_year)} • {student.division_name}
                          </p>
                        </div>
                        {selectedStudentIds.includes(student.school_id) && (
                          <span className="text-green-500 text-xl">✓</span>
                        )}
                      </div>
                    </div>
                  ))}
                  {filteredStudents.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No students found</p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="p-6 border-t border-gray-200 flex gap-3">
            <button
              onClick={handleCloseModal}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAssign}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all font-medium shadow-lg disabled:opacity-50"
            >
              {loading ? 'Assigning...' : 'Assign Course'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // --- Layout with new structure ---
  return (
    <div className="min-h-screen bg-gray-50">
      {renderCurrentView()}
      <DeleteConfirmationModal />
      <SubmissionsViewerModal />
      <SavedAssignmentsModal />
      <AddCourseModal />
      <EditCourseModal />
      <AddTopicModal />
      <EditTopicModal />
      <AddNuggetModal />
      <AddVideoModal />
      <AddQuestionModal />
      <AddSlideModal />
      <AddImageModal />
      <AssignCourseModal />
    </div>
  );
};

export default TeacherDashboard;
